// Default Help Center content — bilingual (es/en). The platform owner can edit,
// add or remove articles from Platform Admin → Centro de Ayuda; once they save,
// the saved set (stored in PlatformSetting) takes over. Until then, the Help
// Center falls back to these defaults so it's never empty.
//
// Article shape: { id, cat, nav, title:{es,en}, body:{es,en} }.
//
// `nav` lists the ids of the MENU entries this article documents (the `NAV` in
// deskte-full.jsx). It is what
// deskte-api/tests/unit/centroDeAyudaAlDia.test.ts checks: ship a new module
// without writing its article and the backend deploy goes red. That gate exists
// because the Help Center had 24 articles for 64 modules and nobody could see
// it — the same shape of hole that made the support AI answer "no se puede"
// about modules that existed (see tests/unit/manualAlDia.test.ts). An article
// that documents no module in particular carries `nav: []`.
//
// Bodies use a tiny markdown subset: "### " subheadings, "- " bullets,
// "**bold**", numbered lines, blank-line paragraphs, and `![alt](url)` media.
// Media with no scheme (`ayuda/clients-es.webp`) is a file shipped inside the
// app under crm-app/public/ayuda/ — screenshots of the DEMO seed (invented
// clients, 555 phone numbers), so no article can ever leak a real client's PHI.
// To refresh them, re-capture the demo (crm-app/capturas-web.mjs) and copy the
// .webp files over. A https:// URL is what the Platform Admin "insert media"
// button mints, and a YouTube/Vimeo link renders as an embedded player.

export const HELP_CATEGORIES = [
  { id: 'start', icon: '🚀', title: { es: 'Primeros pasos', en: 'Getting started' }, desc: { es: 'Deja tu cuenta lista y aprende a leer el panel.', en: 'Set your account up and learn to read the dashboard.' } },
  { id: 'clients', icon: '👥', title: { es: 'Clientes y Leads', en: 'Clients & Leads' }, desc: { es: 'Fichas, leads, formularios, aplicaciones y referidos.', en: 'Profiles, leads, forms, applications and referrals.' } },
  { id: 'policies', icon: '📄', title: { es: 'Pólizas, Pagos y Renovaciones', en: 'Policies, Payments & Renewals' }, desc: { es: 'Administra pólizas, pagos del mes y renovaciones.', en: 'Manage policies, monthly payments and renewals.' } },
  { id: 'tasks', icon: '✅', title: { es: 'Tareas, Citas y Calendario', en: 'Tasks, Appointments & Calendar' }, desc: { es: 'Pendientes, seguimientos, citas y tu agenda.', en: 'To-dos, follow-ups, appointments and your agenda.' } },
  { id: 'comms', icon: '📧', title: { es: 'Correo y Comunicación', en: 'Email & Communication' }, desc: { es: 'Email masivo, campañas, secuencias, WhatsApp y chat.', en: 'Mass email, campaigns, sequences, WhatsApp and chat.' } },
  { id: 'phone', icon: '📞', title: { es: 'SMS y Llamadas (Teléfono)', en: 'SMS & Calls (Phone)' }, desc: { es: 'Conecta tu proveedor (Quo, SignalWire, Twilio, RingCentral, MightyCall) para textear y llamar desde Deskte.', en: 'Connect your provider (Quo, SignalWire, Twilio, RingCentral, MightyCall) to text and call from Deskte.' } },
  { id: 'healthsherpa', icon: '🩺', title: { es: 'HealthSherpa', en: 'HealthSherpa' }, desc: { es: 'Conecta tu cuenta para que el estado de tus pólizas se actualice solo.', en: 'Connect your account so your policy status updates on its own.' } },
  { id: 'sales', icon: '💰', title: { es: 'Ventas y Documentos', en: 'Sales & Documents' }, desc: { es: 'Cotizaciones, facturas, solicitudes, firmas, tickets y recursos.', en: 'Quotes, invoices, requests, signatures, tickets and resources.' } },
  { id: 'reports', icon: '📊', title: { es: 'Reportes y Análisis', en: 'Reports & Analytics' }, desc: { es: 'Comisiones, ventas, reportes de clientes y de equipo.', en: 'Commissions, sales, client and team reports.' } },
  { id: 'business', icon: '🧰', title: { es: 'Negocio y Herramientas', en: 'Business & Tools' }, desc: { es: 'Automatizaciones, metas, gastos, bóveda, licencias, importar.', en: 'Automations, goals, expenses, vault, licenses, import.' } },
  { id: 'agency', icon: '🏢', title: { es: 'Mi Agencia y Equipo', en: 'My Agency & Team' }, desc: { es: 'Equipo, asientos, HIPAA y los paneles del dueño.', en: 'Team, seats, HIPAA and the owner dashboards.' } },
  { id: 'new', icon: '✨', title: { es: 'Funciones nuevas', en: "What's new" }, desc: { es: 'Lo último que agregamos a Deskte.', en: 'The latest additions to Deskte.' } },
];

export const HELP_ARTICLES = [
  // ── Primeros pasos ────────────────────────────────────────────────────────
  { id: 'first-day', cat: 'start', nav: [],
    title: { es: 'Tu primer día: haz esto en orden', en: 'Your first day: do this in order' },
    body: {
      es: `Cinco cosas, una sola vez, y tu cuenta queda lista para trabajar.

### El orden
1. Entra a **app.deskte.com** con tu correo y contraseña (guárdala en favoritos: siempre es la misma dirección).
2. Elige **idioma** 🇪🇸/🇺🇸 y el tema de colores. Se guarda en tu cuenta y te sigue a cualquier computadora.
3. Completa **Mi Perfil** — sobre todo la **dirección postal**, que es obligatoria por ley para poder enviar correos.
4. Conecta tu correo en **Mi Correo** (Gmail u Outlook), para que los mensajes salgan de tu dirección y no caigan en spam.
5. Si tienes agentes o asistentes, agrégalos en **Equipo**.

**Consejo:** no hace falta que aprendas todo el menú el primer día. Empieza por **Clientes** y **Pólizas**; el resto se va sumando solo.`,
      en: `Five things, once, and your account is ready to work.

### The order
1. Go to **app.deskte.com** with your email and password (bookmark it — the address never changes).
2. Pick your **language** 🇪🇸/🇺🇸 and color theme. It's saved to your account and follows you to any computer.
3. Complete **My Profile** — above all the **mailing address**, which is legally required before you can send email.
4. Connect your email in **My Inbox** (Gmail or Outlook), so messages go out from your address and don't land in spam.
5. If you have agents or assistants, add them under **Team**.

**Tip:** you don't have to learn the whole menu on day one. Start with **Clients** and **Policies**; the rest follows naturally.` } },
  { id: 'profile', cat: 'start', nav: ['profile'],
    title: { es: 'Completar "Mi Perfil" (importante por ley)', en: 'Complete "My Profile" (legally important)' },
    body: {
      es: `Tu perfil es la base de tus comunicaciones y firmas.

### Cómo
1. Menú → **Mi Perfil**.
2. Llena tu **nombre legal**, título, teléfono, **NPN** y estados con licencia.
3. Escribe la **dirección postal** — ⚠️ obligatoria por CAN-SPAM para poder enviar correos.
4. Sube tu **foto** y el **logo de tu compañía** (salen en tu firma y en tu tarjeta digital).
5. En la pestaña **Email**, escribe tu firma con variables: {agentName}, {agentPhone}, {agentEmail}, {companyName}.

### Las otras pestañas
- **Plan y Facturación** (dueño): plan actual, métodos de pago e historial.
- **Seguridad:** crea el **PIN de 6 dígitos** de la Bóveda de Claves y revisa quién accedió.
- **Preferencias:** zona horaria, formato de fecha, popups de cumpleaños y auto-asignación de tareas.

**Consejo:** el PIN de 6 dígitos (bóveda) NO es el mismo que el PIN de 4 (inicio de sesión). Nunca compartas ninguno.`,
      en: `Your profile is the basis of your communications and signatures.

### How
1. Menu → **My Profile**.
2. Fill in your **legal name**, title, phone, **NPN** and licensed states.
3. Enter your **mailing address** — ⚠️ required by CAN-SPAM before you can send email.
4. Upload your **photo** and your **company logo** (they appear in your signature and digital card).
5. On the **Email** tab, write your signature using variables: {agentName}, {agentPhone}, {agentEmail}, {companyName}.

### The other tabs
- **Plan & Billing** (owner): current plan, payment methods and history.
- **Security:** create the **6-digit PIN** for the Password Vault and review who accessed it.
- **Preferences:** time zone, date format, birthday popups and task auto-assignment.

**Tip:** the 6-digit PIN (vault) is NOT the 4-digit one (login). Never share either.` } },
  { id: 'email-connect', cat: 'start', nav: ['my_email'],
    title: { es: 'Conectar tu correo (Gmail u Outlook)', en: 'Connect your email (Gmail or Outlook)' },
    body: {
      es: `**Mi Correo** no es una bandeja de entrada: es la pantalla donde **conectas** tu cuenta. Los correos de un cliente se leen dentro de su ficha.

### Cómo
1. Menú → **Mi Correo**.
2. Elige **Google (Gmail)**, **Outlook personal** o **Microsoft 365 corporativo**.
3. Inicia sesión y autoriza. Deskte puede enviar en tu nombre; **nunca ve tu contraseña**.
4. Verás "✅ Conectado". Después puedes **Refrescar** o **Desconectar**.

**Consejos:**
- Si no conectas, tus campañas salen de un remitente genérico y es más fácil que caigan en SPAM.
- Los **asistentes** no ven esta pantalla: envían desde el correo de su agente supervisor.`,
      en: `**My Inbox** is not a mailbox: it's the screen where you **connect** your account. A client's emails are read inside their profile.

### How
1. Menu → **My Inbox**.
2. Choose **Google (Gmail)**, **personal Outlook** or **Microsoft 365 corporate**.
3. Sign in and authorize. Deskte can send on your behalf; it **never sees your password**.
4. You'll see "✅ Connected". After that you can **Refresh** or **Disconnect**.

**Tips:**
- If you don't connect, your campaigns go out from a generic sender and are likelier to hit SPAM.
- **Assistants** don't see this screen: they send from their supervising agent's email.` } },
  { id: 'dashboard', cat: 'start', nav: ['dashboard'],
    title: { es: 'El Dashboard (tu pantalla de inicio)', en: 'The Dashboard (your home screen)' },
    body: {
      es: `Lo primero que ves al entrar: el resumen vivo de tu negocio.

![Dashboard de Deskte](ayuda/dashboard-es.webp)

### Qué hay
- Números clave: clientes activos, pólizas por renovar, documentos faltantes, facturas pendientes.
- **Saludos y Felicitaciones:** 🎂 cumpleaños de hoy y mañana (del cliente, su cónyuge y dependientes) y el 🎉 próximo feriado.
- **Alertas Pendientes:** clic en una y vas directo al cliente.
- **Feed de Actividad:** lo que hizo tu equipo, filtrable por fecha, miembro o búsqueda.

### Enviar un saludo
1. Toca el canal: 📧 email · 💬 WhatsApp · 📱 SMS.
2. Se abre la vista previa ya escrita; edita el asunto y el mensaje, cambia 🇺🇸/🇪🇸 si hace falta.
3. Para email puedes **programarlo** a una fecha futura. Confirma y aparece un ✅ "Enviado".

**Consejos:**
- El saludo respeta el **idioma del cliente**, no el de la app.
- El ✅ lo ve todo el equipo: nadie manda el mismo saludo dos veces.
- Con el botón ⚙️ **ocultas los widgets** que no usas (se guarda por usuario).`,
      en: `The first thing you see when you sign in: a live summary of your business.

![Deskte dashboard](ayuda/dashboard-en.webp)

### What's on it
- Key numbers: active clients, policies up for renewal, missing documents, open invoices.
- **Greetings & Celebrations:** 🎂 today's and tomorrow's birthdays (client, spouse and dependents) and the next 🎉 holiday.
- **Pending Alerts:** click one and you land on the client.
- **Activity Feed:** what your team did, filterable by date, member or search.

### Sending a greeting
1. Pick the channel: 📧 email · 💬 WhatsApp · 📱 SMS.
2. A ready-written preview opens; edit the subject and message, switch 🇺🇸/🇪🇸 if needed.
3. Email can be **scheduled** for a future date. Confirm and a ✅ "Sent" appears.

**Tips:**
- Greetings follow the **client's language**, not the app's.
- The ✅ is shared with the whole team: nobody sends the same greeting twice.
- The ⚙️ button **hides widgets** you don't use (saved per user).` } },
  { id: 'alerts', cat: 'start', nav: ['alerts'],
    title: { es: 'Alertas (y el registro de cambios)', en: 'Alerts (and the change log)' },
    body: {
      es: `Todas tus alertas urgentes en un solo panel, por categoría.

### Cómo
1. Menú → **Alertas**.
2. Usa los chips: 🔔 Todas · 🔄 Renovaciones · 🎂 Cumpleaños · 📄 Documentos · 🏥 Edad/Medicare.
3. Las **URGENTE** (en rojo) siempre van primero, sin importar cuándo se crearon.
4. Clic en una alerta → vas a la ficha del cliente.

### Cambios del equipo
Abajo tienes la auditoría: filtra por **Desde/Hasta**, por miembro o busca texto. Puedes revisar cambios de hace meses.

**Consejos:**
- Lo urgente se calcula solo: renovación ≤7 días, Medicare ≤90 días, etc.
- El registro es **inmutable** (pista de auditoría HIPAA): nadie puede borrarlo.`,
      en: `Every urgent alert in one panel, grouped by category.

### How
1. Menu → **Alerts**.
2. Use the chips: 🔔 All · 🔄 Renewals · 🎂 Birthdays · 📄 Documents · 🏥 Age/Medicare.
3. **URGENT** ones (red) always come first, regardless of when they were created.
4. Click an alert → you land on the client's profile.

### Team changes
Underneath is the audit trail: filter by **From/To**, by member, or search text. You can review changes from months ago.

**Tips:**
- Urgency is calculated for you: renewal ≤7 days, Medicare ≤90 days, and so on.
- The log is **immutable** (HIPAA audit trail): nobody can erase it.` } },
  { id: 'active-members', cat: 'start', nav: ['active_members'],
    title: { es: 'Miembros Activos (quién está conectado)', en: 'Active Members (who is online)' },
    body: {
      es: `Un panel de una sola lectura: quién de tu equipo está dentro de Deskte en este momento.

- Sirve para saber a quién puedes pedirle algo ahora mismo.
- No hay nada que configurar ni ningún paso que dar.`,
      en: `A read-at-a-glance panel: who on your team is inside Deskte right now.

- Useful for knowing who you can ask for something this minute.
- Nothing to configure and no steps to take.` } },
  { id: 'menu-favorites', cat: 'start', nav: [],
    title: { es: 'Ordenar el menú: secciones y favoritos', en: 'Organize the menu: sections & favorites' },
    body: {
      es: `El menú está agrupado en secciones (Resumen, Clientes y Pólizas, Ventas, Marketing, etc.).

### Favoritos
- Pasa el mouse sobre un módulo y haz clic en la **estrella ☆** para fijarlo arriba en **⭐ Favoritos**.
- Así tienes a la mano lo que más usas.`,
      en: `The menu is grouped into sections (Overview, Clients & Policies, Sales, Marketing, etc.).

### Favorites
- Hover over a module and click the **star ☆** to pin it to **⭐ Favorites** at the top.
- Keeps what you use most within reach.` } },

  // ── Clientes y Leads ──────────────────────────────────────────────────────
  { id: 'add-client', cat: 'clients', nav: ['clients'],
    title: { es: 'Agregar y administrar clientes', en: 'Add & manage clients' },
    body: {
      es: `**Qué es:** el corazón del CRM, con toda la ficha del cliente.

![Lista de clientes](ayuda/clients-es.webp)

### Agregar un cliente
1. En **Clientes**, clic en **"+ Nuevo Cliente"**.
2. Llena datos básicos (Nombre*, Apellido*, email, teléfono, dirección, DOB, ingresos, estatus migratorio, idioma preferido, "Referido por", etc.).
3. Opcional: cónyuge y dependientes (nombre, relación, DOB, últimos 4 del SSN, si está asegurado).
4. Ajusta el **Tamaño del Hogar** si aplica: afecta los cálculos de subsidio ACA.
5. **Guardar Cliente**.

**Consejos:**
- La búsqueda encuentra por nombre, email o teléfono.
- Deskte avisa si detecta posibles **duplicados**.
- El botón **Imprimir** genera un PDF con toda la ficha.
- Puedes **asignar** un cliente a un asistente concreto.`,
      en: `**What it is:** the heart of the CRM, with the full client record.

![Client list](ayuda/clients-en.webp)

### Add a client
1. In **Clients**, click **"+ New Client"**.
2. Fill in the basics (First*, Last*, email, phone, address, DOB, income, immigration status, preferred language, "Referred by", etc.).
3. Optional: spouse and dependents (name, relationship, DOB, last 4 of SSN, whether they're covered).
4. Adjust **Household Size** where it applies: it feeds the ACA subsidy math.
5. **Save Client**.

**Tips:**
- Search finds by name, email or phone.
- Deskte warns about possible **duplicates**.
- The **Print** button generates a full-profile PDF.
- You can **assign** a client to a specific assistant.` } },
  { id: 'client-tabs', cat: 'clients', nav: [],
    title: { es: 'La ficha del cliente (pestañas)', en: 'The client profile (tabs)' },
    body: {
      es: `La ficha organiza todo el expediente en pestañas:

![Ficha del cliente](ayuda/client-detail-es.webp)

- **Información:** resumen, estado y los botones Editar / Eliminar / Imprimir / Kit de bienvenida.
- **Hogar:** cónyuge y dependientes (agregar, editar, marcar quién está cubierto).
- **Pólizas:** todas sus pólizas (+ Agregar Póliza).
- **Documentos:** archivos del cliente, "+ Solicitar Documentos" y "+ Subir Documento".
- **Notas, Tareas, Firmas, Elegibilidad.**
- **SSN / PIN:** protegido por PIN; cada vez que se revela queda auditado.
- **Historial / Cambios:** quién cambió qué campo y cuándo (inmutable).
- **Emails:** correos enviados y recibidos del cliente.
- **Portal Cliente:** link seguro con QR para que el cliente vea sus pólizas y suba documentos; se puede revocar.
- **Pestañas personalizadas:** campos extra propios de tu agencia.

**Consejo:** el SSN se guarda **cifrado**. Nunca lo escribas en notas ni en hojas.`,
      en: `The profile organizes the whole file into tabs:

![Client profile](ayuda/client-detail-en.webp)

- **Info:** summary, status and the Edit / Delete / Print / Welcome-kit buttons.
- **Household:** spouse and dependents (add, edit, mark who is covered).
- **Policies:** all their policies (+ Add Policy).
- **Documents:** the client's files, "+ Request Documents" and "+ Upload Document".
- **Notes, Tasks, Signatures, Eligibility.**
- **SSN / PIN:** PIN-protected; every reveal is audited.
- **History / Changes:** who changed which field and when (immutable).
- **Emails:** mail sent to and received from the client.
- **Client Portal:** a secure link with QR so the client can see their policies and upload documents; revocable.
- **Custom tabs:** extra fields your agency defines.

**Tip:** the SSN is stored **encrypted**. Never type it into notes or sheets.` } },
  { id: 'conversations', cat: 'comms', nav: ['conversations'],
    title: { es: 'Conversaciones (bandeja unificada)', en: 'Conversations (unified inbox)' },
    body: {
      es: `**Conversaciones** junta en una sola pantalla los **SMS** y los **correos** de tus clientes, para no tener que entrar ficha por ficha a ver quien contesto.

### Como
1. Menu → **Conversaciones**: la lista de hilos a la izquierda, la conversacion a la derecha.
2. Filtra con **Todas / Mias / Sin asignar**, o busca por nombre.
3. El numerito azul son los mensajes **sin leer**. Al abrir el hilo se ponen en cero.
4. Escribe abajo y pulsa **Enviar**: sale por el mismo canal del hilo — SMS por tu linea, correo por tu buzon.
5. **Sin asignar** reparte el hilo a alguien del equipo, y le llega su aviso.
6. **Cerrar** lo saca de la lista sin borrar nada.

**Lo que conviene saber:**
- Los mensajes **no cambian de sitio**: siguen guardandose en la ficha del cliente, que los enseña igual que siempre.
- Un **SMS que sale de Deskte cuesta** — aproximadamente 1 centavo, y te lo cobra tu proveedor, no Deskte. A cambio queda guardado en la ficha, respeta tu lista de **No-Textear** y al cliente le llega el numero de la agencia.
- Si el cliente esta en **No-Textear**, Deskte no manda y te lo dice: no es un fallo, es su decision.
- Un **asiento** solo ve esta pantalla si le das el permiso **Conversaciones → Ver la bandeja**. Contestar y repartir son permisos aparte.`,
      en: `**Conversations** brings your clients' **texts** and **emails** into one screen, so nobody has to open profiles one by one to find out who replied.

### How
1. Menu → **Conversations**: threads on the left, the conversation on the right.
2. Filter with **All / Mine / Unassigned**, or search by name.
3. The blue number is **unread** messages. Opening the thread clears it.
4. Type below and hit **Send**: it goes out on the thread's own channel — SMS from your line, email from your mailbox.
5. **Unassigned** hands the thread to a teammate, who gets their notification.
6. **Close** takes it off the list without deleting anything.

**Worth knowing:**
- Messages **don't move**: they are still stored on the client's profile, which shows them exactly as before.
- A **text sent from Deskte costs money** — about 1 cent, billed by your provider, not by Deskte. In exchange it is saved on the client record, it respects your **Do-Not-Text** list, and the client sees the agency's number.
- If the client is on **Do-Not-Text**, Deskte will not send and tells you so: that is not a failure, it is their decision.
- A **seat** only sees this screen if you grant **Conversations → See the message inbox**. Replying and assigning are separate permissions.` } },
  { id: 'pipeline', cat: 'clients', nav: ['pipeline'],
    title: { es: 'Embudo (tablero arrastrable)', en: 'Pipeline (drag-and-drop board)' },
    body: {
      es: `El **Embudo** son tus mismos leads, puestos en columnas segun en que van. Se mueven **arrastrando**.

### Como
1. Menu \u2192 **Embudo**. Cada columna es una etapa; cada tarjeta, un lead.
2. **Arrastra una tarjeta** a otra columna y ya esta \u2014 se guarda solo.
3. **$ Valor** en la tarjeta: apunta cuanto vale ese lead si se cierra, y cuando esperas cerrarlo.
4. Arriba de cada columna se ve **cuantos** hay y **cuanto suma**; arriba del todo, el total **en juego**.
5. **+ Columna**, **Renombrar** y **Quitar** cambian las etapas a como trabaja tu agencia.

**Lo que conviene saber:**
- Son **los mismos leads** de la pantalla de Leads, no una lista aparte. Lo que cambias en un sitio se ve en el otro.
- Las tarjetas se ordenan poniendo **arriba a quien toca llamar hoy** (por la fecha de seguimiento). Las vencidas salen en **rojo**.
- **Quitar una columna NO borra sus leads.** Vuelven a la columna que les toca por su estado. Antes de quitarla, Deskte te dice cuantos hay dentro.
- Los leads que entran por el **formulario publico** aparecen solos en la primera columna. No hay que hacer nada.
- Si una columna tiene muchisimas tarjetas, el tablero enseña las primeras y dice **+N mas**; para verlas todas esta la lista de Leads.`,
      en: `The **Pipeline** is your same leads, laid out in columns by where they stand. You move them by **dragging**.

### How
1. Menu \u2192 **Pipeline**. Each column is a stage; each card, a lead.
2. **Drag a card** to another column \u2014 it saves by itself.
3. **$ Value** on a card: note what that lead is worth if it closes, and when you expect to close it.
4. Each column header shows **how many** and **how much**; the top shows the total **in play**.
5. **+ Column**, **Rename** and **Remove** shape the stages to how your agency works.

**Worth knowing:**
- These are **the same leads** as the Leads screen, not a separate list. What you change in one shows in the other.
- Cards are sorted to put **whoever you must call today on top** (by follow-up date). Overdue ones show in **red**.
- **Removing a column does NOT delete its leads.** They go back to the column their status maps to. Before removing, Deskte tells you how many are inside.
- Leads coming in from the **public form** appear on their own in the first column. Nothing to do.
- If a column has very many cards, the board shows the first ones and says **+N more**; the Leads list has them all.` } },
  { id: 'leads', cat: 'clients', nav: ['leads'],
    title: { es: 'Leads y origen (referidos)', en: 'Leads & source (referrals)' },
    body: {
      es: `**Leads** es tu pipeline de contactos antes de que sean clientes.

### Cómo
1. Menú → **Leads**: lista a la izquierda, detalle a la derecha.
2. Filtra con los chips: 🟦 Nuevo · 🟪 Contactado · 🟩 Calificado · 🟢 Convertido · 🔴 Perdido.
3. **"+ Nuevo Lead"**: Nombre*, Apellido*, teléfono, email, **Fuente** (referido, Facebook, sitio web, llamada, evento…), idioma, fecha de seguimiento, intereses y notas.
4. Cambia el estado tocando un chip.
5. Cuando esté listo: **"Convertir a Cliente"** — crea el cliente, y si hay una cotización aceptada crea también las pólizas.

**Consejos:**
- Los leads de los **formularios públicos** se crean solos: cada envío es un lead.
- La **fecha de seguimiento** aparece como recordatorio en Alertas.
- La **fuente** es lo que después te dice de dónde vienen tus mejores clientes.`,
      en: `**Leads** is your pipeline of contacts before they become clients.

### How
1. Menu → **Leads**: list on the left, detail on the right.
2. Filter with the chips: 🟦 New · 🟪 Contacted · 🟩 Qualified · 🟢 Converted · 🔴 Lost.
3. **"+ New Lead"**: First*, Last*, phone, email, **Source** (referral, Facebook, website, cold call, event…), language, follow-up date, interests and notes.
4. Change the status by tapping a chip.
5. When they're ready: **"Convert to Client"** — it creates the client, and if a quote was accepted it creates the policies too.

**Tips:**
- Leads from **public forms** create themselves: every submission is a lead.
- The **follow-up date** shows up as a reminder in Alerts.
- **Source** is what later tells you where your best clients come from.` } },
  { id: 'lead-forms', cat: 'clients', nav: ['lead_forms'],
    title: { es: 'Formularios públicos (captura de leads)', en: 'Public forms (lead capture)' },
    body: {
      es: `Un creador de formularios sin código. Cada persona que lo llena se convierte en un Lead automáticamente.

### Cómo
1. Menú → **Formularios** → **"+ Nuevo formulario"**.
2. Llena Título, Descripción, **URL pública** (se genera sola y puedes editarla) y **Tipo de interés** (la etiqueta que llevará el lead).
3. Agrega campos con los botones rápidos: texto, email, teléfono, número, fecha, lista, opción única, casillas. Marca cuáles son **requeridos**.
4. Personaliza el **color de marca**, el mensaje de **Gracias** y una URL de redirección opcional.
5. **Vista previa** → **Crear formulario**.
6. En la lista: **Copiar link** para pegarlo en WhatsApp, Instagram o tu web. También hay **código QR** para imprimir.
7. Entra al detalle para ver vistas, envíos y la tabla de respuestas (cada fila ya es un Lead).

**Consejos:**
- No requieren login: cualquiera los llena.
- Menos campos obligatorios = más gente lo termina.
- Puedes desactivar un formulario sin borrarlo.`,
      en: `A no-code form builder. Everyone who fills one in becomes a Lead automatically.

### How
1. Menu → **Forms** → **"+ New form"**.
2. Fill in Title, Description, the **public URL** (auto-generated, editable) and the **interest type** (the tag the lead will carry).
3. Add fields with the quick buttons: text, email, phone, number, date, dropdown, single choice, checkboxes. Mark which are **required**.
4. Customize the **brand color**, the **Thank you** message and an optional redirect URL.
5. **Preview** → **Create form**.
6. From the list: **Copy link** and paste it into WhatsApp, Instagram or your website. There's a printable **QR code** too.
7. Open the detail to see views, submissions and the responses table (every row is already a Lead).

**Tips:**
- No login required: anyone can fill them in.
- Fewer required fields = more people finish.
- You can deactivate a form without deleting it.` } },
  { id: 'applications', cat: 'clients', nav: ['applications'],
    title: { es: 'Aplicaciones: que el cliente llene sus datos', en: 'Applications: let the client fill in their own data' },
    body: {
      es: `Solicitudes que le mandas a **una persona concreta** para que ella misma escriba sus datos y conteste tus preguntas, desde su teléfono. Sus respuestas van a la ficha del cliente.

**Distinto de Formularios:** un Formulario es público y crea un Lead; una Aplicación se le manda a alguien que ya tienes.

### Cómo
1. Pestaña **Plantillas** → **Nueva plantilla**: Nombre, Tipo de póliza, Idioma (ES/EN) e Introducción.
2. Enciende las **secciones** que quieres pedir: Dirección, Fecha de nacimiento y SSN, Cónyuge, Dependientes, Salud, Empleo.
3. En **Campos personalizados** escribe tus preguntas. Tipos: **Sí / No**, opción única, lista, casilla, texto, texto largo, correo, teléfono, número y fecha. Marca **Obligatorio** lo que no pueda saltarse.
4. Opción **"Preguntar lo mismo al cónyuge y a los dependientes"**: repite tus preguntas para cada integrante sin volver a escribirlas.
5. Elige la plantilla, pon el correo del cliente y manda. **Sin correo, se genera un enlace** para que lo compartas por WhatsApp o SMS.
6. Pestaña **Enviadas**: el estado de cada una y, al llegar contestada, todas las respuestas.

**Consejos:**
- **El enlace vive 72 horas.** Es corto a propósito: la solicitud pide SSN, fecha de nacimiento y condiciones médicas. Si se pasa el plazo, usa **Reenviar**.
- Si lo mandaste a la dirección equivocada, **Revocar** mata el enlace al momento.
- Puedes **subir un PDF o Word** de una solicitud que ya usas y la IA la convierte en plantilla. Si es un escaneo, el OCR corre **dentro de tu navegador**: el documento no sale de tu equipo.
- Todo lo que contesta el cliente se guarda **cifrado**: son datos de salud.
- **¿Aplicaciones o Firmas?** Aplicaciones sirve para **preguntar**; Firmas, para que **firme**. Si necesitas las dos cosas a la vez, Firmas también admite preguntas Sí/No dentro del documento.`,
      en: `Requests you send to **one specific person** so they type their own data and answer your questions from their phone. Their answers land on the client's profile.

**Different from Forms:** a Form is public and creates a Lead; an Application goes to someone you already have.

### How
1. **Templates** tab → **New template**: Name, policy type, language (ES/EN) and an introduction.
2. Switch on the **sections** you want: Address, Date of birth and SSN, Spouse, Dependents, Health, Employment.
3. Under **Custom fields**, write your questions. Types: **Yes / No**, single choice, dropdown, checkbox, text, long text, email, phone, number and date. Mark **Required** for anything they can't skip.
4. The **"Ask the same of the spouse and dependents"** option repeats your questions for each household member without retyping them.
5. Pick the template, enter the client's email and send. **With no email, a link is generated** for you to share over WhatsApp or SMS.
6. **Sent** tab: the status of each one and, once answered, every response.

**Tips:**
- **The link lives 72 hours.** That's short on purpose: the request asks for SSN, date of birth and medical conditions. If it lapses, use **Resend**.
- Sent it to the wrong address? **Revoke** kills the link immediately.
- You can **upload a PDF or Word** application you already use and the AI turns it into a template. For a scan, OCR runs **inside your browser**: the document never leaves your machine.
- Everything the client answers is stored **encrypted**: it's health data.
- **Applications or Signatures?** Applications is for **asking**; Signatures is for **signing**. If you need both at once, Signatures also supports Yes/No questions inside the document.` } },
  { id: 'prospects', cat: 'clients', nav: ['onboarding'],
    title: { es: 'Prospecto y Referido (seguimiento hasta que compra)', en: 'Prospect & Referral (tracking until they buy)' },
    body: {
      es: `Una sola lista donde conviven tus prospectos (⭐) y tus referidos (🎁), cada uno con sus pasos marcables. Así nadie está en dos listas distintas.

### Cómo
1. **"➕ Agregar prospecto / referido"**: busca un cliente que ya exista o escribe el nombre a mano.
2. Ve marcando los pasos: **👋 Contacto** → **📋 Datos** → **📨 Sometida**.
3. Cierra de una de dos formas:
   - **📦 Kit** — manda el Kit de Bienvenida, el cliente pasa a **Activo** y su póliza pendiente pasa a Activa.
   - **🚫 No procede** — pasa a **Inactivo**.
4. Cada fila tiene su hilo de notas, con texto y fotos.

**Consejos:**
- Los estatus son tuyos: se editan en **"Estatus de Prospectos y Referidos"**.
- Los mensajes del Kit se escriben **por tipo de póliza**, en "Mensajes del Kit por tipo". Puedes insertar **Cliente, Compañía, Tipo, N.º de póliza, Plan, Ingreso, Prima mensual, Fecha de efectividad** y el **Enlace del portal**: se llenan solos con los datos de la póliza y de la ficha al enviar (los mismos campos están en el kit de Renovaciones y en el de Cambio de vida).
- Un asistente necesita permiso de ver clientes para entrar, y de editarlos para tocar algo.`,
      en: `One list where your prospects (⭐) and referrals (🎁) live together, each with checkable steps. Nobody ends up on two separate lists.

### How
1. **"➕ Add prospect / referral"**: search for an existing client or type the name by hand.
2. Tick the steps as you go: **👋 Contact** → **📋 Data** → **📨 Submitted**.
3. Close it one of two ways:
   - **📦 Kit** — sends the Welcome Kit, the client turns **Active** and their pending policy goes Active.
   - **🚫 Not proceeding** — they turn **Inactive**.
4. Every row has its own note thread, with text and photos.

**Tips:**
- The statuses are yours: edit them under **"Prospect & Referral statuses"**.
- Kit messages are written **per policy type**, under "Kit messages by type". You can insert **Client, Company, Type, Policy #, Plan, Income, Monthly premium, Effective date** and the **Portal link**: they fill in with the policy and record data when sent (the same fields are in the Renewals and Life change kits).
- An assistant needs client-view permission to open this, and client-edit to change anything.` } },

  // ── Pólizas, Pagos y Renovaciones ─────────────────────────────────────────
  { id: 'policies', cat: 'policies', nav: ['policies'],
    title: { es: 'Agregar y administrar pólizas', en: 'Add & manage policies' },
    body: {
      es: `Las pólizas viven dentro de la ficha del cliente (pestaña **Pólizas**) o en el módulo **Pólizas**, ordenadas por renovación.

![Módulo de pólizas](ayuda/policies-es.webp)

### Agregar
1. **"+ Nueva póliza"** y llena:
   - **Tipo:** ACA, Medicare Advantage, Suplemento, Vida, Auto, Hogar, Dental, Visión…
   - **Aseguradora**, **Plan**, **Número de póliza**, **Prima**, deducibles.
   - **Pago:** Autopay (tarjeta o banco, con últimos 4 y vencimiento) o "Cliente paga".
   - Datos propios del ramo (auto: año/marca/modelo/VIN; hogar: tipo, año, cobertura).
   - **Personas Aseguradas:** marca quién está cubierto en ESTA póliza.
2. **Guardar**. El lápiz edita; el bote elimina (pide confirmación).

### Estados
Activa, Período de gracia, Pendiente, Cancelada — y los que tú crees con el botón ⚙️, cada uno con su color.

**Consejos:**
- Al crear la primera póliza de un **Prospecto**, este pasa solo a **Cliente activo**.
- **Familia** = titular + cónyuge + dependientes. **Miembros activos** = los marcados en esa póliza.
- Las tarjetas que vencen se pintan naranja (<30 días) o rojo (ya vencida).`,
      en: `Policies live inside the client profile (the **Policies** tab) or in the **Policies** module, sorted by renewal.

![Policies module](ayuda/policies-en.webp)

### Add
1. **"+ New policy"** and fill in:
   - **Type:** ACA, Medicare Advantage, Supplement, Life, Auto, Home, Dental, Vision…
   - **Carrier**, **Plan**, **Policy number**, **Premium**, deductibles.
   - **Payment:** Autopay (card or bank, with last 4 and expiry) or "Client pays".
   - Line-specific data (auto: year/make/model/VIN; home: type, year built, coverage).
   - **Insured people:** tick who is covered on THIS policy.
2. **Save**. The pencil edits; the bin deletes (with confirmation).

### Statuses
Active, Grace period, Pending, Cancelled — plus any you create with the ⚙️ button, each with its own color.

**Tips:**
- Creating a **Prospect's** first policy automatically turns them into an **active Client**.
- **Family** = primary + spouse + dependents. **Active members** = those ticked on that policy.
- Expiring cards turn orange (<30 days) or red (already expired).` } },
  { id: 'monthly-payments', cat: 'policies', nav: ['payment_tracker'],
    title: { es: 'Pagos del Mes (quién pagó su prima)', en: 'Monthly Payments (who paid their premium)' },
    body: {
      es: `Para el mes que elijas, ves todas las pólizas activas y marcas quién pagó. Guarda quién revisó y cuándo.

### Cómo
1. Menú → **Pagos del Mes** y elige el **mes** arriba a la derecha.
2. Filtra por tipo de póliza, compañía, estado (USA), estado de pago (Todos / Pagaron / No pagaron / Sin revisar) y búsqueda.
3. Lee el resumen: ✅ Pagaron · ❌ Falta · Total activas · 💵 Cobrado · ⏳ Pendiente.
4. Marca una póliza con **"✅ Pagó"** o **"❌ No pagó"**.
5. **Acciones masivas:** marca varias con las casillas y usa "Marcar pagado / no pagado".
6. **⚙️ Estatus:** crea estados propios (por ejemplo, Período de gracia).
7. **🔔 Plantillas:** recordatorios de email por **tipo + aseguradora**, con vista previa.

**Consejos:**
- El historial guarda cada cambio: quién y cuándo.
- Combina filtros ("ACA + Florida + Sin revisar").
- El botón ↻ refresca si otro agente marcó algo mientras mirabas.`,
      en: `For whichever month you pick, you see every active policy and mark who paid. It records who checked and when.

### How
1. Menu → **Monthly Payments** and pick the **month** at the top right.
2. Filter by policy type, carrier, US state, payment status (All / Paid / Unpaid / Unreviewed) and search.
3. Read the summary: ✅ Paid · ❌ Missing · Total active · 💵 Collected · ⏳ Pending.
4. Mark a policy **"✅ Paid"** or **"❌ Unpaid"**.
5. **Bulk actions:** tick several with the checkboxes and use "Mark paid / unpaid".
6. **⚙️ Statuses:** create your own (for example, Grace period).
7. **🔔 Templates:** email reminders per **type + carrier**, with a preview.

**Tips:**
- The history keeps every change: who and when.
- Combine filters ("ACA + Florida + Unreviewed").
- The ↻ button refreshes if another agent marked something while you were looking.` } },
  { id: 'renewals', cat: 'policies', nav: ['renewals'],
    title: { es: 'Renovaciones (y el kit de bienvenida)', en: 'Renewals (and the welcome kit)' },
    body: {
      es: `Panel de pólizas próximas a vencer, con una línea de estados y su historial.

![Renovaciones](ayuda/renewals-es.webp)

### Cómo
1. Menú → **Renovaciones**. Filtra por tipo de póliza y compañía.
2. Cada fila muestra el cliente (con los días que faltan), tipo, aseguradora, notas, estado y quién lo tocó.
3. Toca el botón de estado para moverlo: **PROCESO → OFERTA → DOCUMENTOS → LISTA → FINALIZADO ✉️**.
4. Al pasar a **FINALIZADO**, Deskte intenta enviar el **kit de bienvenida** por email; si faltan documentos, te pregunta si lo mandas igual.
5. **"Marcar renovada"** envía el kit (con vista previa antes de mandarlo) y la pasa a la pestaña **Renovadas**. **No cambia la fecha de renovación**: ponla tú en la póliza cuando tengas la del nuevo término.
6. **✉️ Kit de bienvenida:** edita el asunto y el cuerpo, en general o por tipo de póliza. **🎨 Status:** estados propios con color. **🕓 Historial:** toda la cadena de cambios.

**Consejos:**
- "Marcar renovada" NO es lo mismo que "Finalizado": úsalo solo cuando ya esté 100% renovada.
- El número naranja del menú cuenta los próximos 30 días (sin contar canceladas).
- Las notas son privadas de tu agencia.`,
      en: `A panel of policies coming up for renewal, with a status track and its history.

![Renewals](ayuda/renewals-en.webp)

### How
1. Menu → **Renewals**. Filter by policy type and carrier.
2. Each row shows the client (with days remaining), type, carrier, notes, status and who touched it.
3. Tap the status button to move it along: **IN PROCESS → OFFER → DOCUMENTS → READY → FINISHED ✉️**.
4. On **FINISHED**, Deskte tries to email the **welcome kit**; if documents are missing it asks whether to send anyway.
5. **"Mark renewed"** sends the kit (with a preview first) and moves it to the **Renewed** tab. **It does not change the renewal date**: set it on the policy when you have the new term's date.
6. **✉️ Welcome kit:** edit the subject and body, globally or per policy type. **🎨 Status:** your own colored statuses. **🕓 History:** the whole chain of changes.

**Tips:**
- "Mark renewed" is NOT the same as "Finished": use it only once it's 100% renewed.
- The orange menu badge counts the next 30 days (excluding cancelled).
- Notes are private to your agency.` } },

  // ── Tareas, Citas y Calendario ────────────────────────────────────────────
  { id: 'tasks', cat: 'tasks', nav: ['tasks'],
    title: { es: 'Tareas', en: 'Tasks' },
    body: {
      es: `Pendientes con prioridad, vencimiento y cliente vinculado.

### Cómo
1. Menú → **Tareas** (pestañas **General** y **Por cliente**).
2. **"+ Nueva tarea"**: Título*, Descripción, **Notas**, Prioridad (Alta/Media/Baja), Asignado a, Vencimiento y Cliente (opcional).
3. Mueve el estado con los botones **Todo / En progreso / Hecho**.
4. Filtra por estado y entre "Mis tareas / Todas".
5. **🚩 Estados** (dueño o admin): crea estados propios con color.

**Consejos:**
- La **nota** sale debajo del título con un 📌: úsala para apuntar el resultado de la llamada.
- Los 3 estados base no se borran; los tuyos se suman.
- "Por cliente" junta todas las tareas de una misma persona.`,
      en: `To-dos with priority, due date and a linked client.

### How
1. Menu → **Tasks** (**General** and **By client** tabs).
2. **"+ New task"**: Title*, Description, **Notes**, Priority (High/Medium/Low), Assignee, Due date and Client (optional).
3. Move the status with the **Todo / In progress / Done** buttons.
4. Filter by status and between "My tasks / All".
5. **🚩 Statuses** (owner or admin): create your own colored statuses.

**Tips:**
- The **note** shows under the title with a 📌: use it to log how the call went.
- The 3 base statuses can't be deleted; yours are added alongside.
- "By client" gathers every task for one person.` } },
  { id: 'recurring-tasks', cat: 'tasks', nav: ['recurring'],
    title: { es: 'Tareas Recurrentes', en: 'Recurring Tasks' },
    body: {
      es: `Reglas que crean tareas solas en un calendario fijo (diario, semanal, mensual, anual).

### Cómo
1. Menú → **Recurrentes** → **"+ Nueva tarea recurrente"**.
2. Llena Título*, Descripción, Prioridad, Cliente (opcional), **Frecuencia**, **Cada N**, día de la semana o del mes, **hora**, y los límites opcionales (termina el… / máximo de ejecuciones).
3. **Crear** — verás la "Próxima ejecución".
4. **⚡ Ejecutar ahora** crea una tarea hoy, para probar. También puedes **Pausar / Reactivar**, editar o eliminar.

**Consejos:**
- El servidor revisa cada 5 minutos; la tarea aparece a la hora que pusiste.
- Eliminar la regla no borra las tareas que ya creó.`,
      en: `Rules that create tasks by themselves on a fixed schedule (daily, weekly, monthly, yearly).

### How
1. Menu → **Recurring** → **"+ New recurring task"**.
2. Fill in Title*, Description, Priority, Client (optional), **Frequency**, **Every N**, day of week or month, **time**, and the optional limits (ends on… / max runs).
3. **Create** — you'll see the "Next run".
4. **⚡ Run now** creates a task today, to test it. You can also **Pause / Resume**, edit or delete.

**Tips:**
- The server checks every 5 minutes; the task appears at the time you set.
- Deleting the rule does not delete the tasks it already created.` } },
  { id: 'followups', cat: 'tasks', nav: ['followups'],
    title: { es: 'Seguimiento (tarjetas de regalo y documentos)', en: 'Follow-ups (gift cards & documents)' },
    body: {
      es: `Dos listas de trabajo pendiente que **se llenan solas** con datos que ya están en el sistema — casi no hay que escribir nada.

### 🎁 Tarjetas de regalo
Salen de tus **referidos**: cada cliente que tiene a alguien que lo refirió aparece aquí. Tú solo mueves el estado del premio: Pendiente → Enviado → Reenviado → Entregado.

### 📄 Documentos
Salen de tus **solicitudes de documentos**. "Recibido del cliente" se marca solo cuando el cliente sube el archivo; la parte de "Enviado al Marketplace / a la compañía" la marcas tú.

### Cómo
1. Menú → **Seguimiento**.
2. Cambia el estado en la fila. En cuanto tocas algo, la fila automática se guarda y ya no se duplica.
3. Puedes añadir filas **a mano** para lo que no venga de un referido o de una solicitud.
4. Con **Columnas** agregas las tuyas propias, y al crear cada una eliges cómo se llena: **escrito** a mano, una **lista** que se va armando sola con lo que el equipo escribe, o una **casilla** para marcar. Los estados también se personalizan.

**Consejo:** el número rojo del menú son los seguimientos **vencidos**. Si está en cero, no tienes nada atrasado.`,
      en: `Two lists of outstanding work that **fill themselves in** from data already in the system — there's almost nothing to type.

### 🎁 Gift cards
They come from your **referrals**: every client who has a referrer shows up here. You just move the reward status: Pending → Sent → Resent → Delivered.

### 📄 Documents
They come from your **document requests**. "Received from client" ticks itself when the client uploads the file; the "Sent to the Marketplace / carrier" half is yours to mark.

### How
1. Menu → **Follow-ups**.
2. Change the status on the row. The moment you touch one, the automatic row is saved and never duplicates.
3. You can add rows **by hand** for anything that doesn't come from a referral or a request.
4. **Columns** lets you add your own, and when you create one you pick how it is filled in: free **text**, a **list** that builds itself from what the team types, or a **checkbox**. Statuses are customizable too.

**Tip:** the red menu badge counts **overdue** follow-ups. At zero, nothing is late.` } },
  { id: 'citas_medicas', cat: 'tasks', nav: ['citas_medicas'],
    title: { es: 'Citas médicas (del cliente con su doctor)', en: 'Medical appointments (client with their doctor)' },
    body: {
      es: `Las citas del **cliente con su médico** — no reuniones tuyas. Están en dos sitios: dentro de la ficha (pestaña **🩺 Médicos y citas**) y todas juntas en el menú → **🩺 Citas médicas**.

### Cómo se crea
1. Ficha del cliente → pestaña **🩺 Médicos y citas** → **"+ Nueva cita"**. *(Desde la lista global no se crean: la cita cuelga de una persona de la casa, y esa lista es de la ficha.)*
2. Elige **de quién de la casa** es —titular, cónyuge o cada dependiente— y **qué médico** la atiende, de entre los que esa persona tiene puestos arriba. Si es un sitio al que se va una vez (un laboratorio), elige **"Otro proveedor"** y escríbelo: no hace falta darlo de alta como médico.
3. Fecha y hora, y lo que sepas: **n.º de orden**, **razón**, dirección, ciudad, ZIP, estado y **condado**.
4. **"Ponerla también en mi calendario"** va apagada por defecto. Márcala solo si vas a acompañar o a llamar ese día — y ojo: en el calendario **no** aparece la razón, porque un calendario se comparte y se sincroniza con Google.

### Cómo se trabaja el día a día
1. Menú → **🩺 Citas médicas**.
2. Filtra: **Próximas · Esta semana · Este mes · Ya pasadas · Todas**, por estatus, o busca por nombre de cliente.
3. El **estatus se cambia desde la propia lista**, en el desplegable de color: Pendiente → Confirmada → Completada, o No asistió / Cancelada. Quién la confirmó y cuándo se guarda solo.

### Recordarle al cliente por correo
1. Abajo del formulario de la cita: **📧 Recordarle al cliente por correo**. Marca **10 días antes**, **3 días antes** o **1 día antes** — o escribe los días que quieras y pulsa Enter (hasta cinco).
2. **Si no marcas nada, al cliente no le llega ningún correo por esa cita.** A propósito: es lo único de este módulo que sale del CRM.
3. Sale de **tu buzón** (si eres asiento, del de tu agente), en el **idioma del cliente**, con la fecha, el médico, la dirección, el teléfono y el n.º de orden. **No lleva la razón** — un correo se reenvía y se queda en la bandeja; el cliente ya sabe a qué va.
4. El mismo aviso no sale dos veces, y **si mueves la cita de fecha se vuelven a mandar** para la fecha nueva. En la tarjeta verás **📧 10d/1d**, con ✓ cuando alguno ya salió.

### Lo que conviene saber
- La **razón** es el campo que de verdad se lee el día de la cita: qué chequeo es, quién atiende, de qué número se sacó, quién hace de intérprete y cuántos minutos antes hay que llegar.
- El **buscador busca por nombre de cliente**, no por la razón ni por el médico: lo demás está cifrado.
- Si quitas de la ficha el médico al que colgaba una cita, la cita **no se queda sin proveedor**: enseña lo que se guardó con ella y lo avisa.
- **De la lista no se cambia de persona ni de médico** — eso se hace desde la ficha.

**Consejo:** todo lo que escribas va cifrado, y ni la razón ni el proveedor entran en el registro de actividad. Lo ve todo el que puede abrir la ficha; no hay PIN.`,
      en: `Your client's appointments **with their doctor** — not your own meetings. They live in two places: inside the client record (**🩺 Doctors & visits** tab) and all together under Menu → **🩺 Medical visits**.

### How to create one
1. Client record → **🩺 Doctors & visits** tab → **"+ New appointment"**. *(They are not created from the global list: an appointment hangs off a household member, and that list belongs to the record.)*
2. Pick **whose** it is — primary, spouse or each dependent — and **which doctor** sees them, from the ones on file for that person. For a one-off place (a lab), pick **"Other provider"** and type it: no need to add them as a doctor.
3. Date and time, plus whatever you know: **order no.**, **reason**, address, city, ZIP, state and **county**.
4. **"Also put it on my calendar"** is off by default. Turn it on only if you are going along or calling that day — and note the reason is **not** copied there, because calendars get shared and synced to Google.

### Day-to-day
1. Menu → **🩺 Medical visits**.
2. Filter: **Upcoming · This week · This month · Past · All**, by status, or search by client name.
3. The **status changes right in the list**, from the coloured dropdown: Pending → Confirmed → Completed, or No-show / Cancelled. Who confirmed it and when is saved for you.

### Reminding the client by email
1. At the bottom of the appointment form: **📧 Remind the client by email**. Tick **10 days before**, **3 days before** or **1 day before** — or type any number of days and press Enter (up to five).
2. **If you tick nothing, the client gets no email about that appointment.** On purpose: it is the only part of this module that leaves the CRM.
3. It goes out from **your mailbox** (a seat sends from its supervising agent), in the **client's language**, with the date, doctor, address, phone and order no. **It does not carry the reason** — emails get forwarded and stay in the inbox; the client already knows what it is for.
4. The same reminder never goes out twice, and **if you move the appointment the reminders are sent again** for the new date. The card shows **📧 10d/1d**, with a ✓ once one has gone out.

### Worth knowing
- The **reason** is what actually gets read on the day: which check-up it is, who sees them, where the appointment came from, who interprets, and how early to arrive.
- The **search matches client names**, not the reason or the doctor: everything else is encrypted.
- If you remove the doctor an appointment hung off, the appointment **does not lose its provider**: it shows what was saved with it, and says so.
- **Whose it is and which doctor** are changed from the record, not from this list.

**Tip:** everything you type is encrypted, and neither the reason nor the provider goes into the activity log. Anyone who can open the client record can see it; there is no PIN.` } },
  { id: 'medicamentos', cat: 'clients', nav: ['clients'],
    title: { es: 'Medicamentos del cliente (con buscador y dosis)', en: 'Client medications (with search and doses)' },
    body: {
      es: `La lista de **lo que toma cada persona de la casa**, con su dosis. Está en la ficha del cliente → pestaña **🩺 Médicos y citas** → sección **💊 Medicamentos**.

### Cómo
1. En la persona que corresponda (titular, cónyuge o dependiente), pulsa **"+ Medicamento"**.
2. Empieza a escribir el nombre (por ejemplo *metf*, *Eliquis*, *insulina*). Deskte te sugiere los medicamentos del **catálogo oficial de la Biblioteca Nacional de Medicina** de EE. UU., con nombre genérico y de marca.
3. Elige uno: debajo de **Dosis** salen **las dosis que existen de verdad** de ese medicamento (500 mg, 850 mg…). Toca la que toma.
4. Pon **cada cuánto** lo toma (hay sugerencias), **quién se lo receta** (salen los médicos que ya están en la ficha) y una nota si hace falta. **Guardar**.

### Bueno saber
- **Si no aparece en el catálogo, escríbelo a mano** y guárdalo igual. Nada es obligatorio salvo el nombre.
- Todo va **cifrado**, como el SSN, y ni el nombre ni la dosis entran en el registro de actividad. Lo ve todo el que puede abrir la ficha.
- Al catálogo solo viaja lo que escribes en el buscador (*"metf"*), nunca el nombre del cliente.

### ¿Lo cubre el plan? (Mercado de Salud)
En **Cotizaciones → Buscar en el Mercado**, después de buscar planes, sale **💊 ¿Cubre sus medicamentos?**. Cada plan dice **"Cubre sus 3 medicamentos"** o **"No cubre: Ozempic"**, y puedes marcar **"Solo planes que los cubren todos"**. Toca un medicamento para no contarlo.
- Con un **cliente existente** salen solos los de su ficha.
- Con un **cliente nuevo** (o para añadir uno más), escríbelo en el buscador de ese mismo recuadro y elige la dosis. Los que añades ahí **no se guardan** en ninguna ficha.
- Solo se revisan los que se eligieron **del buscador con su dosis**; los escritos a mano no se pueden revisar (se dice cuántos).
- Es el dato de HealthCare.gov: confírmalo en la lista de medicamentos del plan antes de inscribir. Para **Medicare** no hay forma pública de revisarlo: usa Plan Finder.

### Médicos: buscarlos y ver si están en la red
- **En la ficha** (Médicos y citas → **+ Médico**): arriba sale **🔎 Buscar en el directorio del Mercado**. Escribe el nombre del doctor o la especialidad y el ZIP, elige uno y se llenan solos el nombre, la especialidad, el NPI, la dirección, el teléfono y de dónde salió. Revisa y guarda.
- **En el cotizador del Mercado**, debajo de los medicamentos sale **🩺 ¿Sus médicos están en la red?**: cada plan dice **"Su médico está en la red"** o **"Fuera de la red: Dr. X"**. Salen solos los de la ficha que tienen NPI, y con **+ Buscar médico** añades otros (también para un cliente nuevo, sin guardarse).
- Es el directorio de HealthCare.gov: confírmalo con el consultorio antes de inscribir. Para **Medicare** no hay directorio público de redes.`,
      en: `The list of **what each household member takes**, with the dose. It lives in the client record → **🩺 Doctors & visits** tab → **💊 Medications** section.

### How
1. On the right person (primary, spouse or dependent), click **"+ Medication"**.
2. Start typing the name (e.g. *metf*, *Eliquis*, *insulin*). Deskte suggests medications from the **official U.S. National Library of Medicine catalog**, generic and brand names.
3. Pick one: under **Dose** you get **the doses that actually exist** for it (500 mg, 850 mg…). Tap the one they take.
4. Add **how often** (there are suggestions), **who prescribes it** (the doctors already on the record show up) and a note if needed. **Save**.

### Worth knowing
- **If it is not in the catalog, type it by hand** and save anyway. Only the name is required.
- Everything is **encrypted**, like the SSN, and neither the name nor the dose goes into the activity log. Anyone who can open the record can see it.
- Only what you type in the search (*"metf"*) goes to the catalog, never the client's name.

### Does the plan cover it? (Health Marketplace)
In **Quotes → Search Marketplace**, after searching plans you get **💊 Does it cover their medications?**. Each plan says **"Covers all 3 medications"** or **"Does not cover: Ozempic"**, and you can tick **"Only plans that cover all of them"**. Tap a medication to leave it out.
- With an **existing client**, the ones on their record show up on their own.
- With a **new client** (or to add one more), type it in that box's search and pick the dose. Ones added there are **not saved** to any record.
- Only medications picked **from the search with their dose** can be checked; hand-typed ones can't (the count is shown).
- It is HealthCare.gov's data: confirm it on the plan's drug list before enrolling. **Medicare** has no public way to check it: use Plan Finder.

### Doctors: find them and check the network
- **In the record** (Doctors & visits → **+ Doctor**): at the top there is **🔎 Search the Marketplace directory**. Type the doctor's name or specialty and the ZIP, pick one and the name, specialty, NPI, address, phone and source fill in. Review and save.
- **In the Marketplace quote**, below the medications you get **🩺 Are their doctors in network?**: each plan says **"Their doctor is in network"** or **"Out of network: Dr. X"**. The record's doctors with an NPI show up on their own, and **+ Search doctor** adds others (also for a new client, not saved).
- It is HealthCare.gov's directory: confirm with the office before enrolling. **Medicare** has no public network directory.` } },
  { id: 'citas', cat: 'tasks', nav: ['citas'],
    title: { es: 'Citas (agendar con un cliente)', en: 'Appointments (schedule with a client)' },
    body: {
      es: `Agenda una reunión con un cliente sobre un **tipo de póliza** concreto, y el formulario te pide justo lo que hace falta para esa conversación.

### Cómo
1. Menú → **Citas** → **"Nueva cita"**.
2. Elige el cliente, la fecha y la hora, y el **modo**: 📍 en persona, 📞 llamada o 🎥 video.
3. Elige el **tipo de póliza**. Según cuál sea, aparecen campos distintos:
   - **ACA:** ingreso anual estimado, personas en el hogar, plan actual.
   - **Medicare Advantage / Suplemento / Parte D:** Medicare ID (MBI), médico de cabecera, medicamentos.
   - **Vida:** monto de cobertura, beneficiario, condiciones de salud.
   - **Auto:** vehículo, VIN, licencia de conducir. **Hogar:** dirección, año de construcción.
4. Al guardar se manda una copia por correo a la agencia; la dirección viene puesta y puedes cambiarla.
5. Mueve el estado: **Agendada → Confirmada → Completada** (o Cancelada).

**Consejo:** borrar una cita depende del permiso de borrado de tu asiento; si no lo tienes, pídele al dueño.`,
      en: `Book a meeting with a client about a specific **policy type**, and the form asks exactly what that conversation needs.

### How
1. Menu → **Appointments** → **"New appointment"**.
2. Pick the client, the date and time, and the **mode**: 📍 in person, 📞 phone or 🎥 video.
3. Pick the **policy type**. Different fields appear depending on it:
   - **ACA:** estimated annual income, household size, current plan.
   - **Medicare Advantage / Supplement / Part D:** Medicare ID (MBI), primary doctor, medications.
   - **Life:** coverage amount, beneficiary, health conditions.
   - **Auto:** vehicle, VIN, driver's license. **Home:** address, year built.
4. On save a copy is emailed to the agency; the address is pre-filled and you can change it.
5. Move the status along: **Scheduled → Confirmed → Completed** (or Cancelled).

**Tip:** deleting an appointment depends on your seat's delete permission; if you don't have it, ask the owner.` } },
  { id: 'calendar', cat: 'tasks', nav: ['calendar', 'month_events'],
    title: { es: 'Calendario y Eventos del Mes', en: 'Calendar & Month Events' },
    body: {
      es: `Citas, seguimientos, llamadas, reuniones, vencimientos, cumpleaños y renovaciones, todo en un mismo sitio.

### Cómo
1. Menú → **Calendario**. Muévete con ← → o vuelve con **Hoy**.
2. **"+ Nuevo evento"** (o clic en una fecha): Título, Fecha, Hora, Tipo (cita, seguimiento, renovación, llamada, reunión, vencimiento, cumpleaños, otro), Cliente y Notas → **Guardar**.
3. Los **cumpleaños** se generan solos cada año desde las fechas de nacimiento de tus clientes.
4. Hay un botón para generar **recordatorios de renovación** en lote.

### Eventos del Mes
Es la misma información del calendario vista como lista del mes: útil para repasar de un tirón lo que viene sin ir día por día.

**Consejo:** los eventos de renovación llevan un badge rojo con los días que faltan.`,
      en: `Appointments, follow-ups, calls, meetings, deadlines, birthdays and renewals, all in one place.

### How
1. Menu → **Calendar**. Move with ← → or jump back with **Today**.
2. **"+ New event"** (or click a date): Title, Date, Time, Type (appointment, follow-up, renewal, call, meeting, deadline, birthday, other), Client and Notes → **Save**.
3. **Birthdays** generate themselves every year from your clients' dates of birth.
4. There's a button to generate **renewal reminders** in bulk.

### Month Events
The same calendar information shown as a list for the month: handy for reviewing what's coming without stepping through each day.

**Tip:** renewal events carry a red badge with the days remaining.` } },
  { id: 'booking', cat: 'tasks', nav: ['booking'],
    title: { es: 'Booking Link: paso a paso (que tus clientes agenden solos)', en: 'Booking Link: step by step (let clients book you)' },
    body: {
      es: `Un enlace público donde tus clientes y leads agendan una cita contigo **sin llamarte**: eligen día y hora, dejan sus datos y la cita cae sola en tu **Calendario**. Se prepara **una vez** (unos 10 minutos) y después solo lo compartes.

**Los números de cada imagen son los mismos que los de cada paso.** Las capturas salen de un Deskte de práctica con clientes inventados.

### Parte 1 · Prepara tu enlace (una sola vez)

### Paso 1 · Abre Booking Link
En el buscador del menú de la izquierda escribe **booking** y toca **Booking Link**. La primera vez, Deskte te crea un enlace con tu nombre, **apagado**, para que lo revises antes de que alguien lo use.
![El buscador del menú con «booking» escrito y la opción Booking Link resaltada](ayuda/booking-01-menu.webp)

### Paso 2 · Nombre, dirección, duración y título
- **Nombre del enlace:** es para ti (por ejemplo «Salud y Medicare»). El cliente no lo ve.
- **Enlace (slug):** la parte final de tu dirección, por ejemplo **app.deskte.com/book/sofia-marrero**. Usa tu nombre, sin espacios ni acentos.
- **Duración de cita** y **Tiempo entre citas** (el respiro que te queda entre una cita y la siguiente).
- **Zona horaria:** la de tu oficina. El cliente ve las horas en esta zona.
- **Título de la cita (público):** lo que el cliente lee arriba, por ejemplo «Consulta gratis de seguros».
![Los ajustes del enlace: nombre, slug, duración, tiempo entre citas, zona horaria y título](ayuda/booking-02-ajustes.webp)

### Paso 3 · Para qué líneas es y quién atiende (opcional)
- Marca las **líneas** que atiende este enlace (Salud, Medicare, Vida, Auto…). Con dos o más, el cliente elige para qué es la cita, y una hora que bloqueaste solo para Salud sigue libre para Medicare.
- En **¿Quién atiende las citas de este enlace?** enciende a las personas de tu equipo que pueden tomar cada línea. El cliente ve una hora libre si al menos una de ellas está libre, y las citas se reparten parejo. Si no enciendes a nadie más, todas las citas son contigo. Cada persona puede tener su propio horario.
- Más abajo puedes escribir un **Mensaje de bienvenida** (opcional), que el cliente lee al abrir el enlace.
![Las líneas Salud y Medicare marcadas y quién atiende cada una](ayuda/booking-03-lineas.webp)

### Paso 4 · Tu horario de la semana
En **Disponibilidad** enciende los días que atiendes y pon la hora de entrada y de salida. En **🍽️ Descanso** pon tu almuerzo: esas horas nunca se ofrecen. Los días apagados no salen.
![El horario de lunes a viernes con el descanso de 12:00 a 1:00](ayuda/booking-04-horario.webp)

### Paso 5 · Bloquea vacaciones y días libres
En **🚫 Días bloqueados** toca un día del calendario para bloquearlo **todo el día** (sale en rojo). A la derecha puedes cambiarlo a **Solo unas horas** y poner de qué hora a qué hora (sale en amarillo), por ejemplo para una cita médica tuya. Toca el día otra vez para desbloquearlo.
![El calendario de días bloqueados con un día entero en rojo y otro de unas horas en amarillo](ayuda/booking-05-dias-bloqueados.webp)

### Paso 6 · Hasta cuándo y con cuánto aviso
- **Reservable hasta (días):** qué tan adelante pueden agendar (por ejemplo, 30 días).
- **Aviso mínimo (horas):** nadie puede agendar con menos tiempo que este. Con 2 horas, a las 10 de la mañana nadie te agenda para las 11.
![Reservable hasta 30 días y aviso mínimo de 2 horas](ayuda/booking-06-anticipacion.webp)

### Paso 7 · Actívalo y toca «Guardar»
Arriba a la derecha. Toca **Activar** (se guarda en el momento): al lado del título debe decir **Activo** en verde. Mientras esté **Inactivo**, quien abra el enlace verá «Enlace no disponible».
- Cada vez que cambies algo más, toca **Guardar**. Si sales sin guardar, el cambio se pierde.
![Los botones Desactivar y Guardar resaltados, con el enlace Activo](ayuda/booking-07-activar-guardar.webp)

### Parte 2 · Compártelo

### Paso 8 · Copia tu enlace y compártelo
En **Tu link público** toca **Copiar** y pégalo donde te escriben tus clientes: tu firma de correo, WhatsApp, Instagram o Facebook, tu página web o un mensaje de texto. Con **Abrir** ves exactamente lo que verá el cliente.
![Tu link público con los botones Copiar y Abrir](ayuda/booking-08-copiar-enlace.webp)

### Parte 3 · Lo que ve tu cliente

### Paso 9 · Elige para qué es la cita
Esta pregunta solo sale si marcaste dos o más líneas en el paso 3.
![La página pública preguntando «¿Para qué es tu cita?» con Salud y Medicare](ayuda/booking-09-para-que.webp)

### Paso 10 · Elige día y hora
Los días con horas libres están resaltados. Solo salen horas que de verdad tienes libres: tu horario, menos lo bloqueado, lo que ya tienes en el calendario y el tiempo entre citas. Las horas van en tu zona horaria (dice cuál, arriba a la derecha).
![El calendario de la página pública con el día elegido y sus horas libres](ayuda/booking-10-dia-y-hora.webp)

### Paso 11 · Escribe sus datos y confirma
Nombre completo, correo y, si quiere, teléfono y una nota. Toca **Confirmar cita**. A veces sale una casilla de «no soy un robot».
![El formulario con nombre, correo, teléfono y nota, y el botón Confirmar cita](ayuda/booking-11-datos.webp)

### Paso 12 · ¡Cita confirmada!
El cliente ve el día, la hora y con quién. Si tienes tu correo conectado en **Mi Correo**, además le llega un correo de confirmación **desde tu dirección**, con el botón **Reprogramar o cancelar**.
![La pantalla «¡Cita confirmada!» con el día y la hora](ayuda/booking-12-confirmada.webp)

### Parte 4 · Después de la reserva

### Paso 13 · La cita ya está en Deskte
Sin que hagas nada:
- Aparece en **Citas Reservadas** (abajo en esta misma pantalla) y en tu **Calendario**.
- Se crea un **Lead** con su nombre, correo y teléfono.
- Te llega un aviso a la campana 🔔 y un correo «📅 Nueva cita».
- Antes de la cita, al cliente le llega un **recordatorio por correo** (24 horas antes, de fábrica). Lo cambias en **Mi Perfil → Preferencias → Avisos → ✉️ Recordatorio al cliente**.
![La lista Citas Reservadas con tres citas](ayuda/booking-13-citas-reservadas.webp)

### Paso 14 · Si el cliente necesita cambiarla
En el correo de confirmación, el cliente toca **Reprogramar o cancelar**. Puede elegir otra hora libre o cancelar la cita, y tu calendario se actualiza solo.
![La página de la cita con los botones Reprogramar y Cancelar cita](ayuda/booking-14-reprogramar.webp)

### ¿Quieres un enlace para cada línea?

### Paso 15 · Crea otro con «+ Nuevo enlace»
En **Enlaces guardados** ves todos tus enlaces, su dirección y si están activos. **+ Nuevo enlace** crea otro, por ejemplo uno de **Vida** de 45 minutos aparte del de Salud. **Editar** lo abre abajo para cambiarlo y **Borrar** lo elimina. El último que te queda no se puede borrar: si no quieres que se use, desactívalo.
![Enlaces guardados con dos enlaces, Salud y Medicare activo y Vida inactivo](ayuda/booking-15-varios-enlaces.webp)

### Bueno saber
- Si cambias el **slug** (paso 2), la dirección vieja deja de funcionar. Avisa a quien ya la tenga.
- Los **asistentes** no tienen enlace propio: abren y ajustan el de su agente. La dueña de la agencia puede abrir el enlace de cualquier agente con **Enlace de:**, arriba.
- Si al cliente no le llega el correo de confirmación, revisa que tengas tu correo conectado en **Mi Correo**.
- Para que nadie te llene la agenda con citas falsas, cada enlace tiene un límite de citas por día.`,
      en: `A public link where your clients and leads book an appointment with you **without calling**: they pick a day and time, leave their details, and the appointment lands on your **Calendar** by itself. You set it up **once** (about 10 minutes) and from then on you just share it.

**The number on each picture matches the number of each step.** The screenshots come from a practice Deskte with made-up clients.

### Part 1 · Set up your link (once)

### Step 1 · Open Booking Link
In the menu search box on the left type **booking** and tap **Booking Link**. The first time, Deskte creates a link with your name, **turned off**, so you can review it before anyone uses it.
![The menu search with «booking» typed and Booking Link highlighted](ayuda/booking-01-menu-en.webp)

### Step 2 · Name, address, length and title
- **Link name:** just for you (for example "Salud y Medicare"). The client doesn't see it.
- **Link (slug):** the last part of your address, for example **app.deskte.com/book/sofia-marrero**. Use your name, with no spaces or accents.
- **Appointment duration** and **Time between appointments** (the breather you keep between one appointment and the next).
- **Timezone:** your office's. The client sees times in this zone.
- **Meeting title (public):** what the client reads at the top, for example "Free insurance consultation".
![The link settings: name, slug, duration, time between appointments, timezone and title](ayuda/booking-02-ajustes-en.webp)

### Step 3 · Which lines it takes and who handles them (optional)
- Tap the **lines** this link takes (Health, Medicare, Life, Auto…). With two or more, the client picks what the appointment is for, and a time you blocked only for Health stays open for Medicare.
- In **Who takes this link's appointments?** turn on the team members who can take each line. The client sees a time as open if at least one of them is free, and appointments are shared out evenly. If you don't turn anyone else on, every appointment is with you. Each person can have their own hours.
- Further down you can write a **Welcome message** (optional) that the client reads when they open the link.
![Health and Medicare selected and who handles each one](ayuda/booking-03-lineas-en.webp)

### Step 4 · Your weekly hours
Under **Availability** turn on the days you work and set your start and end time. Put your lunch in **🍽️ Break**: those hours are never offered. Days that are off don't show.
![Monday to Friday hours with a 12:00 to 1:00 break](ayuda/booking-04-horario-en.webp)

### Step 5 · Block vacations and days off
Under **🚫 Blocked days** tap a day on the calendar to block it **all day** (it turns red). On the right you can switch it to **Some hours only** and set the hours (it turns yellow), for example for your own doctor's appointment. Tap the day again to unblock it.
![The blocked days calendar with one full day in red and a few hours in yellow](ayuda/booking-05-dias-bloqueados-en.webp)

### Step 6 · How far ahead and how much notice
- **Bookable ahead (days):** how far into the future they can book (for example, 30 days).
- **Minimum notice (hours):** nobody can book with less notice than this. With 2 hours, at 10 a.m. nobody can book you for 11.
![Bookable ahead 30 days and minimum notice 2 hours](ayuda/booking-06-anticipacion-en.webp)

### Step 7 · Turn it on and tap "Save"
Top right. Tap **Turn on** (it saves right away): next to the title it should say **Active** in green. While it's **Inactive**, anyone who opens the link sees "Link unavailable".
- Every time you change anything else, tap **Save**. If you leave without saving, the change is lost.
![The Turn off and Save buttons highlighted, with the link Active](ayuda/booking-07-activar-guardar-en.webp)

### Part 2 · Share it

### Step 8 · Copy your link and share it
In **Your public link** tap **Copy** and paste it wherever your clients reach you: your email signature, WhatsApp, Instagram or Facebook, your website, or a text message. **Open** shows you exactly what the client will see.
![Your public link with the Copy and Open buttons](ayuda/booking-08-copiar-enlace-en.webp)

### Part 3 · What your client sees

### Step 9 · They pick what the appointment is for
This question only shows if you selected two or more lines in step 3.
![The public page asking «What is your appointment about?» with Health and Medicare](ayuda/booking-09-para-que-en.webp)

### Step 10 · They pick a day and time
Days with open times are highlighted. Only times you're really free show: your hours, minus what's blocked, what's already on your calendar, and the time between appointments. Times are in your timezone (it says which, top right).
![The public page calendar with the chosen day and its open times](ayuda/booking-10-dia-y-hora-en.webp)

### Step 11 · They enter their details and confirm
Full name, email and, if they want, phone and a note. They tap **Confirm booking**. Sometimes an "I'm not a robot" box shows up.
![The form with name, email, phone and note, and the Confirm booking button](ayuda/booking-11-datos-en.webp)

### Step 12 · You're booked!
The client sees the day, the time and who it's with. If your email is connected in **My Inbox**, they also get a confirmation email **from your address**, with a **Reprogramar o cancelar** (reschedule or cancel) button.
![The «You're booked!» screen with the day and time](ayuda/booking-12-confirmada-en.webp)

### Part 4 · After the booking

### Step 13 · The appointment is already in Deskte
Without you doing anything:
- It shows under **Booked Appointments** (at the bottom of this same screen) and on your **Calendar**.
- A **Lead** is created with their name, email and phone.
- You get a bell 🔔 notification and a "📅 Nueva cita" email.
- Before the appointment, the client gets an **email reminder** (24 hours before, by default). Change it in **My Profile → Preferences → Alerts → ✉️ Reminder to the client**.
![The Booked Appointments list with three appointments](ayuda/booking-13-citas-reservadas-en.webp)

### Step 14 · If the client needs to change it
In the confirmation email, the client taps **Reprogramar o cancelar**. They can pick another open time or cancel, and your calendar updates by itself.
![The booking page with the Reschedule and Cancel booking buttons](ayuda/booking-14-reprogramar-en.webp)

### Want one link per line?

### Step 15 · Create another with "+ New link"
**Saved links** lists all your links, their address and whether they're active. **+ New link** creates another one, for example a 45-minute **Life** link separate from the Health one. **Edit** opens it below to change it and **Delete** removes it. You can't delete the last one you have: if you don't want it used, turn it off.
![Saved links with two links, Salud y Medicare active and Vida inactive](ayuda/booking-15-varios-enlaces-en.webp)

### Good to know
- If you change the **slug** (step 2), the old address stops working. Tell anyone who already has it.
- **Assistants** don't have their own link: they open and adjust their agent's. The agency owner can open any agent's link with **Link of:** at the top.
- If the client doesn't get the confirmation email, check that your email is connected in **My Inbox**.
- So nobody can flood your calendar with fake bookings, each link has a daily limit.` } },

  // ── Correo y Comunicación ─────────────────────────────────────────────────
  { id: 'mass-email', cat: 'comms', nav: ['mass_email'],
    title: { es: 'Email Masivo', en: 'Mass Email' },
    body: {
      es: `Manda un correo a muchos clientes a la vez, con seguimiento de aperturas, clics y rebotes.

### Cómo
1. Menú → **Email Masivo**, pestaña **Componer**.
2. Escribe el **asunto** y el **cuerpo**. Puedes **🖼️ insertar imágenes**.
3. A la derecha elige destinatarios: pega correos a mano **o** filtra tus clientes (por estado, tipo de póliza, compañía, **🎂 edad** y más) y marca casillas. Tienes "Todos" y "Limpiar".
   - **Edad:** escribe *desde* y *hasta* (por ejemplo 18 – 26, o solo *desde* 65). Cuentan los dos extremos. Quien no tiene fecha de nacimiento en la ficha no entra, y la pantalla te dice cuántos son.
4. **👁️ Vista previa** para verlo con los datos del primer destinatario.
5. **Enviar a [N]** → confirma → **Enviar** ahora o **Programar** para más tarde.
6. En **Historial** ves las campañas pasadas con su % de apertura y clics.

**Consejos:**
- Personaliza con {{firstName}}, {{lastName}}, {{email}}, {{agentName}}.
- Si no conectaste tu correo, sale del remitente genérico y es más fácil que caiga en SPAM.
- Si el contenido lleva datos sensibles (SSN y similares), Deskte te pide confirmar antes de enviar.`,
      en: `Send one email to many clients at once, with open, click and bounce tracking.

### How
1. Menu → **Mass Email**, **Compose** tab.
2. Write the **subject** and **body**. You can **🖼️ insert images**.
3. On the right pick recipients: paste addresses by hand **or** filter your clients (by status, policy type, carrier, **🎂 age** and more) and tick boxes. "All" and "Clear" are there too.
   - **Age:** type *from* and *to* (e.g. 18 – 26, or just *from* 65). Both ends count. Clients with no date of birth on file are left out, and the screen tells you how many.
4. **👁️ Preview** to see it with the first recipient's data.
5. **Send to [N]** → confirm → **Send** now or **Schedule** for later.
6. **History** shows past campaigns with their open and click rates.

**Tips:**
- Personalize with {{firstName}}, {{lastName}}, {{email}}, {{agentName}}.
- If you haven't connected your email, it goes out from the generic sender and is likelier to hit SPAM.
- If the content carries sensitive data (SSN and the like), Deskte asks you to confirm before sending.` } },
  { id: 'campaigns', cat: 'comms', nav: ['campaigns'],
    title: { es: 'Campañas (feriados y plantillas propias)', en: 'Campaigns (holidays & your own templates)' },
    body: {
      es: `Plantillas de correo para ocasiones — feriados, cumpleaños, eventos — que mandas por lote.

![Campañas](ayuda/campaigns-es.webp)

### Cómo
1. Menú → **Campañas**, con las pestañas **🎉 Feriados**, **📧 Email** y **✨ Propias**.
2. En **Propias**, crea una plantilla: nombre (EN/ES), emoji, asunto (EN/ES), cuerpo (EN/ES) e 🖼️ imagen.
3. En cualquier plantilla: **Ver / Editar / Enviar**.
4. Al **Enviar**: filtra por estado o busca, marca destinatarios, elige idioma 🇺🇸/🇪🇸, programa si quieres, y confirma. Verás una barra de progreso.

**Consejos:**
- Editar una plantilla no cambia los correos que ya salieron.
- Los envíos masivos por WhatsApp y SMS todavía se hacen uno a uno desde la ficha del cliente.`,
      en: `Email templates for occasions — holidays, birthdays, events — sent in batches.

![Campaigns](ayuda/campaigns-en.webp)

### How
1. Menu → **Campaigns**, with the **🎉 Holidays**, **📧 Email** and **✨ Custom** tabs.
2. Under **Custom**, create a template: name (EN/ES), emoji, subject (EN/ES), body (EN/ES) and an 🖼️ image.
3. On any template: **View / Edit / Send**.
4. On **Send**: filter by status or search, tick recipients, choose the language 🇺🇸/🇪🇸, schedule it if you like, and confirm. A progress bar shows the run.

**Tips:**
- Editing a template doesn't change emails that already went out.
- Bulk WhatsApp and SMS are still done one by one from the client profile.` } },
  { id: 'surveys', cat: 'comms', nav: ['surveys'],
    title: { es: 'Encuestas al cliente', en: 'Client surveys' },
    body: {
      es: `Pregúntale a tus clientes cómo los atiendes y qué más necesitan. Cada cliente recibe **su propio enlace**, en su idioma, y solo puede responder una vez.

### Crear una encuesta
1. Menú → **Encuestas** → **"Nueva encuesta"**.
2. Elige una **plantilla lista** (Satisfacción del servicio, ¿Nos recomendaría? 0–10, Después de una póliza nueva, Renovación, Interés en otros seguros, Atención del agente) o empieza **en blanco**.
3. Cambia lo que quieras: título, introducción, preguntas y mensaje de gracias, en **español e inglés**.
4. Tipos de pregunta: ⭐ estrellas 1–5, 🔢 escala 0–10, ⚪ una opción, ☑️ varias opciones, ✅ sí/no y ✏️ respuesta libre. Marca **Obligatoria** si hace falta.
5. **Guardar encuesta**.

### Enviarla a muchos clientes
1. Ve a **Email Masivo** y elige a quién con los filtros de siempre.
2. En el cuerpo, pulsa **📋 Encuesta** y elige cuál. Se mete un campo que en el correo de cada cliente sale como el botón **"Responder la encuesta"**.
3. Envía o programa como siempre. Respeta la lista de No-Enviar y lleva el pie de baja.

### ⚡ Automático (en el editor de la encuesta)
- **Puntuación baja** (1–2 estrellas o 0–6 de 10): se crea una **tarea** para el agente del cliente para llamarle. Viene encendido.
- **Interés en otro seguro**: debajo de cada opción eliges **💼 Lead: Vida, Dental…**. Quien la marque genera un **lead** para su agente. Viene encendido. La plantilla "Interés en otros seguros" ya lo trae puesto.
- **Enviarla sola**: marca "Enviarla sola por correo N días después de una póliza nueva", con tu asunto y mensaje. Sale del correo conectado del agente, **una sola vez por cliente**.
- También se puede mandar por **SMS**: en Email Masivo, modo SMS, el botón **📋 Encuesta** mete el enlace.

### Ver los resultados
- En la tarjeta de la encuesta: **enviadas**, **respondidas** y el %.
- **Resultados**: promedio de estrellas, la puntuación de recomendación (promotores menos detractores), gráficas por pregunta, los comentarios y la tabla por cliente. **CSV** para Excel.
- En la ficha del cliente, pestaña **Encuestas**: lo que ese cliente contestó, y **Copiar enlace** para mandárselo por WhatsApp o texto.

**Consejos:**
- **No preguntes datos médicos ni el Seguro Social** en una encuesta por correo; el editor te avisa si lo detecta.
- **Activa** apagada = los enlaces enseñan "encuesta cerrada". Borrarla hace lo mismo y ya no se ven sus respuestas.
- Cada agente ve las respuestas de **sus** clientes.`,
      en: `Ask your clients how you are doing and what else they need. Each client gets **their own link**, in their language, and can answer only once.

### Create a survey
1. Menu → **Surveys** → **"New survey"**.
2. Pick a **ready-made template** (Service satisfaction, Would you recommend us? 0–10, After a new policy, Renewal, Interest in other coverage, Agent service) or start **blank**.
3. Change anything: title, intro, questions and thank-you message, in **Spanish and English**.
4. Question types: ⭐ stars 1–5, 🔢 scale 0–10, ⚪ single choice, ☑️ multiple choice, ✅ yes/no and ✏️ free text. Tick **Required** when needed.
5. **Save survey**.

### Send it to many clients
1. Go to **Mass Email** and pick recipients with the usual filters.
2. In the body, click **📋 Survey** and choose one. It inserts a field that shows up in each client's email as a **"Take the survey"** button.
3. Send or schedule as usual. The Unsubscribed list and the unsubscribe footer still apply.

### ⚡ Automatic (in the survey editor)
- **Low score** (1–2 stars or 0–6 out of 10): a **task** is created for the client's agent to call them. On by default.
- **Interest in other coverage**: under each option pick **💼 Lead: Life, Dental…**. Whoever picks it creates a **lead** for their agent. On by default. The "Interest in other coverage" template comes with it set.
- **Send it automatically**: tick "Email it automatically N days after the client gets a new policy", with your subject and message. It goes from the agent's connected mailbox, **once per client**.
- It can also go by **SMS**: in Mass Email, SMS mode, the **📋 Survey** button inserts the link.

### See the results
- On the survey card: **sent**, **answered** and the %.
- **Results**: average stars, the recommendation score (promoters minus detractors), charts per question, comments and the per-client table. **CSV** for Excel.
- In the client profile, **Surveys** tab: what that client answered, and **Copy link** to send it by WhatsApp or text.

**Tips:**
- **Don't ask for medical details or Social Security numbers** in an email survey; the editor warns you if it spots them.
- Turning **Active** off makes links show "survey closed". Deleting does the same and hides its answers.
- Each agent sees the answers from **their own** clients.` } },
  { id: 'templates-sequences', cat: 'comms', nav: ['sequences'],
    title: { es: 'Secuencias (correos que salen solos)', en: 'Sequences (emails that send themselves)' },
    body: {
      es: `Una serie de correos que salen automáticamente a lo largo de los días (Día 0 bienvenida, Día 3 consejos, Día 7…). Salen de tu Gmail u Outlook conectado.

### Cómo
1. **"Nueva secuencia"**: nombre, descripción, marca **Activa** → Crear.
2. Pestaña **📩 Pasos** → **"Agregar paso"**: **Día** (desfase), **Asunto**, **Cuerpo** y **Hora**. Reordena con ↑ / ↓ → **Guardar pasos**.
3. Pestaña **👥 Inscritos** → **"Inscribir contacto"**: un cliente del CRM o un correo suelto.
4. Por inscripción puedes **⏸️ Pausar / ▶️ Reactivar / ✖️ Detener**.

**Consejos:**
- Variables: {{firstName}}, {{lastName}}, {{email}}, {{agentName}}.
- Un mismo cliente puede estar en varias secuencias.
- El servidor revisa cada 5 minutos.`,
      en: `A series of emails that go out automatically over days (Day 0 welcome, Day 3 tips, Day 7…). They send from your connected Gmail or Outlook.

### How
1. **"New sequence"**: name, description, tick **Active** → Create.
2. **📩 Steps** tab → **"Add step"**: **Day** (offset), **Subject**, **Body** and **Time**. Reorder with ↑ / ↓ → **Save steps**.
3. **👥 Enrolled** tab → **"Enroll contact"**: a CRM client or a loose email address.
4. Per enrollment you can **⏸️ Pause / ▶️ Resume / ✖️ Stop**.

**Tips:**
- Variables: {{firstName}}, {{lastName}}, {{email}}, {{agentName}}.
- The same client can be in several sequences.
- The server checks every 5 minutes.` } },
  { id: 'sent-emails', cat: 'comms', nav: ['sent_emails'],
    title: { es: 'Emails Enviados (y recibidos)', en: 'Sent Emails (and received)' },
    body: {
      es: `El historial de todo el correo de tu agencia.

### Cómo
1. Menú → **Emails Enviados**.
2. Elige **📤 Enviados** o **📧 Recibidos**.
3. Si tienes equipo, elige el alcance: **Solo míos / Mi equipo / Toda la agencia** (según tu rol).
4. Ajusta el **rango de fechas** (por defecto, los últimos 7 días) y busca por asunto, correo o cliente.
5. Clic en una fila para ir a la pestaña Emails de ese cliente.

**Consejos:**
- Un correo que no está asociado a ningún cliente no se puede abrir desde aquí.
- Cambiar la fecha o el alcance te devuelve a la página 1.`,
      en: `The history of all your agency's email.

### How
1. Menu → **Sent Emails**.
2. Choose **📤 Sent** or **📧 Received**.
3. With a team, choose the scope: **Mine only / My team / Whole agency** (depending on your role).
4. Set the **date range** (last 7 days by default) and search by subject, address or client.
5. Click a row to jump to that client's Emails tab.

**Tips:**
- An email not tied to a client can't be opened from here.
- Changing the date or scope resets you to page 1.` } },
  { id: 'scheduled-sends', cat: 'comms', nav: ['scheduled_sends'],
    title: { es: 'Correos Programados (la cola de envíos)', en: 'Scheduled Emails (the outbound queue)' },
    body: {
      es: `Lo que está esperando salir, y lo que ya salió. Sirve sobre todo para **ver antes** y **cancelar a tiempo**.

### Cómo
1. Filtra por **Desde / Hasta** o busca por cliente o correo.
2. **"Cancelar este envío"** en una fila suelta, o **"Seleccionar todo"** para cancelar varias de golpe.
3. Abajo tienes el bloque de **repetidos**: mismo cliente, mismo tipo de saludo, mismo día.

**Consejos:**
- Un saludo mandado con "Enviar por Deskte" en el momento **NO pasa por esta cola** (se cuenta igual en el bloque de repetidos).
- Cancelar solo lo puede hacer quien programó el envío, o el dueño de la agencia. Si no eres dueño, ves únicamente los tuyos.`,
      en: `What's waiting to go out, and what already went. It exists mainly so you can **see it first** and **cancel in time**.

### How
1. Filter by **From / To** or search by client or address.
2. **"Cancel this send"** on a single row, or **"Select all"** to cancel several at once.
3. At the bottom is the **duplicates** block: same client, same greeting type, same day.

**Tips:**
- A greeting sent with "Send through Deskte" on the spot **does NOT pass through this queue** (it still counts in the duplicates block).
- Only whoever scheduled the send, or the agency owner, can cancel it. If you're not the owner, you only see your own.` } },
  { id: 'suppressions', cat: 'comms', nav: ['email_suppressions', 'sms_suppressions'],
    title: { es: 'No-Enviar y No-Textear (la ley)', en: "Do-not-email & do-not-text (the law)" },
    body: {
      es: `Dos listas de bloqueo. Deskte **salta solo** a quien esté en ellas, así no incumples CAN-SPAM ni TCPA.

### 🚫 No-Enviar (correo)
- Entra quien se dio de baja, quien rebotó y quien te marcó como spam.
- Busca por email; cada fila trae origen, motivo y fecha.
- El dueño o admin puede **agregar** a mano y, con 🗑️, **re-suscribir** (queda auditado).
- Agentes y asistentes la ven en solo lectura.

### 🚫 No-Textear (SMS)
- Entra quien respondió **STOP** o **BAJA**, y lo que agregues a mano (número de 10 dígitos, cliente opcional y motivo).
- La columna **Origen** te dice cuál de las dos cosas fue.
- **Agregar** puede cualquiera. **Quitar** solo el dueño de la agencia o el agente solo: sacar a alguien de esta lista vuelve a permitirle textos a quien pidió no recibirlos.

**Consejo:** las dos listas son de la agencia entera, no de cada agente.`,
      en: `Two block lists. Deskte **skips** anyone on them automatically, so you don't breach CAN-SPAM or TCPA.

### 🚫 Do-not-email
- People land here by unsubscribing, bouncing or marking you as spam.
- Search by email; each row carries source, reason and date.
- The owner or an admin can **add** manually and, with 🗑️, **re-subscribe** (it's audited).
- Agents and assistants see it read-only.

### 🚫 Do-not-text (SMS)
- People land here by replying **STOP** or **BAJA**, plus whatever you add by hand (10-digit number, optional client and reason).
- The **Source** column tells you which of the two it was.
- **Adding** is open to anyone. **Removing** is the agency owner or the solo agent only: taking someone off this list re-allows texts to a person who asked not to get them.

**Tip:** both lists belong to the whole agency, not to each agent.` } },
  { id: 'whatsapp', cat: 'comms', nav: ['whatsapp_integration'],
    title: { es: 'Integración WhatsApp: paso a paso (con Google Chrome)', en: 'WhatsApp integration: step by step (with Google Chrome)' },
    body: {
      es: `Una extensión de Google Chrome (o Microsoft Edge) que guarda tus conversaciones de WhatsApp Web en la ficha del cliente, **sola**. Cuesta $0: no necesitas Twilio. Se configura **una vez** (unos 5 minutos) en 3 partes: sacar tu token en Deskte, instalar la extensión en Chrome y abrir WhatsApp Web.

**Los números de cada imagen son los mismos que los de cada paso.** Las capturas salen de un Deskte de práctica con clientes inventados.

### Parte 1 · En Deskte: saca tu token

### Paso 1 · Abre Integración WhatsApp
En el buscador del menú de la izquierda escribe **whatsapp** y toca **Integración WhatsApp**.
![El buscador del menú con «whatsapp» escrito y la opción Integración WhatsApp resaltada](ayuda/whatsapp-01-menu.webp)

### Paso 2 · Toca «Generar token»
El token es la llave que conecta la extensión con **tu** cuenta de Deskte.
![La pantalla Integración WhatsApp con el botón azul Generar token resaltado](ayuda/whatsapp-02-generar.webp)

### Paso 3 · Cópialo con «Copiar»
Empieza con **dkt_ext_**. **Solo se muestra una vez**: cópialo ahora y no cierres esta pantalla hasta pegarlo en el paso 7. Si lo pierdes, no pasa nada: tocas **Regenerar** y sale uno nuevo.
![El token recién generado con el botón verde Copiar resaltado](ayuda/whatsapp-03-copiar.webp)

### Parte 2 · En Google Chrome: instala la extensión

### Paso 4 · Toca «Instalar extensión (Chrome Web Store)»
Está en la misma pantalla, más abajo. Se abre la página de la extensión **Deskte — Guardar WhatsApp** en la tienda de Chrome.
![El botón Instalar extensión (Chrome Web Store) resaltado](ayuda/whatsapp-04-instalar.webp)

### Paso 5 · «Agregar a Chrome» → «Agregar extensión»
En la tienda toca **Agregar a Chrome** (arriba a la derecha). Chrome pregunta si quieres agregarla: toca **Agregar extensión**. El permiso que pide es solo para **web.whatsapp.com** y **api.deskte.com**.
- En **Microsoft Edge** es igual: el botón dice **Obtener** y luego **Agregar extensión**.
![El aviso de Chrome «¿Quieres agregar Deskte — Guardar WhatsApp?» con el botón Agregar extensión resaltado](ayuda/whatsapp-05-agregar-a-chrome.webp)

### Paso 6 · Fíjala en la barra con la chinche 📌
Toca el ícono de **rompecabezas** 🧩 (arriba a la derecha de Chrome) y luego la **chinche** al lado de **Deskte — Guardar WhatsApp**. Así el ícono azul **D** queda siempre a la vista.
![El menú Extensiones de Chrome con la chinche de Deskte resaltada](ayuda/whatsapp-06-fijar.webp)

### Paso 7 · Pega el token y toca «Guardar»
Toca el ícono azul **D** de Deskte. En **Token de Deskte** pega el token (clic derecho → Pegar, o Ctrl+V). **Dirección del servidor** déjala como está (https://api.deskte.com) y deja marcado **Guardado automático**. Toca **Guardar**: debe salir un aviso verde que empieza con «Guardado».
![La ventanita de la extensión con el token pegado y el botón Guardar resaltado](ayuda/whatsapp-07-pegar-token.webp)

### Parte 3 · Úsalo todos los días

### Paso 8 · Abre WhatsApp Web
Entra a **web.whatsapp.com** en el **mismo Chrome** y escanea el código QR con tu teléfono (WhatsApp → ⋮ o Configuración → **Dispositivos vinculados** → **Vincular un dispositivo**). Solo se hace la primera vez.

### Paso 9 · Abre el chat del cliente: se guarda solo
A los pocos segundos, abajo a la derecha, aparece unos segundos el aviso **«✓ Guardado en Deskte»** con el número de mensajes. Si entra un mensaje nuevo con el chat abierto, se vuelve a guardar sin duplicar nada. Para guardar mensajes viejos, **desplázate hacia arriba** en el chat para que carguen.
- La extensión **nunca abre chats por su cuenta** (para no arriesgar tu WhatsApp): solo guarda los que **tú** abres.
![WhatsApp Web con el aviso «✓ Guardado en Deskte (5 mensajes)» resaltado abajo a la derecha](ayuda/whatsapp-08-guardado.webp)

### Paso 10 · Si no se guarda solo: revisa el nombre, o guárdalo con el teléfono
Deskte reconoce al cliente por el **nombre del contacto en tu teléfono**. Ese nombre tiene que ser **el mismo nombre y apellido que tiene el cliente en Deskte**. Por ejemplo, **Rosa Jimenez**, no «Rosi 🌸» ni solo «Rosa». Los acentos, los emojis y las mayúsculas no importan.
![El chat de un contacto guardado como «Rosi 🌸», que no coincide con «Rosa Jimenez» en Deskte](ayuda/whatsapp-09-no-se-guarda.webp)
- **Lo mejor:** cambia el nombre del contacto en tu teléfono para que diga igual que en Deskte. De ahí en adelante se guarda solo.
- **Si no coincide, guárdalo con el teléfono:** toca **📥 Guardar en Deskte** (abajo a la derecha), escribe el **teléfono del cliente tal como está en su ficha de Deskte** y toca **Guardar**.
![La ventana Guardar en Deskte con el teléfono del cliente escrito](ayuda/whatsapp-10-telefono.webp)
- Si dice **«No se encontró un cliente con ese teléfono»**, revisa que el número sea el mismo que tiene la ficha en Deskte.

### Paso 11 · Mira la conversación en la ficha
En Deskte abre el cliente → pestaña **Mensajes** → **WhatsApp**. Ahí queda la conversación, cifrada, con fecha y hora.
![La ficha de Pedro Alvarez, pestaña Mensajes, con la conversación de WhatsApp guardada](ayuda/whatsapp-11-ficha.webp)

### Qué significan los avisos de abajo a la derecha
- **✓ Guardado en Deskte**: todo bien.
- **No se guarda solo**: el nombre del contacto no es igual al del cliente en Deskte (paso 10).
- **No se encontró un cliente con ese teléfono**: el número no es el mismo que tiene la ficha en Deskte.
- **Falta el token**: repite el paso 7.
- **Token inválido o vencido**: el token dura **90 días**. Ve a Integración WhatsApp → **Regenerar**, cópialo y repite el paso 7.
- **Los grupos** no se guardan: es a propósito.
- **No aparece el botón azul abajo a la derecha**: recarga la página de WhatsApp Web (F5) y espera unos segundos.

### Seguridad
- El token se guarda solo en tu navegador. Deskte no lo guarda en texto: si lo pierdes, generas otro y el anterior deja de servir.
- **Revocar** (en Integración WhatsApp) corta el acceso al instante, por ejemplo si cambias de computadora.
- Las conversaciones se guardan **cifradas** dentro de Deskte.

**⚠️ Importante:** **Meta no firma acuerdos HIPAA**. Usa WhatsApp solo para coordinar (citas, recordatorios, documentos) y **nunca** para diagnósticos, números de seguro social ni datos médicos.`,
      en: `A Google Chrome (or Microsoft Edge) extension that saves your WhatsApp Web conversations onto the client profile **on its own**. It costs $0: no Twilio needed. You set it up **once** (about 5 minutes) in 3 parts: get your token in Deskte, install the extension in Chrome, and open WhatsApp Web.

**The number on each picture matches the step number.** The screenshots come from a practice Deskte with invented clients. The extension itself is in Spanish, so its buttons are quoted here exactly as you will see them.

### Part 1 · In Deskte: get your token

### Step 1 · Open WhatsApp Integration
In the search box at the top of the left menu type **whatsapp** and tap **WhatsApp Integration**.
![The menu search box with «whatsapp» typed and WhatsApp Integration highlighted](ayuda/whatsapp-01-menu-en.webp)

### Step 2 · Tap «Generate token»
The token is the key that connects the extension to **your** Deskte account.
![The WhatsApp Integration screen with the blue Generate token button highlighted](ayuda/whatsapp-02-generar-en.webp)

### Step 3 · Copy it with «Copy»
It starts with **dkt_ext_**. **It is shown only once**: copy it now and keep this screen open until you paste it in step 7. If you lose it, no problem: tap **Regenerate** and you get a new one.
![The freshly generated token with the green Copy button highlighted](ayuda/whatsapp-03-copiar-en.webp)

### Part 2 · In Google Chrome: install the extension

### Step 4 · Tap «Install extension (Chrome Web Store)»
It is on the same screen, further down. The page of the **Deskte — Guardar WhatsApp** extension opens in the Chrome Web Store.
![The Install extension (Chrome Web Store) button highlighted](ayuda/whatsapp-04-instalar-en.webp)

### Step 5 · «Add to Chrome» → «Add extension»
In the store tap **Add to Chrome** (top right). Chrome asks whether to add it: tap **Add extension**. The only access it asks for is **web.whatsapp.com** and **api.deskte.com**.
- In **Microsoft Edge** it is the same: the button says **Get**, then **Add extension**.
![Chrome's «Add Deskte — Guardar WhatsApp?» prompt with Add extension highlighted](ayuda/whatsapp-05-agregar-a-chrome-en.webp)

### Step 6 · Pin it to the toolbar 📌
Tap the **puzzle** icon 🧩 (top right of Chrome), then the **pin** next to **Deskte — Guardar WhatsApp**. That keeps the blue **D** icon always visible.
![Chrome's Extensions menu with the Deskte pin highlighted](ayuda/whatsapp-06-fijar-en.webp)

### Step 7 · Paste the token and tap «Guardar»
Tap the blue **D** icon. Paste the token into **Token de Deskte** (right click → Paste, or Ctrl+V). Leave **Dirección del servidor** as it is (https://api.deskte.com) and keep **Guardado automático** (auto-save) checked. Tap **Guardar** (Save): a green message starting with «Guardado» should appear.
![The extension window with the token pasted and the Guardar button highlighted](ayuda/whatsapp-07-pegar-token-en.webp)

### Part 3 · Every day

### Step 8 · Open WhatsApp Web
Go to **web.whatsapp.com** in the **same Chrome** and scan the QR code with your phone (WhatsApp → ⋮ or Settings → **Linked devices** → **Link a device**). First time only.

### Step 9 · Open the client's chat: it saves on its own
A few seconds later, at the bottom right, the message **«✓ Guardado en Deskte»** (saved to Deskte) shows for a moment with the number of messages. If a new message arrives while the chat is open, it saves again without duplicating anything. To save older messages, **scroll up** in the chat so they load.
- The extension **never opens chats on its own** (so your WhatsApp account is never at risk): it only saves the ones **you** open.
![WhatsApp Web with the «✓ Guardado en Deskte (5 mensajes)» message highlighted at the bottom right](ayuda/whatsapp-08-guardado-en.webp)

### Step 10 · If it doesn't save on its own: check the name, or save it by phone number
Deskte recognizes the client by the **contact name on your phone**. That name must be **the same first and last name the client has in Deskte**. For example **Rosa Jimenez**, not «Rosi 🌸» or just «Rosa». Accents, emojis and capital letters don't matter.
![The chat of a contact saved as «Rosi 🌸», which does not match «Rosa Jimenez» in Deskte](ayuda/whatsapp-09-no-se-guarda-en.webp)
- **Best option:** rename the contact on your phone so it matches Deskte. From then on it saves on its own.
- **If it doesn't match, save it by phone number:** tap **📥 Guardar en Deskte** (bottom right), type the **client's phone number exactly as it appears in their Deskte profile** and tap **Guardar**.
![The Guardar en Deskte window with the client's phone number typed in](ayuda/whatsapp-10-telefono-en.webp)
- If it says **«No se encontró un cliente con ese teléfono»** (no client found with that phone), check that the number is the same one on the Deskte profile.

### Step 11 · See the conversation in the client profile
In Deskte open the client → **Messages** tab → **WhatsApp**. The conversation is there, encrypted, with date and time.
![Pedro Alvarez's profile, Messages tab, with the saved WhatsApp conversation](ayuda/whatsapp-11-ficha-en.webp)

### What the messages at the bottom right mean
- **✓ Guardado en Deskte**: all good, it was saved.
- **It doesn't save on its own**: the contact name doesn't match the client's name in Deskte (step 10).
- **No se encontró un cliente con ese teléfono**: the number doesn't match the one on the Deskte profile.
- **Falta el token** (token missing): repeat step 7.
- **Token inválido o vencido** (invalid or expired): tokens last **90 days**. Go to WhatsApp Integration → **Regenerate**, copy it and repeat step 7.
- **Groups** are never saved: on purpose.
- **The blue button doesn't show at the bottom right**: reload WhatsApp Web (F5) and wait a few seconds.

### Security
- The token is stored only in your browser. Deskte never stores it as plain text: if you lose it, generate a new one and the old one stops working.
- **Revoke** (in WhatsApp Integration) cuts access immediately, for example when you change computers.
- Conversations are stored **encrypted** inside Deskte.

**⚠️ Important:** **Meta does not sign HIPAA agreements**. Use WhatsApp only for logistics (appointments, reminders, documents) and **never** for diagnoses, Social Security numbers or medical data.` } },
  { id: 'team-chat', cat: 'comms', nav: ['team_chat'],
    title: { es: 'Chat de Equipo', en: 'Team Chat' },
    body: {
      es: `El chat interno de la agencia: canales y mensajes directos, sin usar el WhatsApp personal.

### Cómo
1. Usa el canal **general**, o **Crear canal** para uno por tema (#ventas, #renovaciones).
2. Clic en un compañero para un mensaje directo.
3. Escribe, adjunta archivos, reacciona con emoji y responde en hilo.
4. **"Buscar mensajes…"** encuentra lo viejo; la campanita activa los avisos del navegador.

### Avisos automáticos del CRM
Se pueden encender para que el chat te avise de: cliente nuevo, cliente que pasa a activo, cotización aceptada o rechazada, cambio de estatus de una póliza, cita agendada y documento subido al portal.

**Consejos:**
- Los mensajes van **cifrados** y llegan en vivo.
- Los canales los ve todo el mundo, asistentes incluidos. Los mensajes directos, solo las dos personas.`,
      en: `The agency's internal chat: channels and direct messages, without using personal WhatsApp.

### How
1. Use the **general** channel, or **Create channel** for one per topic (#sales, #renewals).
2. Click a teammate for a direct message.
3. Type, attach files, react with emoji and reply in a thread.
4. **"Search messages…"** finds old ones; the bell turns on browser notifications.

### Automatic CRM notices
You can switch on chat notices for: new client, client turning active, quote accepted or declined, policy status change, appointment booked and document uploaded to the portal.

**Tips:**
- Messages are **encrypted** and arrive live.
- Channels are visible to everyone, assistants included. Direct messages, only to the two people.` } },

  // ── HealthSherpa ──────────────────────────────────────────────────────────
  { id: 'healthsherpa-overview', cat: 'healthsherpa', nav: ['healthsherpa_integration'],
    title: { es: 'Conectar tu cuenta de HealthSherpa', en: 'Connecting your HealthSherpa account' },
    body: {
      es: `Conectas **tu propia cuenta** de HealthSherpa una vez, y a partir de ahí Deskte se entera **solo** cuando una póliza tuya se cancela, se termina o se hace efectiva. No hay que subir archivos ni revisar nada a mano.

### Por qué importa
Hoy, si un cliente deja de pagar y la aseguradora lo cancela, **Deskte no se entera**: la póliza sigue viéndose activa durante meses. Te enteras cuando el cliente llama molesto o cuando ves el descuento en tu comisión. Con la conexión, el CRM lo sabe el mismo día y te deja la tarea de llamarlo.

### Qué te da la conexión, en concreto
Son tres cosas, y cada una tiene su pantalla en el menú.

**1. Leer el estado real de tus pólizas** — menú → **HealthSherpa** → *Mirar ahora*.
De cada solicitud tuya en el Mercado, HealthSherpa te devuelve: si está **activa, cancelada o terminada**, el **estado de pago**, la **fecha de efecto**, hasta cuándo está **pagada**, lo que **debe** (al día y vencido) y cuándo empezó el **periodo de gracia**. Eso último es lo que más vale: el periodo de gracia es la ventana para llamar al cliente **antes** de que la cancelación sea definitiva.

**2. Emparejar esas pólizas con las tuyas** — menú → **Emparejar HealthSherpa**.
HealthSherpa no manda nombres, solo números de confirmación. Esta pantalla te deja decir qué póliza de HealthSherpa es cuál de tu CRM: copias el número, lo buscas en tu portal para ver de quién es, y lo marcas aquí. Lo que coincide por número de póliza se empareja solo.

**3. Inscribir desde Deskte** — botón **HealthSherpa** en la ficha del cliente, o menú → **Inscribir en HealthSherpa**.
Deskte genera el enlace de inscripción **con la ficha del cliente ya puesta**, y esa póliza **nace emparejada**: no hay que emparejarla nunca. Es la vía que HealthSherpa documenta para un CRM, y la razón por la que vale la pena inscribir desde aquí y no desde su portal.

**4. Avisarte sola cuando algo cambia** — sin que entres a mirar.
Cada 6 horas Deskte le pregunta a HealthSherpa por tus pólizas y compara. Si algo cambió, **te deja la alerta en la campanita**:
- **Falta de pago** — con lo que debe, hasta cuándo está pagada y desde cuándo corre el periodo de gracia. **Este es el aviso que vale la pena**: la ventana para llamarlo antes de que la cancelación sea definitiva.
- **Póliza cancelada o terminada** en el Mercado — y Deskte le cambia el estado a la ficha.

Y si inscribiste por el enlace de Deskte y **todavía no habías creado la ficha de la póliza**, te la crea sola, con el cliente correcto, y te deja una tarea para completar lo que falta.

### Qué te da HealthSherpa y qué NO
Esto es lo que vale la pena tener claro de entrada, para no esperar lo que no va a llegar.

**Lo que SÍ manda, por cada póliza:**
el código de la aseguradora · la fecha de efecto · el estado (activa / cancelada / terminada) · el estado de pago · hasta cuándo está pagada · lo que debe (al día y vencido) · cuándo empezó el periodo de gracia.

**Lo que NO manda, y no va a mandar:**
- **El nombre del plan.** No viene.
- **La prima.** No viene. Por eso una ficha creada automáticamente nace con prima en **$0** y una tarea para que la completes.
- **El subsidio.** No viene.
- **La comisión.** Eso es tuyo, ellos no lo saben.
- **Los documentos pendientes (DMI).** No llegan por esta vía; los sigues viendo en su portal.
- **Avisos automáticos.** HealthSherpa **no manda ninguna notificación** — no existe en su sistema. Todas las alertas que ves salen de que Deskte va y les pregunta.

**Y una advertencia honesta:** su API de estado está en **alpha**. Ellos mismos avisan de que no todas las solicitudes traen estado todavía y de que lo que llega puede venir a medias. Que una póliza salga sin datos no es un fallo de Deskte.

### Las reglas de seguridad que Deskte respeta
Para que nunca te deshaga trabajo tuyo:
- **Un dato suyo nunca pisa uno tuyo en silencio.** Un campo vacío de ellos significa «no sé», nunca «bórralo».
- **Lo que no se entiende, no se toca.** Si HealthSherpa manda una palabra de estado que Deskte no reconoce, la póliza se queda como está. Nunca se cancela una póliza por no entender una palabra.
- **Una póliza que tú cancelaste a mano no se reabre**, aunque ellos todavía la vean activa.
- **La fecha de efecto no se cambia nunca.** Es tuya; la usas para comisiones y renovaciones.
- **La prima, el plan y la comisión no se tocan jamás.** Son tuyos.

### Los 7 pasos, en orden
**El orden importa**: el paso 6 solo funciona DESPUÉS de que te aprueben. Si lo haces antes, parece que funcionó y el permiso no se concede.

### Paso 1 — Sign up, NO Sign in
La primera vez **no tienes cuenta de desarrollador todavía**, así que no busques "Sign in": hay que registrarse en **one.healthsherpa.com**. Es una cuenta distinta de la que usas para vender.
**Usa el mismo correo** que tu cuenta de agente de HealthSherpa. Con un correo distinto, ellos no encuentran tu cuenta y la aprobación se atasca.

### Paso 2 — Entra y ve a API Access
Ya con la cuenta creada, entras y abres la pestaña **API Access**. Arriba verás tu correo y una hora de "SESSION EXPIRES": la sesión caduca sola cada pocas horas. Si te saca, vuelves a entrar y sigues donde ibas.

### Paso 3 — Genera tu llave
Menú lateral **API key** → botón azul **"Rotate API key"**.

![El botón «Rotate API key» en el portal de HealthSherpa](ayuda/healthsherpa-llave.webp)

**La enseñan UNA sola vez:** cópiala en ese momento. Si cierras sin copiarla no hay forma de recuperarla, habría que generar otra. Y es como tu contraseña: no la mandes por WhatsApp ni por correo. El único sitio donde va es la pantalla de HealthSherpa dentro de Deskte.

### Paso 4 — Enlaza tu cuenta de Marketplace
Sección **"Link Your HealthSherpa Marketplace"**. Te pide entrar con tu cuenta **de agente** (la de vender, no la de desarrollador).

![La sección «Link Your HealthSherpa Marketplace» con la etiqueta LINKED y el botón «Re-link account»](ayuda/healthsherpa-marketplace.webp)

Tiene que quedar en **LINKED**. Si dice NOT LINKED, el paso 5 no se puede enviar: este va primero.

### Paso 5 — Pide el acceso (el que todos se saltan)
Sección **"Request on-exchange enrollment and policy status access"**.

![El formulario «Request on-exchange enrollment and policy status access», con el recuadro vacío y el botón azul «Request access»](ayuda/healthsherpa-solicitud.webp)

Hay un recuadro que dice "Why do you need access?" y un botón **"Request access"**. **Si no escribes nada y no le das al botón, no hay ninguna solicitud y nunca te van a aprobar.** No pasa solo, y el portal no avisa de que falta.
Explica en inglés que eres agente con licencia, que quieres leer el estado de TUS pólizas para tu CRM, y que cada agente usa su propia llave. **Lo revisa una persona**, no es automático, y pueden pedirte firmar un **BAA** (el acuerdo de confidencialidad médica).

### Ejemplo de lo que puedes escribir
El recuadro es en inglés, porque lo lee su equipo. Puedes copiar esto y cambiar lo que no cuadre con tu caso — sobre todo si tu agencia se llama de otra forma.

*I am a licensed health insurance agent in Florida, enrolling consumers in on-exchange ACA coverage through HealthSherpa. This request is for my own agent account and my own book of business.*

*What I need: read-only policy status for my own clients. Today I have to log into HealthSherpa and check policies one by one to find out when one has been cancelled, terminated, or effectuated. I want that status to reach me automatically so I can contact the client in time — for example before a termination for non-payment becomes final.*

*Scope: GET /v1/policy-status/applications and /v1/policy-status/applications/{confirmation_id}, limited to the book of business linked to my own account. No access to other agents' data.*

*Where the data goes: my agency uses a CRM called Deskte to track our clients and policies. The policy status I read updates the policy record there and creates a follow-up task for me. The data is not resold, redistributed, or shared outside my agency.*

*Keys: each agent at my agency connects their own HealthSherpa account with their own API key. There is no shared or platform-level key. Keys are stored server-side only, encrypted at rest (AES-256-GCM), never in browser code or source control. PHI is encrypted at rest and scrubbed from logs. I am prepared to sign a BAA.*

**Habla tú, como la agente que eres.** No lo escribas como si lo pidiera una empresa de software: sus términos prohíben una llave de plataforma compartida, y una solicitud que suene a eso se complica.

### Aquí se espera
No sigas al paso 6 hasta que HealthSherpa confirme que tu acceso está aprobado. El permiso que falta solo se puede conceder después.

### Paso 6 — Vuelve a enlazar
Sección "Link Your HealthSherpa Marketplace" → botón **"Re-link account"**.
El enlace del paso 4 se hizo cuando el permiso de estado de pólizas todavía no existía para tu cuenta, así que **no lo incluye**, y no se añade solo con el tiempo. Es el paso invisible: sin él, todo lo demás puede estar bien y Deskte sigue sin leer nada.

### Paso 7 — Pega la llave en Deskte
Menú → **HealthSherpa**.

![La pantalla de HealthSherpa dentro de Deskte, con el estado y el botón «Comprobar ahora»](ayuda/healthsherpa-deskte.webp)

Escribe el correo de tu cuenta, pega la llave, marca la casilla de los términos y **Guardar**. Después, **"Comprobar ahora"**. Si todo fue bien, el cartel se pone **Conectado**.

### "Esperando aprobación" NO es un error
Al conectar por primera vez es normal. Tu llave quedó guardada y HealthSherpa la reconoce; lo que falta es que te den acceso al estado de pólizas. **No vuelvas a pegar la llave**, no sirve de nada. Revisa que el paso 5 esté enviado de verdad. Cuando te aprueben, haz el paso 6 y dale a **"Comprobar ahora"**.

### "Estamos corrigiéndolo"
HealthSherpa nos deja entrar con tu llave, pero rechaza nuestra petición: el fallo es de Deskte, no tuyo. **No generes otra llave** — la tuya está bien. Lo arreglamos nosotros.

### "Se perdió la conexión"
Esto solo sale si antes funcionaba de verdad y dejó de funcionar. Suele ser porque generaste una llave nueva en HealthSherpa: pega la nueva aquí. Tus clientes y pólizas **no se tocan** en ningún caso.

**Cosas a saber:**
- Es **una llave por agente**, no por agencia: cada quien conecta la suya desde su perfil y nadie ve la del otro.
- El acceso de HealthSherpa es **gratis**.
- Tu llave se guarda **cifrada** y no se vuelve a mostrar, ni a ti.
- **Los documentos pendientes (DMI) no llegan por aquí** — HealthSherpa no los manda por esta vía.
- **No se puede inscribir desde Deskte**: la aplicación ACA está regulada por CMS y tiene que correr en una plataforma aprobada.
- Los asistentes no ven esta pantalla (no tienen cuenta propia en HealthSherpa).`,
      en: `You connect **your own** HealthSherpa account once, and from then on Deskte finds out **on its own** when one of your policies is cancelled, terminated or made effective. No files to upload, nothing to check by hand.

### Why it matters
Today, if a client stops paying and the carrier cancels them, **Deskte doesn't find out**: the policy keeps showing as active for months. You learn about it when the client calls upset, or when you see the clawback. With the connection, the CRM knows the same day and leaves you the task to call them.

### What the connection gives you, concretely
Three things, each with its own screen in the menu.

**1. Read your policies' real status** — menu → **HealthSherpa** → *Check now*.
For each of your Marketplace applications, HealthSherpa returns: whether it's **active, cancelled or terminated**, the **payment status**, the **effective date**, how far it's **paid through**, what's **owed** (current and past due), and when the **grace period** started. That last one is worth the most: the grace period is your window to call the client **before** the cancellation is final.

**2. Match those policies to yours** — menu → **Match HealthSherpa**.
HealthSherpa sends no names, only confirmation numbers. This screen lets you say which HealthSherpa policy is which of yours: copy the number, look it up in your portal to see whose it is, and mark it here. Anything that matches on policy number is matched automatically.

**3. Enroll from Deskte** — the **HealthSherpa** button on the client profile, or menu → **Enroll in HealthSherpa**.
Deskte generates the enrollment link **with the client's record already filled in**, and that policy is **born matched**: it never has to be matched. It's the path HealthSherpa documents for a CRM, and the reason it's worth enrolling from here rather than from their portal.

**4. Alert you on its own when something changes** — without you going to look.
Every 6 hours Deskte asks HealthSherpa about your policies and compares. If something changed, **the alert lands in your bell**:
- **Missed payment** — with what's owed, how far it's paid through, and when the grace period started. **This is the alert that earns its keep**: it's the window to call them before the cancellation is final.
- **Policy cancelled or terminated** on the Marketplace — and Deskte updates the record's status.

And if you enrolled through a Deskte link and **hadn't created the policy record yet**, it creates it for you, on the right client, with a task to fill in the rest.

### What HealthSherpa gives you and what it does NOT
Worth knowing up front, so you don't wait for something that isn't coming.

**What it DOES send, per policy:**
the issuer's code · the effective date · the status (active / cancelled / terminated) · the payment status · how far it's paid through · what's owed (current and past due) · when the grace period started.

**What it does NOT send, and won't:**
- **The plan name.** Not included.
- **The premium.** Not included. That's why an auto-created record starts at **$0** with a task to complete it.
- **The subsidy.** Not included.
- **The commission.** That's yours; they don't know it.
- **Pending documents (DMI).** They don't come this way; you still see those in their portal.
- **Automatic notifications.** HealthSherpa **sends no notifications at all** — it doesn't exist in their system. Every alert you see comes from Deskte going and asking.

**And an honest warning:** their policy status API is in **alpha**. They warn that not every application carries status yet and that what arrives can be partial. A policy coming back with no data isn't a Deskte failure.

### The safety rules Deskte follows
So it never undoes your work:
- **Their data never overwrites yours silently.** An empty field from them means "don't know", never "delete it".
- **What isn't understood isn't touched.** If HealthSherpa sends a status word Deskte doesn't recognize, the policy stays as it is. A policy is never cancelled over an unrecognized word.
- **A policy you cancelled by hand is never reopened**, even if they still see it as active.
- **The effective date is never changed.** It's yours; you use it for commissions and renewals.
- **Premium, plan and commission are never touched.** Those are yours.

### The 7 steps, in order
**Order matters**: step 6 only works AFTER you're approved. Do it earlier and it looks like it worked, but the permission isn't granted.

### Step 1 — Sign up, NOT Sign in
The first time you **don't have a developer account yet**, so don't look for "Sign in": you have to register at **one.healthsherpa.com**. It's separate from the one you use to sell.
**Use the same email** as your HealthSherpa agent account. With a different one, they can't find your account and approval stalls.

### Step 2 — Sign in and go to API Access
Once the account exists, sign in and open the **API Access** tab. At the top you'll see your email and a "SESSION EXPIRES" time: the session expires on its own every few hours. If it logs you out, sign back in and carry on.

### Step 3 — Generate your key
Side menu **API key** → blue **"Rotate API key"** button.

![The «Rotate API key» button in the HealthSherpa portal](ayuda/healthsherpa-llave.webp)

**They show it ONCE:** copy it right then. If you close without copying there's no way to get it back, you'd have to generate another. Treat it like your password: don't send it over WhatsApp or email. The only place it goes is the HealthSherpa screen inside Deskte.

### Step 4 — Link your Marketplace account
Section **"Link Your HealthSherpa Marketplace"**. It asks you to sign in with your **agent** account (the selling one, not the developer one).

![The «Link Your HealthSherpa Marketplace» section with the LINKED badge and the «Re-link account» button](ayuda/healthsherpa-marketplace.webp)

It has to end up **LINKED**. If it says NOT LINKED, step 5 can't be submitted: this one goes first.

### Step 5 — Request access (the one everyone skips)
Section **"Request on-exchange enrollment and policy status access"**.

![The «Request on-exchange enrollment and policy status access» form, with the empty box and the blue «Request access» button](ayuda/healthsherpa-solicitud.webp)

There's a box that says "Why do you need access?" and a **"Request access"** button. **If you don't write anything and don't press the button, there is no request and nobody will ever approve you.** It doesn't happen on its own, and the portal doesn't warn you it's missing.
Explain that you're a licensed agent, that you want to read the status of YOUR policies into your CRM, and that each agent uses their own key. **A person reviews it**, it isn't automatic, and they may ask you to sign a **BAA**.

### An example of what to write
You can copy this and change whatever doesn't fit your case — especially your agency's name.

*I am a licensed health insurance agent in Florida, enrolling consumers in on-exchange ACA coverage through HealthSherpa. This request is for my own agent account and my own book of business.*

*What I need: read-only policy status for my own clients. Today I have to log into HealthSherpa and check policies one by one to find out when one has been cancelled, terminated, or effectuated. I want that status to reach me automatically so I can contact the client in time — for example before a termination for non-payment becomes final.*

*Scope: GET /v1/policy-status/applications and /v1/policy-status/applications/{confirmation_id}, limited to the book of business linked to my own account. No access to other agents' data.*

*Where the data goes: my agency uses a CRM called Deskte to track our clients and policies. The policy status I read updates the policy record there and creates a follow-up task for me. The data is not resold, redistributed, or shared outside my agency.*

*Keys: each agent at my agency connects their own HealthSherpa account with their own API key. There is no shared or platform-level key. Keys are stored server-side only, encrypted at rest (AES-256-GCM), never in browser code or source control. PHI is encrypted at rest and scrubbed from logs. I am prepared to sign a BAA.*

**Write it as yourself, the agent.** Don't write it as if a software company were asking: their terms forbid a shared platform key, and a request that sounds like one gets complicated.

### Wait here
Don't move on to step 6 until HealthSherpa confirms your access is approved. The missing permission can only be granted afterwards.

### Step 6 — Link again
Section "Link Your HealthSherpa Marketplace" → **"Re-link account"** button.
The link from step 4 was made when the policy status permission didn't exist for your account yet, so it **doesn't include it**, and it isn't added on its own over time. This is the invisible step: without it everything else can be right and Deskte still reads nothing.

### Step 7 — Paste the key into Deskte
Menu → **HealthSherpa**.

![The HealthSherpa screen inside Deskte, with the status and the «Check now» button](ayuda/healthsherpa-deskte.webp)

Enter your account email, paste the key, tick the terms box and **Save**. Then **"Check now"**. If it all went well, the badge turns to **Connected**.

### "Waiting for approval" is NOT an error
It's normal on a first connection. Your key is saved and HealthSherpa recognises it; what's missing is access to policy status. **Don't re-paste the key** — it doesn't help. Check that step 5 was actually submitted. Once you're approved, do step 6 and hit **"Check now"**.

### "We're fixing it"
HealthSherpa lets us in with your key, but rejects our request: the fault is Deskte's, not yours. **Don't generate another key** — yours is fine. We'll fix it.

### "Connection lost"
This only appears if it really was working and stopped. Usually it means you generated a new key at HealthSherpa: paste the new one here. Your clients and policies are **never** touched.

**Things to know:**
- It's **one key per agent**, not per agency: each person connects their own from their profile, and nobody sees anyone else's.
- HealthSherpa access is **free**.
- Your key is stored **encrypted** and never shown again, not even to you.
- **Pending documents (DMI) don't arrive this way** — HealthSherpa doesn't send them over this channel.
- **You can't enroll from inside Deskte**: the ACA application is regulated by CMS and has to run on an approved platform.
- Assistants don't see this screen (they have no HealthSherpa account of their own).` } },

  { id: 'healthsherpa-emparejar', cat: 'healthsherpa', nav: ['healthsherpa_emparejar'],
    title: { es: 'Emparejar tus pólizas con HealthSherpa', en: 'Matching your policies with HealthSherpa' },
    body: {
      es: `Se hace **una vez por póliza**. Después, el estado de esa póliza se actualiza solo y esta pantalla deja de pedírtelo.

### Por qué hay que hacerlo a mano
Porque **HealthSherpa no manda nombres**. Su respuesta no trae el nombre del cliente, ni fecha de nacimiento, ni prima: solo la **aseguradora**, la **fecha de efecto** y el estado. Con eso Deskte puede proponer, pero no decidir.

Y emparejar mal es caro: haría que el estado de la póliza de un cliente apareciera en la de otro, en silencio, y no se descubriría hasta llamar al cliente equivocado.

### Cómo se usa
Menú → **Emparejar HealthSherpa**. Cada póliza de HealthSherpa sale con **su número de confirmación** y un botón de copiar.

1. Copia ese número.
2. Búscalo en tu portal de HealthSherpa — ahí ves de **qué cliente** es.
3. Vuelve a Deskte y escribe ese nombre en el buscador de esa misma tarjeta.
4. Marca su póliza y pasa a la siguiente.

Cuando termines, **Guardar las elegidas**. Lo que no tengas claro, déjalo en **"Dejarla para después"**: no hay prisa y no se pierde nada.

**A veces Deskte lo adivina y te lo deja ya marcado** (cuando el número de póliza coincide exacto, o cuando hay una sola de tu cartera con esa aseguradora y esa fecha). Sale con la nota *"Deskte cree que es esta"*. Compruébalo igual antes de guardar — es una ayuda, no una certeza.

### Si te equivocaste
Abajo, en **Ya emparejadas**, cada una tiene **Deshacer**. Deshacer no toca la póliza ni el cliente: solo deja de actualizarse sola.

### Lo que esto todavía NO hace
Emparejar **no cambia el estado de tus pólizas**: solo deja atado qué es qué. Traer el estado es el paso siguiente y pasará por revisión antes de tocar nada, porque HealthSherpa avisa de que su servicio de estado está en pruebas y puede mandar datos incompletos.

**Cosas a saber:**
- Solo ves **tus** pólizas: nadie empareja contra la cartera de otro agente.
- Una póliza que ya está emparejada no se vuelve a ofrecer.
- Si una póliza de HealthSherpa no se parece a ninguna tuya, puede ser de un cliente que no está en Deskte, o estar guardada con otra aseguradora o fecha.`,
      en: `This is done **once per policy**. After that, that policy's status updates on its own and this screen stops asking.

### Why it has to be done by hand
Because **HealthSherpa sends no names**. Their response carries no client name, no date of birth, no premium: only the **carrier**, the **effective date** and the status. With that, Deskte can propose but not decide.

And a wrong match is expensive: it would show one client's policy status on another client's policy, silently, and nobody would find out until the wrong client got the call.

### How to use it
Menu → **Match HealthSherpa**. Each HealthSherpa policy appears with **its confirmation number** and a copy button.

1. Copy that number.
2. Look it up in your HealthSherpa portal — that tells you **which client** it is.
3. Come back to Deskte and type that name into the search box on the same card.
4. Tick their policy and move on to the next one.

When you're done, **Save the chosen ones**. Anything you're unsure about, leave on **"Leave it for later"**: there's no rush and nothing is lost.

**Sometimes Deskte guesses it and ticks it for you** (when the policy number matches exactly, or when only one policy of yours has that carrier and that date). It shows the note *"Deskte thinks this is the one"*. Check it anyway before saving — it's a hint, not a certainty.

### If you got one wrong
Below, under **Already matched**, each one has **Undo**. Undoing doesn't touch the policy or the client: it just stops updating on its own.

### What this does NOT do yet
Matching **doesn't change your policy status**: it only records what is what. Bringing the status in is the next step and will go through review before touching anything, because HealthSherpa warns their status service is in alpha and can send partial data.

**Things to know:**
- You only see **your** policies: nobody matches against another agent's book.
- A policy that's already matched isn't offered again.
- If a HealthSherpa policy doesn't resemble any of yours, it may belong to a client who isn't in Deskte, or be saved with a different carrier or date.` } },

  { id: 'healthsherpa-inscribir', cat: 'healthsherpa', nav: ['healthsherpa_inscribir'],
    title: { es: 'Inscribir un cliente desde Deskte', en: 'Enrolling a client from Deskte' },
    body: {
      es: `Deskte genera el enlace de inscripción de HealthSherpa para un cliente tuyo. **La póliza que salga de ahí queda emparejada sola con ese cliente** — no hay que emparejarla después.

### Por qué esto importa
Emparejar hacia atrás es casi imposible: HealthSherpa **no manda nombres** con el estado de una póliza, así que no hay nada que comparar. Con las pólizas que ya tenías vendidas hay que emparejar a mano, una por una.

En cambio, cuando la inscripción **arranca desde Deskte**, les mandamos un identificador nuestro y ellos nos lo devuelven en cada consulta. Esa póliza nace sabiendo de quién es. Es la vía que HealthSherpa documenta para un CRM.

### Paso a paso
**El número de cada imagen es el mismo que el del paso.** Las capturas salen de un Deskte de práctica con clientes inventados.

### Paso 1 · En la ficha del cliente, toca «HealthSherpa»
Está arriba, junto a **Cotizar** y **Pedir firma**. También puedes entrar por el menú → **Inscribir en HealthSherpa** y buscar al cliente por su nombre.
![La ficha de Pedro Alvarez con el botón HealthSherpa resaltado](ayuda/hs-inscribir-01-boton.webp)

### Paso 2 · El cliente ya viene elegido
Se abre **Inscribir en HealthSherpa** con ese cliente puesto, y al lado su **estado**. Si no es el cliente correcto, toca **Cambiar**.
![La pantalla Inscribir en HealthSherpa con Pedro Alvarez · FL ya elegido](ayuda/hs-inscribir-02-cliente-elegido.webp)

### Paso 3 · Elige quién completa la solicitud
- **Yo, acompañando al cliente**: la llenas tú con él (en persona o por teléfono).
- **El cliente solo, por un enlace que le mando**: Deskte te da un enlace para mandárselo.
![Las dos opciones de ¿Quién completa la solicitud?](ayuda/hs-inscribir-03-quien.webp)

### Paso 4 · Elige el año del plan
El año en curso o el próximo. En la inscripción abierta normalmente es **el próximo**.
![Los botones del Año del plan, con el próximo año resaltado](ayuda/hs-inscribir-04-anio.webp)

### Paso 5 · Revisa qué se prerrellena y qué no
Arriba sale lo que Deskte va a mandar de la ficha; abajo, en amarillo, **lo que NO se va a prerrellenar** y por qué. Si quieres que viaje, ponlo en la ficha del cliente y vuelve. Si lo dejas, el enlace funciona igual y esos campos se escriben en HealthSherpa.
- **Si el cliente no tiene estado en su ficha, no deja crear el enlace**: HealthSherpa lo exige. Ponle la dirección con el estado en la ficha y vuelve.
![El recuadro Lo que Deskte va a prerellenar y Lo que NO se va a prerellenar](ayuda/hs-inscribir-05-prerelleno.webp)

### Paso 6 · Marca el consentimiento
Confirma que el cliente es real y te dio su permiso para iniciar la inscripción. Deskte guarda la fecha. Sin esta casilla no se crea el enlace.
![La casilla de consentimiento marcada](ayuda/hs-inscribir-06-consentimiento.webp)

### Paso 7 · Toca «Crear el enlace»
![El botón verde Crear el enlace resaltado](ayuda/hs-inscribir-07-crear.webp)

### Paso 8 · Abre el enlace o cópialo
Sale en **Enlaces de este cliente**. Toca **Abrir** para seguir en HealthSherpa, o **Copiar** para mandárselo al cliente (si elegiste «el cliente solo»). Los enlaces quedan guardados aquí: si hay que reenviarlo, no hace falta crear otro.
![La lista Enlaces de este cliente con los botones Copiar y Abrir](ayuda/hs-inscribir-08-enlace.webp)

### Los asistentes (asientos) también pueden inscribir
En una agencia el asistente suele ser quien prepara la inscripción. Puede hacerlo, **si su agente le marca el permiso**: *Equipo → ese asiento → permisos → **HealthSherpa → Crear enlaces de inscripción***. Viene apagado, como todo.

**Lo que el asistente NO decide es a nombre de quién sale.** Eso lo resuelve Deskte solo, y es importante entender por qué:

> HealthSherpa acredita la solicitud a **la cuenta de agente cuya llave se usó**. O sea que elegir la llave es elegir **quién cobra**.

Por eso Deskte usa siempre **la llave del agente dueño del cliente**, nunca la de quien teclea. Así, un asistente que trabaja los libros de **dos agentes** acierta solo: la ficha ya dice de quién es cada cliente.

Antes de crear el enlace, la pantalla se lo dice con nombre y apellido: **«Se enviará a nombre de: María Fernanda»**. Si ese no es el agente que debería cobrar, lo que está mal es el agente asignado **en la ficha del cliente** — se arregla ahí, no en esta pantalla.

**Dos condiciones:**
- **Cada agente tiene que haber conectado su propia llave.** Si el dueño del cliente no la conectó, la pantalla lo dice con su nombre y no deja crear el enlace.
- **El asistente nunca ve ninguna llave.** No tiene cuenta propia en HealthSherpa, y las pantallas de conectar y de emparejar le siguen ocultas.

### El consentimiento no es un trámite de adorno
HealthSherpa lo exige por contrato: una sesión de inscripción tiene que ser de **un cliente real** que **dio su consentimiento** antes de empezar. Deskte guarda la fecha en que lo marcaste. Sin marcarlo no se crea el enlace.

### El enlace va prerrellenado con la ficha del cliente
No hace falta volver a teclear lo que Deskte ya sabe. Al enlace viajan, cuando están en la ficha:

**nombre · fecha de nacimiento · sexo · correo · teléfono · dirección · código postal · estado · tamaño del hogar · ingreso anual**

Antes de marcar el consentimiento, la pantalla te enseña **la lista exacta de lo que va a salir** de ese cliente. Son los nombres de los campos, nunca los valores: es para que puedas contestarle al cliente qué mandas de él.

### Y te avisa de lo que falta
Debajo de esa lista sale otra: **lo que NO se va a prerrellenar**, con el motivo. Hay dos motivos y la diferencia importa:

- **"no está en la ficha"** — ese dato está en blanco. Lo pones en la ficha del cliente, vuelves, y ya viaja.
- **"está en la ficha, pero HealthSherpa no lo acepta escrito así"** — este es el que nadie adivinaría. Por ejemplo un ingreso anotado como *"40k-50k"* o *"por confirmar"*: la ficha se ve completa, pero no es una cantidad y no se puede mandar.

En los dos casos el enlace funciona igual. Lo único que cambia es que esos campos los escribe el cliente.

### Por qué "si fuma" en blanco no se manda
Porque en Deskte **en blanco significa "no se ha preguntado", no "no fuma"**. Si Deskte mandara "no fuma" por defecto, estaría declarando no fumadora a media cartera sin que nadie lo dijera — y la tarifa de fumador puede doblar la prima. Lo mismo con el ingreso: adivinar un rango cambiaría el subsidio que el cliente ve en pantalla.

Es la misma regla para todo: **un dato que no está seguro, no viaja.** Prefiere que el cliente teclee un campo a que la solicitud lleve algo que nadie confirmó.

### Son DOS números, y no se guardan en el mismo sitio
Es la duda más común, así que vale la pena dejarla clara.

**1. El identificador que genera Deskte.** Es el que de verdad enlaza. Se lo mandamos a HealthSherpa al crear la sesión y ellos nos lo devuelven en cada consulta. Es un código opaco a propósito: no lleva nombre, ni fecha de nacimiento, ni nada del cliente — su contrato lo prohíbe expresamente. **Se guarda atado al cliente**, junto con el año, el flujo, la fecha del consentimiento y los dos enlaces.

**2. El número de confirmación, que genera HealthSherpa.** Nace cuando el cliente **completa** la solicitud en el formulario de ellos. Es el que ves en tu portal de HealthSherpa.
- Aparece en esta misma pantalla, al lado del enlace, en cuanto Deskte lo ve — con el cartel verde **"Ya inscrita"** y un botón de copiar. Si todavía no sale, es que esa solicitud aún no está completada (o no has abierto **Emparejar** desde entonces, que es cuando Deskte lo lee).
- Y **se guarda en la póliza**, junto con un segundo código: el de la póliza concreta.

**Por qué en la póliza y por qué dos códigos:** una sola solicitud puede llevar **varias pólizas dentro** (médica y dental, por ejemplo). Si se guardara solo el número de confirmación, esas dos pólizas compartirían enlace y cada una acabaría recibiendo el estado de la otra.

### Lo que NO hace
- **No se inscribe dentro de Deskte.** La aplicación ACA está regulada por CMS y tiene que correr en una plataforma aprobada: el enlace lleva al formulario de HealthSherpa.
- **No manda cónyuge ni dependientes** todavía: solo el titular. Esos los añade el cliente en el formulario.

**Cosas a saber:**
- El cliente **tiene que tener el estado** puesto en su ficha: HealthSherpa lo exige.
- El enlace sale en **el idioma del cliente** (el que tenga en su ficha).
- Cada enlace usa una llamada de tu cuota de HealthSherpa. Por eso se guardan y se reenvían en vez de crear otro.`,
      en: `Deskte generates the HealthSherpa enrollment link for one of your clients. **The policy that comes out of it is matched to that client on its own** — no matching afterwards.

### Why this matters
Matching backwards is nearly impossible: HealthSherpa **sends no names** with a policy's status, so there's nothing to compare. For policies you'd already sold, matching has to be done by hand, one by one.

But when the enrollment **starts from Deskte**, we send them an identifier of ours and they hand it back on every lookup. That policy is born knowing whose it is. It's the path HealthSherpa documents for a CRM.

### Step by step
**The number on each picture matches the step number.** The screenshots come from a practice Deskte with invented clients.

### Step 1 · On the client's profile, tap «HealthSherpa»
It's at the top, next to **Quote** and **Signature**. You can also go through the menu → **Enroll in HealthSherpa** and search for the client by name.
![Pedro Alvarez's profile with the HealthSherpa button highlighted](ayuda/hs-inscribir-01-boton-en.webp)

### Step 2 · The client is already selected
**Enroll in HealthSherpa** opens with that client in place, and their **state** next to the name. If it's the wrong client, tap **Change**.
![The Enroll in HealthSherpa screen with Pedro Alvarez · FL already selected](ayuda/hs-inscribir-02-cliente-elegido-en.webp)

### Step 3 · Choose who completes the application
- **Me, with the client**: you fill it in together (in person or on the phone).
- **The client alone, through a link I send**: Deskte gives you a link to send them.
![The two options under Who completes the application?](ayuda/hs-inscribir-03-quien-en.webp)

### Step 4 · Choose the plan year
This year or next. During open enrollment it's usually **next year**.
![The Plan year buttons, with next year highlighted](ayuda/hs-inscribir-04-anio-en.webp)

### Step 5 · Check what gets prefilled and what doesn't
At the top is what Deskte will send from the record; below, in yellow, **what will NOT be prefilled** and why. If you want it to travel, add it on the client's record and come back. If you leave it, the link works the same and those fields are typed in at HealthSherpa.
- **If the client has no state on their record, the link can't be created**: HealthSherpa requires it. Add the address with the state on the record and come back.
![The What Deskte will prefill and What will NOT be prefilled box](ayuda/hs-inscribir-05-prerelleno-en.webp)

### Step 6 · Tick the consent box
Confirm the client is real and gave you permission to start their enrollment. Deskte records the date. Without this box, no link is created.
![The consent box ticked](ayuda/hs-inscribir-06-consentimiento-en.webp)

### Step 7 · Tap «Create the link»
![The green Create the link button highlighted](ayuda/hs-inscribir-07-crear-en.webp)

### Step 8 · Open the link or copy it
It shows under **This client's links**. Tap **Open** to continue at HealthSherpa, or **Copy** to send it to the client (if you chose «the client alone»). Links stay saved here: to resend one, there's no need to create another.
![The This client's links list with the Copy and Open buttons](ayuda/hs-inscribir-08-enlace-en.webp)

### Assistants (seats) can enroll too
In an agency the assistant is usually the one who prepares the enrollment. They can, **if their agent grants the permission**: *Team → that seat → permissions → **HealthSherpa → Create enrollment links***. It starts off, like everything else.

**What the assistant does NOT decide is whose name it goes out under.** Deskte settles that on its own, and the reason matters:

> HealthSherpa credits the application to **the agent account whose key was used**. So choosing the key is choosing **who gets paid**.

That's why Deskte always uses **the key of the agent who owns the client**, never the key of whoever is typing. An assistant working the books of **two agents** gets it right without choosing: the record already says whose client it is.

Before creating the link, the screen says it by name: **"It will go out under: María Fernanda"**. If that isn't the agent who should be credited, what's wrong is the agent assigned **on the client's record** — fix it there, not on this screen.

**Two conditions:**
- **Each agent must have connected their own key.** If the client's owner hasn't, the screen says so by name and won't let the link be created.
- **The assistant never sees any key.** They have no HealthSherpa account of their own, and the connect and match screens stay hidden from them.

### Consent isn't a formality
HealthSherpa requires it by contract: an enrollment session must serve **a real client** who **gave consent** before it starts. Deskte records the date you ticked it. Without ticking it, no link is created.

### The link comes prefilled with the client's record
No need to retype what Deskte already knows. These travel with the link, when they're on the record:

**name · date of birth · sex · email · phone · address · ZIP code · state · household size · annual income**

Before you tick the consent box, the screen shows you **the exact list of what will leave** for that client. Field names, never values: it's so you can tell the client what you're sending about them.

### And it tells you what's missing
Below that list comes another one: **what will NOT be prefilled**, with the reason. There are two reasons and the difference matters:

- **"not on the record"** — that field is blank. Fill it in on the client's record, come back, and it travels.
- **"it's on the record, but HealthSherpa doesn't accept it written that way"** — this is the one nobody would guess. For example an income noted as *"40k-50k"* or *"TBD"*: the record looks complete, but it isn't an amount and can't be sent.

Either way the link still works. The only difference is that the client types those fields in.

### Why a blank "tobacco use" isn't sent
Because in Deskte **blank means "not asked", not "doesn't smoke"**. If Deskte sent "doesn't smoke" by default, it would be declaring half the book non-smokers without anyone saying so — and the tobacco rate can double a premium. Same with income: guessing at a range would change the subsidy the client sees on screen.

It's the same rule throughout: **a field that isn't certain doesn't travel.** Better the client types one field than the application carries something nobody confirmed.

### There are TWO numbers, and they aren't stored in the same place
It's the most common question, so it's worth spelling out.

**1. The identifier Deskte generates.** This is the one that actually links. We send it to HealthSherpa when the session is created and they hand it back on every lookup. It's deliberately opaque: no name, no date of birth, nothing about the client — their contract forbids it explicitly. **It's stored against the client**, along with the plan year, the flow, the consent date and the two links.

**2. The confirmation number, which HealthSherpa generates.** It's born when the client **completes** the application on their form. It's the one you see in your HealthSherpa portal.
- It shows on this same screen, next to the link, as soon as Deskte sees it — with a green **"Already enrolled"** tag and a copy button. If it isn't there yet, that application isn't completed (or you haven't opened **Match** since, which is when Deskte reads it).
- And it's **stored on the policy**, along with a second code: the one for the specific policy.

**Why on the policy, and why two codes:** a single application can carry **several policies inside it** (medical and dental, say). If only the confirmation number were stored, those two policies would share a link and each would end up receiving the other's status.

### What it does NOT do
- **You don't enroll inside Deskte.** The ACA application is regulated by CMS and has to run on an approved platform: the link goes to HealthSherpa's form.
- **It doesn't send spouse or dependents** yet, only the primary applicant. The client adds those on the form.

**Things to know:**
- The client **must have a state** on their record: HealthSherpa requires it.
- The link comes out in **the client's language** (whatever is on their record).
- Each link uses one call from your HealthSherpa quota. That's why they're saved and resent instead of creating another.` } },

  { id: 'healthsherpa-comprobar', cat: 'healthsherpa',
    title: { es: 'Comprobar una inscripción, paso a paso', en: 'Checking an enrollment, step by step' },
    body: {
      es: `Hiciste una inscripción por el enlace de Deskte. Esto es **qué mirar, dónde, y cuánto tarda cada cosa**.

Lo primero que conviene saber: **nada de esto es instantáneo, y es a propósito.** HealthSherpa no avisa de nada — no tiene notificaciones, no existe en su sistema. Deskte va y les pregunta cada 6 horas. Por eso hay esperas, y por eso no son un fallo.

### Paso 1 — Nada más crear el enlace (al instante)
Menú → **Inscribir en HealthSherpa** → busca ese cliente.

Abajo, en **«Enlaces de este cliente»**, tiene que estar el enlace que acabas de crear, con el año del plan, quién completa la solicitud y la fecha.

Si está, la sesión quedó guardada. Si no está, el enlace no llegó a crearse y habría salido un error en rojo.

### Paso 2 — Comprueba que el vínculo quedó puesto
Esta es **la comprobación que de verdad importa**, y la puedes hacer tú sin esperar al resto.

Menú → **HealthSherpa** → botón **«Mirar ahora»**.

Sale una tabla con tus solicitudes. Fíjate en la columna **\`external_id\`**:

- **Con un código largo** (tipo \`4cd185a9-4bf7-419f-...\`) → **esa inscripción salió de Deskte y está vinculada.** ✅
- **Con una rayita (—)** → esa solicitud no salió de Deskte. Son las de antes, o las que se inscribieron entrando directo por HealthSherpa. Esas hay que emparejarlas a mano.

**¿Cuánto tarda en aparecer ahí?** No depende de Deskte. HealthSherpa mete la solicitud en esa lista cuando la procesa de su lado: puede ser al rato, o al día siguiente. Si no sale hoy, mira mañana.

### Paso 3 — La revisión automática (hasta 6 horas después)
Cuando la solicitud ya está en esa lista, en la siguiente revisión Deskte hace cuatro cosas:

**1. Te pone el número de confirmación.**
En *Inscribir en HealthSherpa*, junto al enlace de ese cliente, aparece el cartel verde **«Ya inscrita»** con el número de confirmación de HealthSherpa y un botón de copiar. Ese es el número que buscas en su portal.

![El cartel verde «Ya inscrita» con el número de confirmación de HealthSherpa y el botón de copiar](ayuda/healthsherpa-ya-inscrita.webp)

*(El número de la imagen es inventado.)*

**2. La ficha de la póliza.**
- Si ya la habías creado tú con la misma fecha de efecto → Deskte **la adopta** y le pega el vínculo. No crea otra.
- Si no la habías creado → **te la crea**, con la aseguradora, la fecha de efecto y el estado. **Con la prima en $0**, a propósito: HealthSherpa no manda la prima.

**3. Una tarea para ti**, con aviso en la campanita: *«Completa la póliza que llegó de HealthSherpa»*.

**4. En *Emparejar HealthSherpa*** esa solicitud ya sale reconocida. No te pide emparejarla.

### También sale en la ficha del cliente
No hace falta volver a *Inscribir* para consultarlo. Abre el cliente → pestaña **Pólizas**: arriba del todo, encima de sus pólizas, aparece **«Inscripciones de HealthSherpa»** con el año, el cartel verde **«Ya inscrita»** y el número de confirmación con botón de copiar.

Los enlaces de inscripción **no salen aquí**, y es a propósito: un enlace sirve para mandarlo, y eso se hace en *Inscribir en HealthSherpa* en el momento. En la ficha, semanas después, lo que se consulta es si quedó y con qué número.

Ese bloque **solo sale si ese cliente tiene alguna inscripción hecha desde Deskte**. En los demás clientes no aparece nada, para no llenar la ficha de secciones vacías.

### Paso 4 — De ahí en adelante
Cada 6 horas Deskte vuelve a preguntar. Si algo cambia, te llega a la campanita:
- **Falta de pago** — con lo que debe, hasta cuándo está pagada y desde cuándo corre el periodo de gracia.
- **Póliza cancelada o terminada** — y le cambia el estado a la ficha.

### Si algo no aparece

| Lo que ves | Qué significa | Qué hacer |
|---|---|---|
| El enlace no está en «Enlaces de este cliente» | No se llegó a crear | Vuelve a crearlo; si da error, mándame el mensaje |
| La solicitud no sale en «Mirar ahora» | HealthSherpa todavía no la procesó | Esperar. Mira mañana |
| Sale, pero el \`external_id\` está en rayita | Esa inscripción **no** entró por Deskte | Empareja a mano en *Emparejar HealthSherpa* |
| Tiene \`external_id\` pero pasó un día y no hay cartel verde | Ahí sí hay algo raro | Avísame |

### Lo que NO deberías ver
Si ves alguna de estas, es un fallo y hay que decirlo:
- **Dos pólizas** del mismo seguro para el mismo cliente.
- Una **alerta de cancelación o de falta de pago** en una póliza recién inscrita.
- Que la ficha nueva le **cambie la fecha de efecto** a una que ya tenías.`,
      en: `You enrolled someone through the Deskte link. Here's **what to look at, where, and how long each part takes**.

First thing to know: **none of this is instant, and that's by design.** HealthSherpa sends no notifications — they don't exist in their system. Deskte goes and asks them every 6 hours. That's why there are waits, and why the waits aren't a failure.

### Step 1 — Right after creating the link (instantly)
Menu → **Enroll in HealthSherpa** → find that client.

Below, under **"This client's links"**, the link you just created should be there, with the plan year, who completes the application, and the date.

If it's there, the session was saved. If it isn't, the link was never created and you'd have seen a red error.

### Step 2 — Check the link actually attached
This is **the check that matters**, and you can do it yourself without waiting for the rest.

Menu → **HealthSherpa** → **"Check now"**.

A table of your applications appears. Look at the **\`external_id\`** column:

- **A long code** (like \`4cd185a9-4bf7-419f-...\`) → **that enrollment came from Deskte and is linked.** ✅
- **A dash (—)** → that application didn't come from Deskte. Those are the older ones, or ones enrolled by going straight into HealthSherpa. Those need matching by hand.

**How long until it shows up?** That isn't up to Deskte. HealthSherpa puts the application on that list when they process it on their side: could be shortly after, could be the next day. If it isn't there today, check tomorrow.

### Step 3 — The automatic check (within 6 hours)
Once the application is on that list, on the next pass Deskte does four things:

**1. It records the confirmation number.**
In *Enroll in HealthSherpa*, next to that client's link, a green **"Already enrolled"** tag appears with HealthSherpa's confirmation number and a copy button. That's the number you look up in their portal.

![The green "Already enrolled" tag with HealthSherpa's confirmation number and the copy button](ayuda/healthsherpa-ya-inscrita.webp)

*(The number in the image is made up.)*

**2. The policy record.**
- If you'd already created it with the same effective date → Deskte **adopts it** and attaches the link. It doesn't create a second one.
- If you hadn't → it **creates it** for you, with the issuer, effective date and status. **Premium at $0**, on purpose: HealthSherpa doesn't send the premium.

**3. A task for you**, with a bell notification: *"Complete the policy that came from HealthSherpa"*.

**4. In *Match HealthSherpa*** that application now shows as recognized. It won't ask you to match it.

### It also shows on the client's record
No need to go back to *Enroll* to look it up. Open the client → **Policies** tab: right at the top, above their policies, you'll see **"HealthSherpa enrollments"** with the plan year, the green **"Already enrolled"** tag and the confirmation number with a copy button.

The enrollment links are **not shown here**, on purpose: a link is for sending, and that happens in *Enroll in HealthSherpa* at the time. On the record, weeks later, what you check is whether it went through and with what number.

That block **only appears if that client has an enrollment started from Deskte**. On every other client nothing shows, so records don't fill up with empty sections.

### Step 4 — From then on
Every 6 hours Deskte asks again. If something changes, it lands in your bell:
- **Missed payment** — with what's owed, how far it's paid through, and when the grace period started.
- **Policy cancelled or terminated** — and the record's status is updated.

### If something doesn't show up

| What you see | What it means | What to do |
|---|---|---|
| The link isn't under "This client's links" | It was never created | Create it again; if it errors, send me the message |
| The application isn't in "Check now" | HealthSherpa hasn't processed it yet | Wait. Check tomorrow |
| It's there, but \`external_id\` is a dash | That enrollment did **not** come through Deskte | Match it by hand in *Match HealthSherpa* |
| It has an \`external_id\` but a day passed with no green tag | That one is genuinely off | Tell me |

### What you should NOT see
If you see any of these, it's a bug and it should be reported:
- **Two policies** for the same coverage on the same client.
- A **cancellation or missed-payment alert** on a freshly enrolled policy.
- The new record **changing the effective date** on one you already had.` } },

  // ── SMS y Llamadas (Teléfono) ─────────────────────────────────────────────
  { id: 'phone-overview', cat: 'phone', nav: ['phone_integration'],
    title: { es: 'Cómo funcionan el SMS y las llamadas', en: 'How SMS & calls work' },
    body: {
      es: `Deskte no es una compañía de teléfono: tú conectas **tu propia cuenta** de un proveedor (Quo, SignalWire, Twilio, RingCentral o MightyCall) y usas **tu propio número**. Le pagas el uso directo al proveedor; Deskte solo guarda tus llaves cifradas.

### Dónde se conecta
Menú → **SMS y Llamadas**. Elige un proveedor, pon tus credenciales y **Guardar conexión**. Marca los canales (SMS, llamadas o los dos) y usa **"Activar recepción"** para que lo entrante caiga dentro del CRM.

### SMS (textos)
- Salen y entran **desde Deskte**.
- Un **asistente** que no conecte su propio número **hereda el del agente** que lo supervisa (igual que el correo). No tiene que conectar nada.

### Llamadas
- El botón **"Llamar ahora"** en la ficha del cliente **abre la app del proveedor** en tu equipo (marca por un enlace **tel:**). Deskte **no** hace la llamada por ti.
- El número con el que sale depende de **con qué cuenta esté abierta la app** del proveedor en esa computadora.
- Las llamadas **se registran** de vuelta en la ficha (por webhook o por sincronización, según el proveedor).

### Elegir proveedor por canal
Si conectas varios, puedes usar **uno para SMS y otro para llamadas** con los botones "Usar para: SMS / Llamadas".

### HIPAA — cuidado con lo que escribes
- **Auto, Hogar/Inquilino y Negocio** no son datos de salud: puedes dar detalle.
- **Salud, ACA y Medicare SÍ lo son** y exigen un **BAA firmado** con tu proveedor (tiene costo mensual). Sin BAA, textea solo logística ("¿te llamo?"), nunca detalles médicos.
- **Vida** depende: si hablas de condiciones médicas, trátalo como salud.
- **WhatsApp nunca** lleva datos médicos (Meta no firma BAA).

**Nota:** tu token se guarda cifrado y el servidor no lo devuelve nunca, ni a ti. Si lo pierdes, se vuelve a conectar con uno nuevo. Los asistentes no ven esta pantalla.`,
      en: `Deskte is not a phone company: you connect **your own account** with a provider (Quo, SignalWire, Twilio, RingCentral or MightyCall) and use **your own number**. You pay usage directly to the provider; Deskte only stores your encrypted keys.

### Where to connect
Menu → **SMS & Calls**. Pick a provider, enter your credentials and **Save connection**. Tick the channels (SMS, calls or both) and use **"Turn on receiving"** so inbound traffic lands in the CRM.

### SMS (texts)
- They go out and come in **from Deskte**.
- An **assistant** who doesn't connect their own number **inherits the supervising agent's** (just like email). They don't need to connect anything.

### Calls
- The **"Call now"** button on the client profile **opens the provider's app** on your device (it dials via a **tel:** link). Deskte does **not** place the call for you.
- The number it goes out on depends on **which account the provider's app is signed into** on that computer.
- Calls **are logged** back onto the profile (via webhook or sync, depending on the provider).

### Pick a provider per channel
If you connect several, you can use **one for SMS and another for calls** with the "Use for: SMS / Calls" buttons.

### HIPAA — mind what you write
- **Auto, Home/Renters and Business** are not health data: you can be specific.
- **Health, ACA and Medicare ARE**, and they require a **signed BAA** with your provider (it costs monthly). Without a BAA, text logistics only ("can I call you?"), never medical detail.
- **Life** depends: if you discuss medical conditions, treat it as health.
- **WhatsApp never** carries medical data (Meta won't sign a BAA).

**Note:** your token is stored encrypted and the server never returns it, not even to you. Lose it and you reconnect with a new one. Assistants don't see this screen.` } },
  { id: 'phone-quo', cat: 'phone', nav: [],
    title: { es: 'Quo (OpenPhone): conectar y solucionar problemas', en: 'Quo (OpenPhone): setup & troubleshooting' },
    body: {
      es: `Quo (antes OpenPhone) da **SMS de dos vías desde Deskte** y **llamadas con su propia app** (iPhone/Android/PC). Las llamadas viven en la app de Quo; el SMS entra y sale desde Deskte. Firma el BAA en el plan Business.

### Conectar
1. En Quo → **Settings → API** (necesitas plan **Business/Scale** y ser **Owner/Admin**). Genera una **API Key** (empieza con **op_**).
2. En Deskte → **SMS y Llamadas** → tarjeta de Quo → **Conectar**. Pon tu número y pega la API Key. **Guardar**.

### Activar recepción (SMS entrantes + registro de llamadas)
- Clic en **"Activar recepción"**. Con un clic, Deskte configura los webhooks en tu cuenta de Quo (SMS entrantes + registro de llamadas con resumen). No tienes que tocar el panel de Quo.

### Error "Quo 401" al activar
Significa que **Quo rechazó la API Key** (no es un error de Deskte). Casi siempre:
- La llave se pegó **incompleta o con espacios** (debe empezar con **op_**).
- El plan **no incluye API** (se requiere **Business/Scale**).
- Quien la generó **no es Owner/Admin**.
- La llave **fue regenerada** y la vieja dejó de servir.

**Arreglo:** genera una llave nueva (Owner/Admin, plan Business) → en Deskte **Reconectar** Quo → pégala → **Guardar** → **Activar recepción** otra vez.

### El botón "Llamar" no abre la app de Quo
Windows necesita saber que los enlaces **tel:** los abre Quo.
1. Instala la **app de escritorio de Quo** (no solo la web) e inicia sesión.
2. **Prueba:** tecla Windows + R → escribe **tel:+13055550123** → Enter. Si abre Quo, funciona.
3. Si no abre: cierra Quo, **desinstala y reinstala** la app, ábrela e inicia sesión, y repite la prueba.
4. En el navegador, al tocar "Llamar", acepta el aviso **"¿Abrir Quo?"** y marca "Permitir siempre".

**Importante:** el registro de llamadas depende de la API Key. Sin resolver el 401, aunque llames desde Quo, la llamada **no se registra** en Deskte.`,
      en: `Quo (formerly OpenPhone) gives **two-way SMS from Deskte** and **calls through its own app** (iPhone/Android/PC). Calls live in the Quo app; SMS flows in and out from Deskte. It signs the BAA on the Business plan.

### Connect
1. In Quo → **Settings → API** (needs the **Business/Scale** plan and **Owner/Admin**). Generate an **API Key** (starts with **op_**).
2. In Deskte → **SMS & Calls** → Quo card → **Connect**. Enter your number and paste the API Key. **Save**.

### Turn on receiving (inbound SMS + call logging)
- Click **"Turn on receiving"**. In one click, Deskte configures the webhooks in your Quo account (inbound SMS + call logging with summary). No need to touch the Quo dashboard.

### "Quo 401" error when turning it on
It means **Quo rejected the API Key** (not a Deskte error). Usually:
- The key was pasted **incomplete or with spaces** (it must start with **op_**).
- The plan **doesn't include API** (needs **Business/Scale**).
- Whoever generated it **isn't Owner/Admin**.
- The key was **regenerated** and the old one stopped working.

**Fix:** generate a new key (Owner/Admin, Business plan) → in Deskte **Reconnect** Quo → paste it → **Save** → **Turn on receiving** again.

### The "Call" button doesn't open the Quo app
Windows needs to know that Quo opens **tel:** links.
1. Install the **Quo desktop app** (not just the web) and sign in.
2. **Test:** Windows key + R → type **tel:+13055550123** → Enter. If Quo opens, it works.
3. If it doesn't: close Quo, **uninstall and reinstall** the app, open it and sign in, then repeat the test.
4. In the browser, when you click "Call", accept the **"Open Quo?"** prompt and choose "Always allow".

**Important:** call logging depends on the API Key. Until the 401 is fixed, calls made in Quo are **not logged** in Deskte.` } },
  { id: 'phone-signalwire', cat: 'phone', nav: [],
    title: { es: 'SignalWire (recomendado): conectar', en: 'SignalWire (recommended): setup' },
    body: {
      es: `**Recomendado:** el más barato para uso normal, sin cuota fija de plataforma. Envía **SMS y llamadas por API**. Firma el BAA (ese plan tiene un mínimo mensual).

### Qué necesitas de SignalWire
En tu panel de SignalWire (API credentials):
- **Project ID**
- **API Token**
- **Space URL** (ej. **tunombre.signalwire.com**)

### Conectar en Deskte
1. Menú → **SMS y Llamadas** → tarjeta de SignalWire → **Conectar**.
2. Pon tu **número**, el **Project ID**, la **Space URL** y el **API Token**.
3. **Guardar conexión**.

### Respuestas automáticas (renovación)
Con SignalWire conectado puedes activar **"Respuestas automáticas"**: cuando el cliente responde al aviso de renovación, Deskte le manda el link solo (configura el webhook de tu número automáticamente).

### Para textear como negocio en EE. UU.
Se requiere el registro **A2P 10DLC** (~$4 una vez + ~$2/mes). Costos aprox.: SMS ~$0.007 c/u, llamada ~$0.01/min, número ~$0.85–1.15/mes (los pagas al proveedor).`,
      en: `**Recommended:** cheapest for regular use, with no fixed platform fee. Sends **SMS and calls via API**. It signs the BAA (that plan has a monthly minimum).

### What you need from SignalWire
In your SignalWire dashboard (API credentials):
- **Project ID**
- **API Token**
- **Space URL** (e.g. **yourname.signalwire.com**)

### Connect in Deskte
1. Menu → **SMS & Calls** → SignalWire card → **Connect**.
2. Enter your **number**, the **Project ID**, the **Space URL** and the **API Token**.
3. **Save connection**.

### Auto-replies (renewal)
With SignalWire connected you can turn on **"Auto-replies"**: when the client replies to the renewal notice, Deskte sends them the link automatically (it configures your number's webhook for you).

### To text as a US business
The **A2P 10DLC** registration is required (~$4 once + ~$2/mo). Approx costs: SMS ~$0.007 each, calls ~$0.01/min, number ~$0.85–1.15/mo (paid to the provider).` } },
  { id: 'phone-twilio', cat: 'phone', nav: [],
    title: { es: 'Twilio: conectar', en: 'Twilio: setup' },
    body: {
      es: `El proveedor **más conocido**. Envía **SMS y llamadas por API**. Para firmar el acuerdo HIPAA (BAA) exige su edición de pago **"Security"**.

### Qué necesitas de Twilio
En la consola de Twilio (dashboard principal):
- **Account SID**
- **Auth Token**

### Conectar en Deskte
1. Menú → **SMS y Llamadas** → tarjeta de Twilio → **Conectar**.
2. Pon tu **número**, el **Account SID** y el **Auth Token**.
3. **Guardar conexión**.

### Respuestas automáticas (renovación)
Con Twilio conectado puedes activar **"Respuestas automáticas"** para que el link de renovación se envíe solo cuando el cliente responde.

### Para textear como negocio en EE. UU.
Requiere el registro **A2P 10DLC** (~$4 una vez + ~$2/mes). Costos aprox.: SMS ~$0.007 c/u, llamada ~$0.01/min, número ~$0.85–1.15/mes.`,
      en: `The **best-known** provider. Sends **SMS and calls via API**. Signing the HIPAA agreement (BAA) requires their paid **"Security"** edition.

### What you need from Twilio
In the Twilio Console (main dashboard):
- **Account SID**
- **Auth Token**

### Connect in Deskte
1. Menu → **SMS & Calls** → Twilio card → **Connect**.
2. Enter your **number**, the **Account SID** and the **Auth Token**.
3. **Save connection**.

### Auto-replies (renewal)
With Twilio connected you can turn on **"Auto-replies"** so the renewal link is sent automatically when the client replies.

### To text as a US business
Requires the **A2P 10DLC** registration (~$4 once + ~$2/mo). Approx costs: SMS ~$0.007 each, calls ~$0.01/min, number ~$0.85–1.15/mo.` } },
  { id: 'phone-ringcentral', cat: 'phone', nav: [],
    title: { es: 'RingCentral: conectar', en: 'RingCentral: setup' },
    body: {
      es: `Si ya usas **RingCentral** para tu teléfono de negocio, conéctalo aquí. Envía **SMS y llamadas**, y Deskte **jala automáticamente las grabaciones/registro** de tus llamadas hacia la ficha del cliente.

### Qué necesitas de RingCentral
En la consola de **RingCentral Developers**, crea una app **server-to-server** y toma de **Credentials**:
- **Client ID**
- **Client Secret**
- **JWT** (credencial server-to-server → Credentials → JWT)

### Conectar en Deskte
1. Menú → **SMS y Llamadas** → tarjeta de RingCentral → **Conectar**.
2. Pon tu **número**, el **Client ID**, el **Client Secret** y pega el **JWT**.
3. **Guardar conexión**.

### Llamadas
Marca desde tu **app de RingCentral / teléfono**. Deskte registra la llamada y, cuando RingCentral termina de procesar, adjunta la **grabación** a la ficha (por sincronización periódica).`,
      en: `If you already use **RingCentral** for your business phone, connect it here. It sends **SMS and calls**, and Deskte **automatically pulls in your call recordings/logs** onto the client profile.

### What you need from RingCentral
In the **RingCentral Developers** console, create a **server-to-server** app and take from **Credentials**:
- **Client ID**
- **Client Secret**
- **JWT** (server-to-server credential → Credentials → JWT)

### Connect in Deskte
1. Menu → **SMS & Calls** → RingCentral card → **Connect**.
2. Enter your **number**, the **Client ID**, the **Client Secret** and paste the **JWT**.
3. **Save connection**.

### Calls
Dial from your **RingCentral app / phone**. Deskte logs the call and, once RingCentral finishes processing, attaches the **recording** to the profile (via periodic sync).` } },
  { id: 'phone-mightycall', cat: 'phone', nav: [],
    title: { es: 'MightyCall (solo llamadas): conectar', en: 'MightyCall (calls only): setup' },
    body: {
      es: `**Solo llamadas** (no envía SMS). Suena en tu app de MightyCall y te conecta con el cliente. Firma el acuerdo HIPAA (BAA).

### Qué necesitas de MightyCall
En MightyCall → **Integraciones → API**:
- **API Key** (x-api-key, formato **00000000-0000-0000-0000-000000000000**)
- **Client ID**
- **Client Secret**

### Conectar en Deskte
1. Menú → **SMS y Llamadas** → tarjeta de MightyCall → **Conectar**.
2. Pon tu **número**, el **Client ID**, el **Client Secret** y la **API Key**.
3. **Guardar conexión**.

**Nota:** como es solo voz, en la tarjeta no aparece la opción de SMS. Para textos, usa otro proveedor (Quo, SignalWire o Twilio).`,
      en: `**Calls only** (no SMS). It rings in your MightyCall app and connects you to the client. It signs the HIPAA agreement (BAA).

### What you need from MightyCall
In MightyCall → **Integrations → API**:
- **API Key** (x-api-key, format **00000000-0000-0000-0000-000000000000**)
- **Client ID**
- **Client Secret**

### Connect in Deskte
1. Menu → **SMS & Calls** → MightyCall card → **Connect**.
2. Enter your **number**, the **Client ID**, the **Client Secret** and the **API Key**.
3. **Save connection**.

**Note:** since it's voice-only, the SMS option doesn't appear on the card. For texts, use another provider (Quo, SignalWire or Twilio).` } },

  // ── Ventas y Documentos ───────────────────────────────────────────────────
  { id: 'cotizador-mercado', cat: 'sales', nav: ['quotes_invoices'],
    title: { es: 'Cotizador del Mercado de Salud: paso a paso', en: 'Health Marketplace quoter: step by step' },
    body: {
      es: `Busca en vivo los planes del Mercado de Salud (HealthCare.gov) para un cliente, con su crédito fiscal ya aplicado, y los pone en una cotización que le puedes mandar. También te dice qué planes cubren sus medicamentos y si sus médicos están en la red.

**Los números de cada imagen son los mismos que los de cada paso.** Las capturas salen de un Deskte de práctica con clientes inventados.

### Parte 1 · Abre el cotizador

### Paso 1 · Cotizaciones → «Nueva cotización»
En el menú entra a **Cotizaciones** y toca **Nueva cotización** (arriba a la derecha).
![La pantalla Cotizaciones con el botón Nueva cotización resaltado](ayuda/mercado-01-nueva.webp)

### Paso 2 · Ponle un título y elige al cliente
Escribe un **Título** (por ejemplo «Salud familiar 2027») y en **Cliente** busca al cliente por su nombre. El nombre y el correo del destinatario se llenan solos.
![El formulario de la cotización con el título y la cliente Ana Gutierrez elegidos](ayuda/mercado-02-titulo-cliente.webp)

### Paso 3 · Toca «Buscar en el Mercado»
Está en **Opciones de plan**.
- Si no ves el botón, el cotizador del Mercado no está activado en tu cuenta: escríbenos a soporte.
![El botón Buscar en el Mercado resaltado](ayuda/mercado-03-buscar.webp)

### Parte 2 · Los datos del hogar

### Paso 4 · Revisa lo que trajo de la ficha
Con **Cliente existente**, Deskte trae de la ficha el **ZIP**, el **ingreso ANUAL del hogar** y a **cada persona** con su fecha de nacimiento. Revísalo y elige el **Año del plan**.
- El ingreso es **del año entero**, no del mes.
- Usa la **fecha de nacimiento**, no la edad a mano: así el precio sale igual que en HealthCare.gov.
- Quita la marca de **¿Cobertura?** a quien ya tiene seguro pero cuenta en el hogar: sigue contando para el subsidio.
- **Negado Medicaid**: márcalo si el estado ya le negó Medicaid o CHIP; así se cotiza en el plan del Mercado.
- Si un dependiente tiene **ficha propia**, Deskte pregunta si incluirlo. Los padres entran siempre.
- **Cliente nuevo** es para cotizar a alguien que no está en Deskte: escribes los datos a mano y no se crea ninguna ficha.
![La ventana Buscar en el Mercado de Salud con el ZIP, el ingreso y las tres personas traídos de la ficha](ayuda/mercado-04-datos.webp)

### Paso 5 · Elige el sexo de cada persona y toca «Buscar planes»
El Mercado lo pide para calcular el precio. Deja marcado **Aplicar subsidio (crédito fiscal) estimado** si el hogar puede tener crédito.
![El sexo de cada persona elegido y el botón Buscar planes resaltado](ayuda/mercado-05-sexo-buscar.webp)

### Parte 3 · Elige los planes

### Paso 6 · Mira el crédito fiscal y la lista
La barra verde dice cuánto crédito recibe el hogar al mes. **Los precios de la lista ya son lo que paga el cliente**; el precio completo sale tachado debajo. Arriba puedes filtrar por compañía, nivel (Bronze, Silver…) o deducible máximo, y ordenar por prima, deducible o nivel.
- Si alguien del hogar califica para **Medicaid o CHIP**, sale aparte: se inscribe gratis por su lado y no entra en el plan.
![La barra verde Crédito fiscal aplicado de $428.00 al mes resaltada](ayuda/mercado-06-credito.webp)

### Paso 7 · ¿Cubre sus medicamentos?
Salen solos los medicamentos de la ficha. Para añadir otro, escríbelo en **Medicamento**, elígelo de la lista y toca su **dosis**. Cada plan dice **«Cubre su medicamento»** o **«No cubre»**. Marca **Solo planes que los cubren todos** para esconder los demás. Toca un medicamento para dejar de contarlo.
- Lo que añades aquí no se guarda en la ficha (para eso, pestaña **Médicos y citas** del cliente).
![La sección ¿Cubre sus medicamentos? con metformina 500 mg y los planes que la cubren o no](ayuda/mercado-07-medicamentos.webp)

### Paso 8 · ¿Sus médicos están en la red?
Salen solos los médicos de la ficha que tienen NPI. Para añadir otro toca **+ Buscar médico**, escribe el nombre o la especialidad y el ZIP, y elígelo. Cada plan dice **«Su médico está en la red»** o **«Fuera de la red»**.
- Es el dato del directorio de HealthCare.gov: confírmalo con el consultorio antes de inscribir.
![La sección ¿Sus médicos están en la red? con la doctora Ana Morales y lo que dice cada plan](ayuda/mercado-08-medicos.webp)

### Paso 9 · Marca los planes y toca «Usar planes»
Marca la casilla de los planes que le quieres presentar (uno o varios) y toca **Usar 2 planes** (el número cambia según cuántos marques), abajo a la derecha.
![Dos planes Silver marcados en la lista](ayuda/mercado-09-marcar.webp)

### Parte 4 · La cotización

### Paso 10 · Revisa las opciones
Cada plan entra como una **opción** con la compañía, la prima, el subsidio, los deducibles, los copagos y el máximo de bolsillo. Arriba queda la barra **Datos de la cotización** (ZIP, ingreso, personas y año): viaja con la cotización, para que el cliente vea con qué datos salió el precio. Puedes cambiar cualquier número a mano.
![La cotización con la barra Datos de la cotización y la Opción 1 llena](ayuda/mercado-10-opciones.webp)

### Paso 11 · Toca «Crear»
Abajo del todo. La tabla comparativa de los planes se adjunta sola (**comparacion-planes.png**) y puedes añadir el resumen de beneficios u otros archivos con **Adjuntar**.
![El final de la cotización con el adjunto y el botón Crear resaltado](ayuda/mercado-11-crear.webp)

### Paso 12 · Mándasela al cliente
Con **Enviar** le llega un enlace donde compara las opciones y acepta la que quiere. Con **Descargar PDF** tienes la cotización para imprimir o mandar por otro lado.
- ¿Te dijo por teléfono cuál quiere? Marca esa opción en la cotización y se crea la póliza en **Pendiente**, sin mandarle nada.
![La cotización guardada con los botones Descargar PDF y Enviar resaltados](ayuda/mercado-12-guardada.webp)

### Ten en cuenta
- Es un **estimado**: la inscripción se hace en el Mercado (por ejemplo con **Inscribir en HealthSherpa**) y el subsidio se confirma al aplicar.
- Si haces una **asesoría**, el botón **Cotizar en el Mercado a: …** de su paso 4 abre este mismo cotizador con las personas que van al Mercado (ver la guía **Asesoría guiada**).`,
      en: `Searches live Health Marketplace (HealthCare.gov) plans for a client, with their tax credit already applied, and puts them in a quote you can send. It also tells you which plans cover their medications and whether their doctors are in network.

**The number on each picture matches the step number.** The screenshots come from a practice Deskte with invented clients.

### Part 1 · Open the quoter

### Step 1 · Quotes → «New Quote»
In the menu go to **Quotes** and tap **New Quote** (top right).
![The Quotes screen with the New Quote button highlighted](ayuda/mercado-01-nueva-en.webp)

### Step 2 · Give it a title and pick the client
Type a **Title** (for example «Family health 2027») and in **Client** search the client by name. The recipient's name and email fill in on their own.
![The quote form with the title and the client Ana Gutierrez chosen](ayuda/mercado-02-titulo-cliente-en.webp)

### Step 3 · Tap «Search Marketplace»
It is under **Plan options**.
- If you don't see the button, the Marketplace quoter is not turned on for your account: write to support.
![The Search Marketplace button highlighted](ayuda/mercado-03-buscar-en.webp)

### Part 2 · The household data

### Step 4 · Check what came from the profile
With **Existing client**, Deskte brings the **ZIP**, the **ANNUAL household income** and **each person** with their date of birth from the profile. Check it and pick the **Plan year**.
- Income is **for the whole year**, not monthly.
- Use the **date of birth**, not a typed age: that way the price matches HealthCare.gov.
- Untick **Coverage?** for someone who already has insurance but is part of the household: they still count for the subsidy.
- **Denied Medicaid**: tick it if the state already denied them Medicaid or CHIP, so they are quoted in the Marketplace plan.
- If a dependent has **their own profile**, Deskte asks whether to include them. Parents are always included.
- **New client** is for quoting someone who is not in Deskte: you type the data by hand and no profile is created.
![The Search the Health Marketplace window with ZIP, income and three people brought from the profile](ayuda/mercado-04-datos-en.webp)

### Step 5 · Pick each person's sex and tap «Search plans»
The Marketplace needs it to price the plan. Keep **Apply estimated subsidy (tax credit)** ticked if the household may get a credit.
![Each person's sex chosen and the Search plans button highlighted](ayuda/mercado-05-sexo-buscar-en.webp)

### Part 3 · Choose the plans

### Step 6 · Look at the tax credit and the list
The green bar shows the household's monthly credit. **The prices in the list are already what the client pays**; the full price is crossed out below. At the top you can filter by company, metal level or maximum deductible, and sort by premium, deductible or level.
- If someone in the household qualifies for **Medicaid or CHIP**, they show up separately: they enroll for free on their own and are not in the plan.
![The green Tax credit applied bar of $428.00 a month highlighted](ayuda/mercado-06-credito-en.webp)

### Step 7 · Does it cover their medications?
The medications in the profile show up on their own. To add another, type it in **Medication**, pick it from the list and tap its **dose**. Each plan says **«Covers their medication»** or **«Does not cover»**. Tick **Only plans that cover all of them** to hide the rest. Tap a medication to stop counting it.
- What you add here is not saved to the profile (use the client's **Doctors & visits** tab for that).
![The Does it cover their medications? section with metformin 500 mg and which plans cover it](ayuda/mercado-07-medicamentos-en.webp)

### Step 8 · Are their doctors in network?
The profile's doctors with an NPI show up on their own. To add another tap **+ Search doctor**, type the name or specialty and the ZIP, and pick them. Each plan says **«Their doctor is in network»** or **«Out of network»**.
- It comes from the HealthCare.gov directory: confirm with the office before enrolling.
![The Are their doctors in network? section with Dr. Ana Morales and what each plan says](ayuda/mercado-08-medicos-en.webp)

### Step 9 · Tick the plans and tap «Use plans»
Tick the plans you want to present (one or more) and tap **Use 2 plans** (the number follows how many you tick), bottom right.
![Two Silver plans ticked in the list](ayuda/mercado-09-marcar-en.webp)

### Part 4 · The quote

### Step 10 · Review the options
Each plan comes in as an **option** with the carrier, premium, subsidy, deductibles, copays and out-of-pocket maximum. At the top sits the **Quote data** bar (ZIP, income, people and year): it travels with the quote so the client sees the data behind the price. You can change any number by hand.
![The quote with the Quote data bar and Option 1 filled in](ayuda/mercado-10-opciones-en.webp)

### Step 11 · Tap «Create»
At the very bottom. The plan comparison table is attached on its own (**plan-comparison.png**) and you can add the benefits summary or other files with **Attach**.
![The end of the quote with the attachment and the Create button highlighted](ayuda/mercado-11-crear-en.webp)

### Step 12 · Send it to the client
**Send** gives them a link where they compare the options and accept the one they want. **Download PDF** gives you the quote to print or send another way.
- Did they tell you on the phone which one they want? Mark that option in the quote and the policy is created as **Pending**, without sending them anything.
![The saved quote with the Download PDF and Send buttons highlighted](ayuda/mercado-12-guardada-en.webp)

### Keep in mind
- It is an **estimate**: enrollment happens on the Marketplace (for example with **Enroll in HealthSherpa**) and the subsidy is confirmed when applying.
- In a **consultation**, the **Quote the Marketplace for: …** button in its step 4 opens this same quoter with the people going to the Marketplace (see the **Guided consultation** guide).`,
    } },
  { id: 'asesoria', cat: 'sales', nav: ['quotes_invoices'],
    title: { es: 'Asesoría guiada: paso a paso', en: 'Guided consultation: step by step' },
    body: {
      es: `La asesoría te guía en la llamada con el cliente: quién se cubre y con qué estatus, cómo usan el médico y cuánto pueden pagar. Con eso Deskte te dice **qué ofrecer** de tu catálogo y del Mercado, arma el consentimiento que firma el cliente y, al cerrar, crea la cotización y las pólizas. Se guarda sola mientras escribes, y los datos de salud y de estatus migratorio van cifrados.

**Los números de cada imagen son los mismos que los de cada paso.** Las capturas salen de un Deskte de práctica con clientes inventados (Ana Gutierrez y sus dos hijos).

### Parte 1 · Empieza la asesoría

### Paso 1 · Cotizaciones → pestaña «Asesorías» → «Nueva asesoría»
Arriba ves cuántas asesorías tienes por color de salud y un buscador para encontrar las que siguen en curso.
- Desde la ficha de un cliente también puedes empezarla: **Cotizar → Asesoría guiada**.
![La pestaña Asesorías y el botón Nueva asesoría resaltados](ayuda/asesoria-01-nueva.webp)

### Paso 2 · Elige a quién y toca «Empezar asesoría»
- **Cliente**: busca su nombre. Se llenan solos el hogar, las edades, el estatus migratorio y el ingreso de la ficha. Si un dependiente tiene ficha propia, Deskte pregunta si incluirlo.
- **Lead**: elige el lead de la lista.
- **Persona nueva**: escribe nombre, apellido, fecha de nacimiento y estado.
![La ventana Nueva asesoría con la cliente Ana Gutierrez elegida](ayuda/asesoria-02-elegir.webp)

### Parte 2 · La entrevista

### Paso 3 · Póliza actual (paso 0 de la pantalla)
Si ya tiene seguro, Deskte toma lo que sabe de su póliza de salud. Escribe lo que falte: lo que paga hoy, el precio completo sin crédito y el subsidio. Está en la tarjeta del seguro o en el resumen de beneficios (SBC).
![El paso 0 Póliza actual con los datos tomados de su póliza en Deskte](ayuda/asesoria-03-poliza-actual.webp)

### Paso 4 · Quién se cubre y su estatus (paso 1)
Revisa cada persona: relación, edad, **estatus migratorio** y si **se cubre**. Debajo de cada estatus, Deskte sugiere por dónde puede ir esa persona. Luego marca la **Elegibilidad del hogar**:
- **Marketplace con posible crédito**, **Marketplace sin crédito** o **Fuera del Marketplace**.
- **Hogar mixto**: cada persona va por su camino. En el ejemplo, Ana (DACA) va fuera del Mercado y sus hijos ciudadanos van al Mercado con crédito.
- **Por verificar**: si tienes dudas. Quien decide de verdad es el Mercado con los documentos.
![El paso 1 con Ana en DACA, los hijos ciudadanos y Hogar mixto marcado](ayuda/asesoria-04-quien.webp)

### Paso 5 · Uso del cuidado médico (paso 2) y «¿A quién?»
Seis preguntas sobre cómo usa el hogar el médico. No es un cuestionario médico. Debajo de cada respuesta, **¿A quién?** dice a quién le pasa: **Todos** (así sale) o toca el nombre de esa persona. En el ejemplo, solo **Ana** tiene una condición controlada.
![La pregunta 4 contestada «Sí, controlada» y en ¿A quién? solo Ana](ayuda/asesoria-05-a-quien.webp)

### Paso 6 · El semáforo: por persona y del hogar
**Por persona** muestra el color de cada uno. Los planes de fuera se recomiendan mirando **solo la salud de los que van ahí**: así el que está enfermo y va al Mercado no le quita opciones a los sanos. Debajo, el **Semáforo de salud** (Saludable, Moderado, Delicado) sale sugerido y lo puedes cambiar.
- Es solo para ordenar la recomendación: no sustituye las preguntas de cada compañía y **nunca se le muestra al cliente**.
![La línea Por persona y el Semáforo de salud en Moderado](ayuda/asesoria-06-por-persona.webp)

### Paso 7 · Ingresos del hogar (paso 3)
Escribe el ingreso del año, las personas que declaran impuestos juntas y el estado. Deskte calcula el **% de la línea de pobreza (FPL)** y sugiere la **capacidad de pago**; toca la que te diga el cliente.
![El paso 3 con el ingreso, el 113% del FPL y la capacidad Media marcada](ayuda/asesoria-07-ingresos.webp)

### Parte 3 · Qué ofrecer (paso 4)

### Paso 8 · Quién va por dónde
Cada persona va al **Mercado** o a **Planes de fuera**; cámbialo tocando el botón. **Cotizar en el Mercado a: …** abre el cotizador del Mercado solo con los que van ahí (ver la guía **Cotizador del Mercado de Salud**).
![El cuadro Quién va por dónde: Ana a planes de fuera, Isabella y Marco al Mercado](ayuda/asesoria-08-quien-va.webp)

### Paso 9 · Elige una opción
La **Opción 1 · Recomendada** sale de **tu catálogo**, con lo que se puede vender en el estado del cliente. Toca **Qué cubre** para ver el detalle y **Elegir esta opción** cuando el cliente esté de acuerdo.
- **Ver otras opciones**: las demás combinaciones.
- **Personalizar**: arma la opción a tu gusto (la compañía, el producto y el plan de cada cosa).
- **Dental** y **Visión** se pueden sumar a cualquier opción.
![La Opción 1 recomendada con su botón Elegir esta opción y Personalizar](ayuda/asesoria-09-opcion.webp)

### Paso 10 · La opción que se le presenta
Revisa cada producto antes de presentarlo:
- **Para**: a quién cubre. Se cambia en «Quién va por dónde».
- **Quién califica**: un aviso amarillo dice si la compañía puede preguntar por la condición de alguien o excluirla, y cuáles son las esperas. Un ✓ verde dice que entran todos.
- **Plan** y precio de cada producto, **Otra compañía…** para cambiarlo por otro del mismo tipo, **×** para quitarlo y **+ Agregar producto** para sumar otro.
- El **Total** al mes es de referencia: el precio final lo confirma la compañía.
![La opción que se le presenta con Hospital Diario y Accidente Esencial, el aviso de Ana y el total](ayuda/asesoria-10-presentada.webp)

### Parte 4 · Cierre (paso 5)

### Paso 11 · Datos a pedirle y tu compromiso
**Datos a pedirle al cliente** es la lista de lo que pide cada producto para la solicitud: márcalo a medida que lo tengas. En **Compromiso del agente** marca lo que le explicaste: qué cubre y qué no, que no es un plan del Mercado, que el Mercado existe, el total y que decidió sin presión.
![Las listas Datos a pedirle al cliente y Compromiso del agente](ayuda/asesoria-11-compromiso.webp)

### Paso 12 · El consentimiento del cliente
Si la opción lleva productos que **no son del Mercado**, Deskte arma el consentimiento con lo que eligió. Elige el idioma del cliente, añade un párrafo propio si quieres y mándalo **por correo, SMS o WhatsApp**, o **Firmar en persona**. Queda en **Firmas**, y **Ver si ya firmó** te lo dice.
- Para mandarlo, la persona tiene que ser cliente en Deskte. Si es un lead o una persona nueva, toca **Agregar como cliente**.
- **Informe de la asesoría (imprimir / PDF)**: el resumen de toda la asesoría.
![El consentimiento del cliente con los botones para enviarlo](ayuda/asesoria-12-consentimiento.webp)

### Paso 13 · «Cerrar asesoría»
Abajo a la derecha. Elige:
- **Con venta**: se crean en la ficha **una cotización** con la opción elegida y **una póliza en Pendiente por cada producto**, con la fecha de inicio que pongas. Si falta algo (datos, firma), Deskte te avisa antes.
- **Sin venta**: se cierra sin crear nada.
Una asesoría cerrada queda guardada y ya no se puede cambiar. La pestaña **Panel** muestra cuántas asesorías terminaron en venta.
![La ventana Cerrar la asesoría con Con venta y la fecha de inicio de las pólizas](ayuda/asesoria-13-cerrar.webp)`,
      en: `The consultation guides your call with the client: who is covered and with what status, how they use the doctor and how much they can pay. With that, Deskte tells you **what to offer** from your catalog and the Marketplace, drafts the consent the client signs and, when you close, creates the quote and the policies. It saves itself as you type, and health and immigration data are encrypted.

**The number on each picture matches the step number.** The screenshots come from a practice Deskte with invented clients (Ana Gutierrez and her two children). Some catalog texts in the pictures are in Spanish because the practice catalog is.

### Part 1 · Start the consultation

### Step 1 · Quotes → «Consultations» tab → «New consultation»
At the top you see how many consultations you have per health color, and a search box to find the ones still in progress.
- You can also start one from a client's profile: **Quote → Guided consultation**.
![The Consultations tab and the New consultation button highlighted](ayuda/asesoria-01-nueva-en.webp)

### Step 2 · Choose who and tap «Start consultation»
- **Client**: search their name. Household, ages, immigration status and income fill in from the profile. If a dependent has their own profile, Deskte asks whether to include them.
- **Lead**: pick the lead from the list.
- **New person**: type first name, last name, date of birth and state.
![The New consultation window with the client Ana Gutierrez chosen](ayuda/asesoria-02-elegir-en.webp)

### Part 2 · The interview

### Step 3 · Current policy (step 0 on screen)
If they already have insurance, Deskte takes what it knows from their health policy. Type what is missing: what they pay today, the full price without credit and the subsidy. It is on the insurance card or the benefits summary (SBC).
![Step 0 Current policy with the data taken from their policy in Deskte](ayuda/asesoria-03-poliza-actual-en.webp)

### Step 4 · Who is covered and their status (step 1)
Check each person: relationship, age, **immigration status** and whether they **are covered**. Under each status Deskte suggests where that person can go. Then mark the **Household eligibility**:
- **Marketplace, may get tax credit**, **Marketplace, no tax credit** or **Not Marketplace eligible**.
- **Mixed household**: each person goes their own way. In the example Ana (DACA) goes outside the Marketplace and her citizen children go to the Marketplace with credit.
- **To be verified**: when in doubt. The Marketplace decides for real with the documents.
![Step 1 with Ana on DACA, the citizen children and Mixed household marked](ayuda/asesoria-04-quien-en.webp)

### Step 5 · Use of medical care (step 2) and «Who?»
Six questions on how the household uses the doctor. It is not a medical questionnaire. Under each answer, **Who?** says who it applies to: **Everyone** (the default) or tap that person's name. In the example only **Ana** has a condition under control.
![Question 4 answered «Yes, under control» with only Ana in Who?](ayuda/asesoria-05-a-quien-en.webp)

### Step 6 · The health light: per person and for the household
**Per person** shows each one's color. Outside plans are recommended looking **only at the health of those going there**: the sick person going to the Marketplace doesn't take options away from the healthy ones. Below it, the **Health light** (Healthy, Moderate, Delicate) comes suggested and you can change it.
- It only orders the recommendation: it does not replace each carrier's questions and is **never shown to the client**.
![The Per person line and the Health light on Moderate](ayuda/asesoria-06-por-persona-en.webp)

### Step 7 · Household income (step 3)
Type the yearly income, the people who file taxes together and the state. Deskte works out the **% of the poverty line (FPL)** and suggests the **ability to pay**; tap the one the client tells you.
![Step 3 with the income, 113% of FPL and Middle ability to pay marked](ayuda/asesoria-07-ingresos-en.webp)

### Part 3 · What to offer (step 4)

### Step 8 · Who goes where
Each person goes to the **Marketplace** or to **Other plans**; tap the button to change it. **Quote the Marketplace for: …** opens the Marketplace quoter with only those people (see the **Health Marketplace quoter** guide).
![The Who goes where box: Ana to other plans, Isabella and Marco to the Marketplace](ayuda/asesoria-08-quien-va-en.webp)

### Step 9 · Choose an option
**Option 1 · Recommended** comes from **your catalog**, with what can be sold in the client's state. Tap **What it covers** for the detail and **Choose this option** when the client agrees.
- **See other options**: the other combinations.
- **Customize**: build your own option (the carrier, product and plan of each thing).
- **Dental** and **Vision** can be added to any option.
![Option 1 recommended with its Choose this option and Customize buttons](ayuda/asesoria-09-opcion-en.webp)

### Step 10 · The option to present
Check each product before presenting it:
- **For**: who it covers. You change it in «Who goes where».
- **Who qualifies**: a yellow notice says whether the carrier may ask about someone's condition or exclude it, and the waiting periods. A green ✓ says everyone gets in.
- Each product's **Plan** and price, **Other carrier…** to swap it for another of the same type, **×** to remove it and **+ Add product** for another.
- The monthly **Total** is a reference: the carrier confirms the final price.
![The option to present with Hospital Diario and Accidente Esencial, Ana's notice and the total](ayuda/asesoria-10-presentada-en.webp)

### Part 4 · Closing (step 5)

### Step 11 · What to collect and your checklist
**What to collect** lists what each product needs for the application: tick it as you get it. In the **Agent checklist** tick what you explained: what it covers and doesn't, that it is not a Marketplace plan, that the Marketplace exists, the total, and that they decided without pressure.
![The What to collect and Agent checklist lists](ayuda/asesoria-11-compromiso-en.webp)

### Step 12 · The client's consent
If the option has products that are **not from the Marketplace**, Deskte drafts the consent with what they chose. Pick the client's language, add your own paragraph if you want and send it **by email, SMS or WhatsApp**, or **Sign in person**. It goes to **Signatures**, and **Check signature** tells you.
- To send it, the person has to be a client in Deskte. If they are a lead or a new person, tap **Add as client**.
- **Consultation report (print / PDF)**: the summary of the whole consultation.
![The client consent with the buttons to send it](ayuda/asesoria-12-consentimiento-en.webp)

### Step 13 · «Close consultation»
Bottom right. Choose:
- **Sold**: the profile gets **a quote** with the chosen option and **one Pending policy per product**, with the start date you set. If something is missing (data, signature), Deskte warns you first.
- **Not sold**: it closes without creating anything.
A closed consultation stays saved and can no longer be changed. The **Dashboard** tab shows how many consultations ended in a sale.
![The Close the consultation window with Sold and the policies' start date](ayuda/asesoria-13-cerrar-en.webp)`,
    } },
  // Johanna & Otto Arosemena, 2026-11-04: *"Me puedes dar los pasos para
  // agregar las compañías y sus folletos para que cotice y en Consultor"*.
  // Imágenes: crm-app/capturas-guia-catalogo.mjs (sale de la demo).
  { id: 'catalogo-productos', cat: 'sales', nav: ['quotes_invoices'],
    title: { es: 'Agregar compañías, productos y folletos al Catálogo', en: 'Add carriers, products and brochures to the Catalog' },
    body: {
      es: `El **Catálogo de productos** es la lista de lo que vende tu agencia: suplementarios (dental, accidente, hospital, indemnización), Short-Term, vida… Lo que pongas aquí sale en **dos sitios**: en tus **cotizaciones**, con el botón **Catálogo**, y en las **Asesorías**, donde Deskte recomienda qué ofrecer.

**Los números de cada imagen son los mismos que los de cada paso.** Las capturas salen de un Deskte de práctica con datos inventados.

**Quién puede editarlo:** la dueña de la agencia, la admin y el agente solo. Los demás lo ven y lo usan para cotizar, pero no lo cambian.

### Parte 1 · Agrega la compañía y su producto

### Paso 1 · Cotizaciones → pestaña «Catálogo de productos»
Ahí ves todo lo que tiene tu agencia, con filtros por tipo de seguro y un buscador.
- Los que llevan la etiqueta **DESKTE** ya vienen listos (por ejemplo, los de UnitedHealthcare). Si no los vendes, ocúltalos con el ojo tachado.
![La pestaña Catálogo de productos resaltada](ayuda/catalogo-01-pestana.webp)

### Paso 2 · «Nuevo producto»
- Si el producto ya está en la lista y solo quieres **poner tus precios**, usa el botón de **copiar** de esa fila («Copiar a mi catálogo»): te queda una copia tuya para editar.
![El botón Nuevo producto y el de copiar a mi catálogo resaltados](ayuda/catalogo-02-nuevo.webp)

### Paso 3 · General: nombre, compañía y tipo
- **Nombre del producto** (por ejemplo, «Dental Plus») y **Compañía** (la aseguradora).
- **Tipo de producto**: dental, accidente, hospitalización, indemnización fija, visión, Short-Term… Es lo que usan los filtros.
- Si hay edad mínima o máxima, escríbela: el cotizador no lo ofrecerá fuera de esas edades.
![Nombre, compañía y tipo de producto llenos](ayuda/catalogo-03-general.webp)

### Paso 4 · Lo que cubre
- **Beneficios para explicarle al cliente**: uno por línea, con tus palabras.
- **Cuánto paga cada plan**: con **Agregar beneficio**, una fila por beneficio con el valor tal como dice el folleto («$1,000 por día, máx. 31 días»).
- Más abajo, **Lo que no cubre**, las preexistencias y los períodos de espera.
- Todo esto **lo ve el cliente** en el resumen de la opción.
![Los beneficios escritos y el botón Agregar beneficio](ayuda/catalogo-04-beneficios.webp)

### Paso 5 · Dónde se vende
**Todos los estados**, **Solo en los que marque** o **Todos menos los que marque**. En la asesoría, al marcar el estado del cliente, Deskte esconde lo que no se vende allí.
![Las tres opciones de Dónde se vende](ayuda/catalogo-05-donde.webp)

### Paso 6 · Precios
- Escribe los **planes o niveles** del producto (**+ Plan** para agregar otro).
- Elige **¿Cómo se cobra?**: **por tipo de cobertura** (1 persona, pareja, hijos, familia), **por persona** (se suma cada miembro, como casi todos los dentales) o **sin tabla** (escribes la prima que te dio la compañía). Si el precio sube con la edad, marca **El precio cambia según la edad**.
- Lo más rápido: **Pegar tabla desde Excel o PDF**. Copia las filas de la tabla de la compañía y Deskte las acomoda; te dice cuántas filas entendió.
- Con **Probar precio** compruebas que la tabla quedó bien.
![Los planes y el botón Pegar tabla desde Excel o PDF](ayuda/catalogo-06-precios.webp)

### Parte 2 · Los folletos

### Paso 7 · Para contratar → «Brochures para el cliente»
- Pulsa **Subir PDF** y elige el folleto. También puedes pegar un **enlace** al PDF con su nombre y tocar **Agregar**.
- Ponle un nombre que se entienda («Folleto en español», «Brochure in English»).
- Estos folletos van en la cotización y el cliente los abre desde el enlace.
- **Nunca subas documentos de un cliente** (IDs, solicitudes llenas): todo lo del catálogo lo ve toda la agencia.
- Arriba, en **Datos que hay que pedirle al cliente**, pon lo que pide la compañía para contratar (foto del ID, peso y estatura…). En el cierre de la asesoría se van marcando.
![La sección Brochures para el cliente con el botón Subir PDF](ayuda/catalogo-07-folletos.webp)

### Paso 8 · Solo para el agente
La comisión, un aviso que sale en amarillo en la lista y notas internas. **Nada de esta pestaña se le muestra ni se le envía al cliente.**
![La pestaña Solo para el agente](ayuda/catalogo-08-agente.webp)

### Paso 9 · «Activo» y «Guardar producto»
Deja marcado **Activo — el cotizador lo puede recomendar** y guarda. Si falta algo obligatorio, Deskte lo dice en amarillo encima de los botones.
![La casilla Activo y el botón Guardar producto](ayuda/catalogo-09-guardar.webp)

### Parte 3 · Úsalo para cotizar

### Paso 10 · En una cotización: botón «Catálogo»
En **Cotizaciones → Nueva cotización** (o al editar una), en **Opciones de plan**, toca **Catálogo**.
![El botón Catálogo dentro de una cotización](ayuda/catalogo-10-en-cotizacion.webp)

### Paso 11 · Marca los planes y agrégalos
Marca los planes que vas a ofrecer y toca **Agregar plan(es)**. Entran como opciones con lo que cubre, lo que no y los folletos. El precio sale solo si el producto tiene tabla y la cotización tiene un cliente con fecha de nacimiento; si no, escribe la prima que te dio la compañía.
![Un plan marcado y el botón Agregar plan(es)](ayuda/catalogo-11-marcar.webp)

### En las Asesorías
No hay que hacer nada más. En el paso **Qué ofrecer**, Deskte recomienda los productos **activos** de tu catálogo que se le pueden vender a ese cliente (por su estado, su edad y su salud). Mira la guía **Asesoría guiada: paso a paso**.`,
      en: `The **Product catalog** is the list of what your agency sells: supplementals (dental, accident, hospital, fixed indemnity), Short-Term, life… What you add here shows up in **two places**: in your **quotes**, through the **Catalog** button, and in **Consultations**, where Deskte recommends what to offer.

**The number on each image matches the step number.** The screenshots come from a practice Deskte with made-up data.

**Who can edit it:** the agency owner, the agency admin and solo agents. Everyone else can see it and quote with it, but not change it.

### Part 1 · Add the carrier and its product

### Step 1 · Quotes → «Product catalog» tab
You see everything your agency has, with filters by insurance type and a search box.
- Products tagged **DESKTE** come ready to use (for example, the UnitedHealthcare ones). If you don't sell one, hide it with the crossed-out eye.
![The Product catalog tab highlighted](ayuda/catalogo-01-pestana-en.webp)

### Step 2 · «New product»
- If the product is already on the list and you only want to **set your own prices**, use that row's **copy** button («Copy to my catalog»): you get your own copy to edit.
![The New product button and the copy-to-my-catalog button highlighted](ayuda/catalogo-02-nuevo-en.webp)

### Step 3 · General: name, carrier and type
- **Product name** (for example, «Dental Plus») and **Carrier**.
- **Product type**: dental, accident, hospital, fixed indemnity, vision, Short-Term… The filters use it.
- If there is a minimum or maximum age, enter it: the quoter won't offer it outside those ages.
![Product name, carrier and type filled in](ayuda/catalogo-03-general-en.webp)

### Step 4 · What it covers
- **Benefits to explain to the client**: one per line, in your own words.
- **What each plan pays**: with **Add benefit**, one row per benefit, worded as in the brochure («$1,000 per day, max 31 days»).
- Further down: **What it does not cover**, pre-existing conditions and waiting periods.
- **The client sees all of this** in the option summary.
![The benefits written in and the Add benefit button](ayuda/catalogo-04-beneficios-en.webp)

### Step 5 · Where sold
**All states**, **Only the ones I pick** or **All but the ones I pick**. In a consultation, once you set the client's state, Deskte hides what isn't sold there.
![The three Where sold options](ayuda/catalogo-05-donde-en.webp)

### Step 6 · Rates
- Enter the product's **plans or tiers** (**+ Plan** to add one).
- Choose **How is it priced?**: **by coverage tier** (individual, couple, children, family), **per person** (each member is added up, like most dental plans) or **no table** (you type the premium the carrier gave you). If the price goes up with age, check the age box.
- Fastest: **Paste table from Excel or PDF**. Copy the rows of the carrier's rate table and Deskte lays them out; it tells you how many rows it understood.
- **Try a price** lets you check the table came out right.
![The plans and the Paste table from Excel or PDF button](ayuda/catalogo-06-precios-en.webp)

### Part 2 · Brochures

### Step 7 · To enroll → «Client brochures»
- Click **Upload PDF** and pick the brochure. You can also paste a **link** to the PDF with its name and click **Add**.
- Give it a clear name («Brochure in English», «Folleto en español»).
- These brochures go out with the quote and the client opens them from the link.
- **Never upload a client's documents** (IDs, filled-in applications): everything in the catalog is visible to the whole agency.
- Above it, in **Data to collect from the client to enroll**, list what the carrier needs (photo ID, height and weight…). They get checked off at the close of a consultation.
![The Client brochures section with the Upload PDF button](ayuda/catalogo-07-folletos-en.webp)

### Step 8 · Agent only
Commission, a warning shown in yellow on the list, and internal notes. **Nothing in this tab is shown or sent to the client.**
![The Agent only tab](ayuda/catalogo-08-agente-en.webp)

### Step 9 · «Active» and «Save product»
Leave **Active — the quoter may recommend it** checked and save. If something required is missing, Deskte says so in yellow above the buttons.
![The Active checkbox and the Save product button](ayuda/catalogo-09-guardar-en.webp)

### Part 3 · Quote with it

### Step 10 · In a quote: the «Catalog» button
In **Quotes → New Quote** (or when editing one), under **Plan options**, click **Catalog**.
![The Catalog button inside a quote](ayuda/catalogo-10-en-cotizacion-en.webp)

### Step 11 · Pick the plans and add them
Check the plans you'll offer and click **Add plan(s)**. They come in as options with what they cover, what they don't, and the brochures. The price fills in only when the product has a rate table and the quote has a client with a date of birth; otherwise, type the premium the carrier gave you.
![A plan checked and the Add plan(s) button](ayuda/catalogo-11-marcar-en.webp)

### In Consultations
Nothing else to do. In the **What to offer** step, Deskte recommends the **active** products in your catalog that can be sold to that client (by state, age and health). See the guide **Guided consultation: step by step**.`,
    },
  },
  { id: 'quotes-invoices', cat: 'sales', nav: ['quotes_invoices', 'invoices'],
    title: { es: 'Cotizaciones y Facturas', en: 'Quotes & Invoices' },
    body: {
      es: `### 📋 Cotizaciones
Propuestas con varias opciones de plan que el cliente compara y acepta desde un enlace público.

![Cotizaciones](ayuda/quotes-es.webp)

1. **"Nueva cotización"**: Título, cliente (o nombre y correo del destinatario), "Válida hasta", notas.
2. **"Agregar opción"** por cada plan: tipo, aseguradora, nombre del plan*, prima mensual*, fecha efectiva, deducible, copago, máximo de bolsillo y red.
3. Opcional: **adjunta** un PDF o imagen comparativa (hasta 25 MB).
4. **Crear** → **Enviar** (manda un enlace único). Si el correo falla, copia el enlace a mano.
5. Sigue el estado: BORRADOR → ENVIADA → VISTA → ACEPTADA.
6. Cuando acepta: **"Convertir a póliza"** (o a factura).

### 💰 Facturas
Cobro completo: conceptos, envío con enlace de pago, PDF y control de vencimientos.

![Facturas](ayuda/invoices-es.webp)

1. **"Nueva Factura"**: cliente (o externo), número automático (INV-AAAA-####), fecha de emisión y de vencimiento (+14 días).
2. Agrega **ítems** (descripción, cantidad, precio); puedes "Insertar desde póliza".
3. En el panel lateral: **descuento** e **impuesto (%)**; el subtotal y el total se recalculan en vivo.
4. **Notas** y **Términos** salen en el PDF.
5. **Guardar** (queda en BORRADOR y editable) → **Enviar** (enlace público; el cliente paga con tarjeta y se marca PAGADA).
6. **"Registrar pago"** manual (tarjeta, efectivo, cheque, transferencia, Zelle, otro), incluso parcial.

**Consejos:**
- Si nadie acepta la cotización, no se crea ninguna póliza sola.
- Edita la factura mientras esté en BORRADOR; una vez enviada, mejor duplícala (por auditoría).
- Arriba tienes los totales: facturado, cobrado, pendiente y vencido.`,
      en: `### 📋 Quotes
Proposals with several comparable plan options that the client reviews and accepts from a public link.

![Quotes](ayuda/quotes-en.webp)

1. **"New quote"**: Title, client (or the recipient's name and email), "Valid until", notes.
2. **"Add option"** per plan: type, carrier, plan name*, monthly premium*, effective date, deductible, copay, out-of-pocket max and network.
3. Optional: **attach** a comparison PDF or image (up to 25 MB).
4. **Create** → **Send** (it mails a unique link). If the email fails, copy the link manually.
5. Track the status: DRAFT → SENT → VIEWED → ACCEPTED.
6. Once accepted: **"Convert to policy"** (or to an invoice).

### 💰 Invoices
Full billing: line items, sending with a payment link, PDF and due-date tracking.

![Invoices](ayuda/invoices-en.webp)

1. **"New Invoice"**: client (or external), automatic number (INV-YYYY-####), issue and due dates (+14 days).
2. Add **items** (description, quantity, price); you can "Insert from policy".
3. In the side panel: **discount** and **tax (%)**; subtotal and total recalculate live.
4. **Notes** and **Terms** appear on the PDF.
5. **Save** (stays a DRAFT and editable) → **Send** (public link; the client pays by card and it's marked PAID).
6. **"Record payment"** manually (card, cash, check, transfer, Zelle, other), partial payments included.

**Tips:**
- If nobody accepts the quote, no policy is created on its own.
- Edit the invoice while it's a DRAFT; once sent, duplicate it instead (for the audit trail).
- The KPIs on top: invoiced, collected, outstanding and overdue.` } },
  { id: 'recurring-invoices', cat: 'sales', nav: ['recurring_invoices'],
    title: { es: 'Facturas Recurrentes', en: 'Recurring Invoices' },
    body: {
      es: `Reglas que generan facturas solas en un calendario fijo (retainers, rentas, cuotas).

### Cómo
1. **"Nueva regla"**: nombre interno, cliente y título de la factura.
2. **Frecuencia** (diaria, semanal, mensual, anual), **cada N**, hora, y día de la semana o del mes.
3. Agrega las **líneas de concepto**; verás el total por período.
4. **Términos:** Net (días para vencer) e impuesto (%).
5. **Guardar** — queda ACTIVA. Después: **⚡ Generar ahora**, Pausar / Reanudar, Editar o Eliminar.

**Consejos:**
- Las facturas generadas aparecen en **Facturas** como BORRADOR: revísalas antes de enviarlas.
- Editar la regla solo afecta a las facturas futuras.`,
      en: `Rules that generate invoices by themselves on a fixed schedule (retainers, rents, installments).

### How
1. **"New rule"**: internal name, client and the invoice title.
2. **Frequency** (daily, weekly, monthly, yearly), **every N**, time, and day of week or month.
3. Add the **line items**; you'll see the total per period.
4. **Terms:** Net (days to due) and tax (%).
5. **Save** — it goes ACTIVE. After that: **⚡ Generate now**, Pause / Resume, Edit or Delete.

**Tips:**
- Generated invoices land in **Invoices** as DRAFTS: review them before sending.
- Editing the rule only affects future invoices.` } },
  { id: 'requests-signatures', cat: 'sales', nav: ['requests', 'signatures'],
    title: { es: 'Solicitudes de Documentos y Firmas', en: 'Document Requests & Signatures' },
    body: {
      es: `### 📋 Solicitudes de Documentos
Un portal seguro donde el cliente sube sus documentos con la cámara o desde su galería.

![Solicitudes de documentos](ayuda/requests-es.webp)

1. **"Nueva solicitud"** → elige el cliente (los documentos se pre-llenan según sus pólizas).
2. Agrega o quita documentos (hay atajos: Identificación, Comprobante de Domicilio, Ingresos…) y marca cuáles son **requeridos**.
3. Elige el canal: **Email** (automático), **SMS** o **WhatsApp** (se abre tu app y tú envías).
4. **"Enviar solicitud"** genera el enlace.
5. Sigue el progreso por documento: ⏳ Pendiente · ✅ Recibido · 🔴 Vencido.
6. En **Plantillas** (⚙️) defines qué documentos se piden por tipo de póliza.

**El enlace caduca a los 14 días.** También puedes marcar un documento como recibido a mano si te llegó por otro lado.

### ✍️ Firmas
Firma electrónica válida bajo ESIGN/UETA: el cliente firma y Deskte genera el PDF firmado.

![Firmas](ayuda/signatures-es.webp)

1. **Plantillas** → **Nueva plantilla**: nombre (EN/ES), categoría y cuerpo (EN/ES) con variables ({{firstName}}, {{date}}, {{signature}}…). El botón 🤖 traduce.
2. Si quieres que el cliente **conteste algo**, usa el bloque verde **"Preguntas — las responde el CLIENTE al firmar"**: escribe la pregunta, elige el tipo (**Sí / No**, opción única, lista, casilla o texto corto) y marca **Obligatoria** si no puede saltársela.
3. **Solicitudes** → **Nueva solicitud**: plantilla, destinatario, canal (Email/SMS/WhatsApp) y, si quieres, un PDF adjunto.
4. El cliente abre el enlace, contesta, firma con el dedo o el mouse y acepta. Pasa a **FIRMADA** ✅ y puedes **descargar el PDF** con sello de tiempo y las respuestas impresas.

**Consejos:**
- **Los campos NO son las preguntas.** Los campos de arriba los rellena **el agente** antes de enviar; las preguntas del bloque verde las contesta **el cliente**.
- Las preguntas quedan congeladas al enviar: editar la plantilla después no cambia lo que alguien ya firmó.
- Las respuestas se guardan **cifradas** y no se ven volviendo a abrir el enlace: salen en el PDF.
- La solicitud caduca a los 14 días si no firma.

### 🍑 Consentimiento de Georgia Access (ya listo)
En **Plantillas** ya tienes **«Consentimiento Georgia Access (inglés)»** y **«(español)»**: el formulario oficial, con todo colocado.
- **Arriba se llena solo:** nombre, teléfono y correo del cliente (de su ficha) y nombre / agencia, NPN, teléfono y correo del agente (de su perfil; si envía un asistente, los de su agente). Lo que falte en la ficha o el perfil sale en blanco.
- El cliente firma **una vez** y su firma y la fecha salen en los **6 renglones** (puntos 1 al 5 y «Opcional»).
- En **«Opcional»** el cliente elige al firmar: **solo el agente** o **el agente y cualquier miembro de su agencia**, y sale una **X** en esa casilla.
- Para usarla: **Solicitudes → Nueva solicitud** y elige la plantilla en el idioma del cliente. Puedes mover los sitios desde la plantilla si hace falta; si la borras, no vuelve a aparecer.`,
      en: `### 📋 Document Requests
A secure portal where the client uploads their documents with the camera or from their gallery.

![Document requests](ayuda/requests-en.webp)

1. **"New request"** → pick the client (documents are pre-filled from their policies).
2. Add or remove documents (there are quick presets: ID, Proof of Address, Income…) and mark which are **required**.
3. Pick the channel: **Email** (automatic), **SMS** or **WhatsApp** (your app opens and you send).
4. **"Send request"** generates the link.
5. Track progress per document: ⏳ Pending · ✅ Received · 🔴 Overdue.
6. Under **Templates** (⚙️) you define which documents are asked for per policy type.

**The link expires after 14 days.** You can also mark a document received by hand if it reached you another way.

### ✍️ Signatures
E-signature valid under ESIGN/UETA: the client signs and Deskte generates the signed PDF.

![Signatures](ayuda/signatures-en.webp)

1. **Templates** → **New template**: name (EN/ES), category and body (EN/ES) with variables ({{firstName}}, {{date}}, {{signature}}…). The 🤖 button translates.
2. If you want the client to **answer something**, use the green **"Questions — answered by the CLIENT when signing"** block: type the question, pick the type (**Yes / No**, single choice, dropdown, checkbox or short text) and mark it **Required** if they can't skip it.
3. **Requests** → **New request**: template, recipient, channel (Email/SMS/WhatsApp) and, if you like, an attached PDF.
4. The client opens the link, answers, signs with finger or mouse and accepts. It turns **SIGNED** ✅ and you can **download the PDF** with a timestamp and the answers printed on it.

**Tips:**
- **Fields are NOT questions.** The fields at the top are filled by **the agent** before sending; the green block's questions are answered by **the client**.
- Questions are frozen at send time: editing the template later doesn't change what someone already signed.
- Answers are stored **encrypted** and can't be seen by reopening the link: they come out on the PDF.
- The request expires after 14 days if they don't sign.

### 🍑 Georgia Access Consumer Consent (ready to use)
Under **Templates** you already have **"Georgia Access Consumer Consent (English)"** and **"(Spanish)"**: the official form, with everything placed.
- **The top fills itself:** the client's name, phone and email (from their profile) and the agent's name / agency, NPN, phone and email (from their profile; if a seat sends it, their agent's). Anything missing comes out blank.
- The client signs **once** and their signature and the date go on all **6 lines** (items 1 to 5 and "Optional").
- In **"Optional"** the client chooses when signing: **the agent only** or **the agent and any member of their agency**, and an **X** goes in that box.
- To use it: **Requests → New request** and pick the template in the client's language. You can move the spots from the template if needed; if you delete it, it won't come back.` } },
  { id: 'tickets', cat: 'sales', nav: ['tickets'],
    title: { es: 'Tickets (casos de clientes)', en: 'Tickets (client cases)' },
    body: {
      es: `Para que ninguna petición de un cliente — cambio de póliza, cancelación, facturación, un claim — se pierda ni se quede sin dueño.

### Cómo
1. **"Nuevo ticket"**: eliges cliente, tipo y prioridad.
2. Se asigna solo a quien atiende a ese cliente; lo ves en la columna **"Atiende"**.
3. Dentro tienes el hilo de conversación, el campo de **resolución** ("cómo se resolvió") y notas libres. Puedes adjuntar archivos o documentos del cliente.
4. **"Personalizar"** te deja crear tus estados y tipos con color, y editar los avisos automáticos al cliente.

**Consejos:**
- Hay dos correos automáticos: el **acuse** al abrir el ticket y el **aviso de cierre** al marcarlo Resuelto o Cerrado. Los dos se editan desde "Personalizar".
- Solo **borrar** un ticket exige un permiso especial; verlos y trabajarlos, no.`,
      en: `So no client request — policy change, cancellation, billing, a claim — gets lost or ends up ownerless.

### How
1. **"New ticket"**: pick the client, type and priority.
2. It auto-assigns to whoever handles that client; you see it in the **"Handled by"** column.
3. Inside there's the conversation thread, the **resolution** field ("how it was resolved") and free notes. You can attach files or the client's documents.
4. **"Customize"** lets you create your own colored statuses and types, and edit the automatic notices to the client.

**Tips:**
- Two automatic emails exist: the **acknowledgement** when the ticket opens and the **closing notice** when you mark it Resolved or Closed. Both are edited under "Customize".
- Only **deleting** a ticket needs a special permission; viewing and working them doesn't.` } },
  { id: 'resources', cat: 'sales', nav: ['resources'],
    title: { es: 'Recursos (biblioteca para clientes)', en: 'Resources (library for clients)' },
    body: {
      es: `Documentos, videos y guías que compartes con tus clientes por un portal público.

### Cómo
1. **"Agregar Recurso"**: Título, Descripción, Tipo (video, PDF, imagen, documento), tipo de seguro, aseguradora, categoría, y el archivo o una URL (YouTube o Vimeo).
2. Comparte con clientes concretos (casillas) o con todos.
3. **"Generar Portal Link"**: eliges un cliente (puedes filtrar por aseguradora), se genera el enlace y se lo mandas.
4. Filtra por tipo, aseguradora, categoría o búsqueda.

**Consejos:**
- Un recurso **sin aseguradora** es material de entrenamiento del equipo: no sale en los portales de clientes.
- Cada cliente ve **solo** lo que compartiste con él.`,
      en: `Documents, videos and guides you share with your clients through a public portal.

### How
1. **"Add Resource"**: Title, Description, Type (video, PDF, image, document), insurance type, carrier, category, and the file or a URL (YouTube or Vimeo).
2. Share with specific clients (checkboxes) or with everyone.
3. **"Generate Portal Link"**: pick a client (you can filter by carrier), the link is generated and you send it over.
4. Filter by type, carrier, category or search.

**Tips:**
- A resource **with no carrier** is team training material: it never appears in client portals.
- Each client sees **only** what you shared with them.` } },
  { id: 'sheets', cat: 'sales', nav: ['sheets'],
    title: { es: 'Hojas (tablas dentro de Deskte)', en: 'Sheets (spreadsheets inside Deskte)' },
    body: {
      es: `Tablas tipo hoja de cálculo, compartidas con todo el equipo. Pueden estar en blanco o llenarse solas con datos del CRM. Sirven para llevar cualquier control que el CRM no traiga de fábrica, sin irte a Excel.

### Cómo
1. **"Nueva hoja"**.
2. Elige la **base** (de dónde salen las filas): en blanco, o del CRM (Pólizas, Clientes, Agente/Equipo, Licencias), y marca las **columnas del CRM** que se llenan solas.
3. Agrega tus propias columnas con **"Columna mía"** y filas con el botón **"Fila"**.
4. **Importar Excel/CSV** para traer datos y exportar para llevártelos. **"Actualizar clientes"** refresca lo que viene del CRM.

**⚠️ Importante:**
- Las hojas están pensadas **sin datos de salud**, a propósito. **No metas ahí PHI** (diagnósticos, medicamentos, SSN).
- Las ve y las edita **todo el equipo**: lo que uno borra desaparece para todos.`,
      en: `Spreadsheet-style tables shared with the whole team. They can start blank or fill themselves from CRM data. Use them for any tracking the CRM doesn't ship with, without leaving for Excel.

### How
1. **"New sheet"**.
2. Choose the **base** (where the rows come from): blank, or from the CRM (Policies, Clients, Agent/Team, Licenses), and tick the **CRM columns** that fill themselves.
3. Add your own columns with **"My column"** and rows with the **"Row"** button.
4. **Import Excel/CSV** to bring data in, and export to take it out. **"Refresh clients"** updates what comes from the CRM.

**⚠️ Important:**
- Sheets are designed **without health data**, on purpose. **Don't put PHI in them** (diagnoses, medications, SSN).
- **The whole team** sees and edits them: what one person deletes disappears for everyone.` } },

  // ── Reportes y Análisis ───────────────────────────────────────────────────
  { id: 'commissions-reports', cat: 'reports', nav: ['reports'],
    title: { es: 'Comisiones (importar de la aseguradora)', en: 'Commissions (import from the carrier)' },
    body: {
      es: `Sube el archivo que te manda la aseguradora y Deskte lo empareja con tus clientes por el nombre.

![Comisiones](ayuda/commissions-es.webp)

### Cómo
1. Pestaña **UPLOAD**: arrastra el archivo (Excel, CSV o PDF). El sistema detecta el formato.
2. Revisa las coincidencias: ✅ Emparejado / ❌ No está en el CRM, con su **puntaje de coincidencia**.
3. Para las filas sin emparejar, usa **"Assign…"** y elige el cliente a mano.
4. **IMPORT** para guardar.
5. Pestaña **Tracker**: vista por mes de pagado / no pagado / no está en el CRM, filtrable por mes, tipo de póliza y aseguradora.

**Consejos:**
- Acepta PDF, pero conviene convertirlo a Excel antes (por ejemplo en ilovepdf.com) para que empareje mejor.
- El emparejado automático necesita **nombre y apellido** y una coincidencia de al menos 65%.
- Si creas al cliente después, las filas de "No está en el CRM" se mueven solas a "Pagado".`,
      en: `Upload the file the carrier sends you and Deskte matches it against your clients by name.

![Commissions](ayuda/commissions-en.webp)

### How
1. **UPLOAD** tab: drag the file in (Excel, CSV or PDF). The system detects the format.
2. Review the matches: ✅ Matched / ❌ Not in CRM, with a **match score**.
3. For unmatched rows use **"Assign…"** and pick the client by hand.
4. **IMPORT** to save.
5. **Tracker** tab: a monthly view of paid / unpaid / not in CRM, filterable by month, policy type and carrier.

**Tips:**
- PDF works, but converting it to Excel first (for example at ilovepdf.com) matches far better.
- Automatic matching needs **first and last name** and at least a 65% match.
- If you create the client later, the "Not in CRM" rows move to "Paid" on their own.` } },
  { id: 'commissions-by-policy', cat: 'reports', nav: ['commissions_table'],
    title: { es: 'Comisiones por Póliza', en: 'Commissions by Policy' },
    body: {
      es: `Todas las pólizas de tu libro y la comisión que deja cada una, calculada según el tipo de póliza.

### Cómo
1. Filtra por **tipo de póliza**, **compañía** y año. La **Prima (cliente)** se trae sola de la póliza.
2. **Salud:** escribe el **$ por persona** → total = $ por persona × personas × % Split.
3. **Vida y Gastos Finales:** escribe el **% de la prima** → total del primer año = prima × 12 × % × % Split, con el **adelanto** (9 meses si no cambias **Meses de adelanto**) y lo que queda por mes.
4. **Las demás:** escribe el **% de la prima** → total = prima × % × % Split, **una sola vez**.
5. **% Split** y **Razón** (Split / Agencia) van en todas; en blanco es 100%.

**Consejo:** es la MISMA comisión del **Reporte de Ventas**: lo que llenes aquí sale allá y al revés. Un asistente necesita permiso de ver comisiones para entrar.`,
      en: `Every policy in your book and the commission it earns, computed by policy type.

### How
1. Filter by **policy type**, **carrier** and year. The **Premium (client)** comes from the policy.
2. **Health:** enter the **$ per person** → total = $ per person × people × Split %.
3. **Life and Final Expense:** enter the **% of premium** → first-year total = premium × 12 × % × Split %, with the **advance** (9 months unless you change **Advance months**) and what's left per month.
4. **Everything else:** enter the **% of premium** → total = premium × % × Split %, **one time**.
5. **Split %** and **Reason** (Split / Agency) apply to every row; blank means 100%.

**Tip:** it's the SAME commission as the **Sales Report**: what you fill here shows there and vice versa. An assistant needs commission-view permission to open this.` } },
  { id: 'commissions-by-analyst', cat: 'reports', nav: ['analyst_commissions'],
    title: { es: 'Comisiones por Analista', en: 'Commissions by Analyst' },
    body: {
      es: `Lo que se le paga a cada analista por sus ventas, con las reglas que pones tú y **por tipo de póliza**. No es la comisión de la póliza. Se llena solo: cuenta cada póliza **activa** que tiene **Analista** (quien la vendió) y **fecha de venta**. Las canceladas, vencidas o pendientes no salen ni suman.

### Quién ve qué
- **Agente o dueña de la agencia:** ve a todos los analistas, incluida ella misma si se pone como analista.
- **Asiento (asistente):** ve **solo sus ventas** y lo que le toca. No ve las de sus compañeros ni lo que se les paga. Solo le sale el módulo si en **Equipo → Permisos → Qué reportes puede ver** tiene marcada **Comisiones por Analista**.
- Una póliza es de un asiento si en **Analista** dice su nombre como en Deskte. Si lo escriben distinto (por ejemplo solo «Victor»), la agente lo vincula en las reglas con **Su usuario de Deskte**.

### Las reglas (solo agente o dueña)
1. Toca **Reglas de comisión**.
2. En **⭐ Tabla general** pon cuánto se paga **por tipo de póliza** (por ejemplo Salud $25, Vida $50). Con **Agregar tipo** sumas más tipos. Lo que no tenga tipo se paga con **Cualquier otro tipo de póliza**.
3. A cada tipo le puedes poner **niveles**: «desde 10 pólizas → $35».
4. Si a un analista le pagas distinto, elígelo en **¿Para quién?** y ponle sus propios montos. Los suyos ganan sobre la tabla general.
5. **Precios especiales por compañía o fuente de lead:** por ejemplo Salud + Ambetter $40, o Salud + Facebook $15. Lo que dejes en «Cualquier…» vale para todo. Si no pones compañía, vale el precio del tipo; si pones una compañía, esa compañía tiene su precio y las demás siguen con el del tipo. Si una póliza cumple varias líneas, gana la más detallada.
6. **Pagar por miembro:** marca la casilla en el tipo o línea que se paga por persona (por ejemplo Salud). Se multiplica por las personas aseguradas de la póliza; si no tiene ninguna anotada, cuenta 1. Los niveles se siguen contando por pólizas.
7. **Ajustes propios:** sumar o restar un monto o un %, o multiplicar, cuando se cumplen unas condiciones (tipo, compañía, fuente). Ej.: «Fuente Facebook → Sumar 10%». Se aplican todos los que coincidan y nunca bajan de $0.
8. **% de la prima:** en Ajustes propios, **Pagar % de la prima** hace que la comisión sea ese porcentaje de la prima (mensual o anualizada), aunque no haya precio puesto; **Sumar % de la prima** lo suma a lo que ya se paga. Ej.: «Salud → Pagar 5% de la prima anualizada». La prima es la de la ficha de la póliza.
9. **Reglas guardadas:** en **¿Para quién?** toca **+ Nueva regla guardada**, ponle nombre (Regla 1, Regla 2…), arma los montos y marca los **analistas que la usan**. Así no repites la misma regla para cada uno. Cada analista está en una sola; lo **individual** de un analista sigue ganando sobre su regla guardada, y la regla guardada gana sobre la tabla general.

### El reporte
Tiene el mismo formato que el **Reporte de Ventas**:
- **Filtros:** fechas de venta (con atajos Este mes, Mes pasado y Este año), tipo de póliza, compañía y analista.
- **Tarjetas** con las ventas y el total a pagar.
- **Gráfico** como el del Reporte de Ventas: agrupa por analista, tipo, compañía, agente o mes, y cuenta pólizas o suma la comisión del analista.
- **Resumen por analista**, con lo de cada tipo.
- **Detalle de ventas** con la regla que se usó y lo que se paga por cada póliza.
- Exportar a **Excel** o **CSV**.

**Consejos:**
- Los niveles se cuentan **por mes y por tipo**: 12 de Salud con «desde 10 → $35» se pagan las 12 a $35 = $420. Las de Vida cuentan aparte.
- Un filtro no cambia lo que se paga: el nivel siempre se calcula con todas las ventas activas del mes.
- Si una póliza dice **Sin regla**, ese tipo todavía no tiene monto: agrégalo en la tabla general.`,
      en: `What each analyst earns from their sales, with the rules you set **by policy type**. It is not the policy's commission. It fills in by itself: it counts every **active** policy that has an **Analyst** (who sold it) and a **sale date**. Cancelled, expired or pending ones don't show or count.

### Who sees what
- **Agent or agency owner:** sees every analyst, including themselves when they're set as the analyst.
- **Seat (assistant):** sees **only their own sales** and what they earn. They don't see their teammates' sales or pay. The module only shows if **Team → Permissions → Which reports they can see** has **Commissions by Analyst** ticked.
- A policy belongs to a seat when its **Analyst** field has their name as in Deskte. If it's written differently (for example just "Victor"), the agent links it in the rules with **Their Deskte user**.

### The rules (agent or owner only)
1. Tap **Commission rules**.
2. In **⭐ General table** set how much is paid **per policy type** (for example Health $25, Life $50). **Add type** adds more. Anything without a type is paid with **Any other policy type**.
3. Each type can have **levels**: "from 10 policies → $35".
4. If you pay an analyst differently, pick them in **For whom?** and set their own amounts. Theirs win over the general table.
5. **Special prices by carrier or lead source:** for example Health + Ambetter $40, or Health + Facebook $15. Anything left as "Any…" applies to all. With no carrier, the type price applies; with a carrier, that carrier gets its own price and the rest keep the type price. If a policy matches several lines, the most detailed one wins.
6. **Pay per member:** tick it on the type or line paid per person (for example Health). It's multiplied by the policy's insured people; 1 if none is listed. Levels still count policies.
7. **Custom adjustments:** add or subtract an amount or a %, or multiply, when some conditions match (type, carrier, lead source). E.g.: "Lead source Facebook → Add 10%". Every matching one applies and never goes below $0.
8. **% of premium:** in Custom adjustments, **Pay % of premium** makes the commission that percentage of the premium (monthly or annualized), even with no price set; **Add % of premium** adds it to what's already paid. E.g.: "Health → Pay 5% of annualized premium". The premium is the one on the policy card.
9. **Saved rules:** in **For whom?** tap **+ New saved rule**, name it (Rule 1, Rule 2…), set the amounts and tick the **analysts using it**. No need to repeat the same rule for each one. Each analyst is in one only; an analyst's **individual** rules still win over their saved rule, and the saved rule wins over the general table.

### The report
It has the same format as the **Sales Report**:
- **Filters:** sale dates (with shortcuts This month, Last month and This year), policy type, carrier and analyst.
- **Cards** with sales and the total to pay.
- A **chart** like the Sales Report's: group by analyst, type, carrier, agent or month, and count policies or add up the analyst commission.
- **Summary by analyst**, broken down by type.
- **Sales detail** with the rule used and what each policy pays.
- Export to **Excel** or **CSV**.

**Tips:**
- Levels count **per month and per type**: 12 Health sales with "from 10 → $35" pay all 12 at $35 = $420. Life counts separately.
- A filter never changes what is paid: the level is always worked out with all of the month's active sales.
- If a policy says **No rule**, that type has no amount yet: add it to the general table.` } },
  { id: 'report-dashboards', cat: 'reports',
    title: { es: 'Paneles de Reportes (tableros interactivos)', en: 'Report Dashboards (interactive boards)' },
    body: {
      es: `Tu tablero: varias gráficas en una pantalla — pólizas, prima, comisión, clientes y miembros — con filtros de fecha, personas y estados. Se actualiza solo.

### Cómo
1. Menú → **Paneles de Reportes** → **Nuevo panel**.
2. Toca **Plantilla profesional** para empezar con indicadores, una línea por mes y gráficas por agente, analista, compañía, tipo y estado. O **Agregar** una por una.
3. En cada gráfica eliges: **qué medir**, **agrupar por** (agente, analista, estado, compañía, tipo, estatus o mes), la **forma** (indicador, barras, línea, dona o tabla), el **tamaño**, la **fecha que cuenta** (venta, efectiva o renovación), el **rango** (el del panel o uno propio) y sus **filtros** propios.
4. Al abrirlo, la barra de arriba filtra TODAS las gráficas a la vez (fechas, agente, analista, estado, tipo, compañía, estatus).
5. **Clic en una barra, un pedazo o una fila** para ver las pólizas de ese grupo; toca el cliente para abrirlo.

**Consejos:**
- Los indicadores comparan con el **período anterior** del mismo largo (sube ▲ verde, baja ▼ rojo).
- La comisión es la MISMA de Comisiones por Póliza.
- Las gráficas de pólizas cambian solas cuando alguien guarda una venta; los reportes del servidor se vuelven a pedir cada 5 minutos (se cambia arriba).
- El correo programado lleva los reportes del servidor; las gráficas de pólizas se ven solo en el panel.`,
      en: `Your board: several charts on one screen — policies, premium, commission, clients and members — filtered by date, people and states. It refreshes by itself.

### How
1. Menu → **Report Dashboards** → **New dashboard**.
2. Tap **Pro template** to start with KPIs, a monthly line and charts by agent, analyst, carrier, type and state. Or **Add** them one by one.
3. For each chart pick: **what to measure**, **group by** (agent, analyst, state, carrier, type, status or month), the **shape** (KPI, bars, line, donut or table), the **size**, the **date that counts** (sale, effective or renewal), the **range** (the panel's or its own) and its own **filters**.
4. When open, the bar at the top filters ALL charts at once (dates, agent, analyst, state, type, carrier, status).
5. **Click a bar, slice or row** to see that group's policies; tap the client to open it.

**Tips:**
- KPIs compare against the **previous period** of the same length (up ▲ green, down ▼ red).
- Commission is the SAME as Commissions by Policy.
- Policy charts change by themselves when someone saves a sale; server reports are re-fetched every 5 minutes (change it at the top).
- The scheduled email carries the server reports; policy charts are shown on the panel only.` } },
  { id: 'analytics', cat: 'reports', nav: ['reports_analytics'],
    title: { es: 'Análisis (Analytics)', en: 'Analytics' },
    body: {
      es: `Gráficos y números de tu negocio: ingresos por mes, comisiones, clientes nuevos, conversión de leads y tus mejores clientes.

### Cómo
1. Menú → **Análisis**.
2. Elige el período (3, 6 o 12 meses).
3. Lee los gráficos y las tarjetas.

**Consejos:**
- Todo se calcula en vivo desde tus datos: no hay nada que actualizar a mano.
- Para llevártelo en PDF, usa Imprimir del navegador.`,
      en: `Charts and numbers for your business: revenue by month, commissions, new clients, lead conversion and your top clients.

### How
1. Menu → **Analytics**.
2. Pick the period (3, 6 or 12 months).
3. Read the charts and cards.

**Tips:**
- Everything is calculated live from your data: nothing to refresh by hand.
- To take it away as a PDF, use the browser's Print.` } },
  { id: 'sales-report', cat: 'reports', nav: ['sales_report'],
    title: { es: 'Reporte de Ventas', en: 'Sales Report' },
    body: {
      es: `Cada póliza cuenta como una venta. Contesta *"¿quién vendió qué, de qué compañía y cuánto dejó?"* en un mes o en un rango.

![Reporte de ventas](ayuda/sales-report-es.webp)

### Cómo
1. **Filtros:** tipo, compañía, analista, quién procesó, agente y rango de fecha de venta. **"Limpiar filtros"** reinicia todo.
2. **"Mostrar columnas"** decide qué se ve: cliente, tipo, compañía, comisión, plan, fecha de venta, analista, agente, fecha activa y fecha de expiración.
3. **"Agrupar por"** (analista, tipo, compañía, quién procesó, agente, plan o mes de venta) dibuja la gráfica.
4. Exporta a Excel o CSV.

**Consejo:** el reporte se arma con las pólizas que ya tienes en pantalla. Si tu lista está filtrada, el reporte también.`,
      en: `Every policy counts as a sale. It answers *"who sold what, from which carrier, and what did it earn?"* for a month or a range.

![Sales report](ayuda/sales-report-en.webp)

### How
1. **Filters:** type, carrier, analyst, who processed it, agent and sale-date range. **"Clear filters"** resets everything.
2. **"Show columns"** decides what's visible: client, type, carrier, commission, plan, sale date, analyst, agent, active date and expiration date.
3. **"Group by"** (analyst, type, carrier, processed by, agent, plan or sale month) draws the chart.
4. Export to Excel or CSV.

**Tip:** the report is built from the policies already on screen. If your list is filtered, so is the report.` } },
  { id: 'client-reports', cat: 'reports', nav: ['client_reports'],
    title: { es: 'Reportes de Clientes', en: 'Client Reports' },
    body: {
      es: `Búsquedas ya hechas: cumpleaños del mes, renovaciones próximas, clientes nuevos, mayores, por estado, por tipo de póliza, sin póliza activa, prospectos…

### Cómo
1. Menú → **Reportes de Clientes**.
2. Elige un reporte y ajusta sus parámetros (días para renovación, estado, edad…).
3. **"RUN REPORT"**.
4. Exporta a **Excel** o **CSV**.

**Consejo:** lee del servidor, así que ves **todos** los que coinciden (no solo la página que tenías abierta). Es la mejor forma de armar la lista para una campaña de Email Masivo.`,
      en: `Ready-made searches: this month's birthdays, upcoming renewals, new clients, seniors, by state, by policy type, without an active policy, prospects…

### How
1. Menu → **Client Reports**.
2. Pick a report and set its parameters (days to renewal, status, age…).
3. **"RUN REPORT"**.
4. Export to **Excel** or **CSV**.

**Tip:** it reads from the server, so you get **every** match (not just the page you had open). It's the best way to build the list for a Mass Email campaign.` } },
  { id: 'team-reports', cat: 'reports', nav: ['team_reports'],
    title: { es: 'Reportes de Equipo', en: 'Team Reports' },
    body: {
      es: `Reportes por persona: producción, horas, leads, tareas, pólizas, actividad, conversión y metas. Tiene dos pestañas.

### Cómo
1. **Pre-armados:** eliges el reporte y el período.
2. **Mis reportes:** creas el tuyo — métrica, columnas, miembros, período y tipo de gráfica. Le pones nombre, lo guardas, lo compartes y lo corres con el ▶.
3. Descargas el resultado.

**Consejos:**
- Solo se guarda la **receta**, no los números: cada vez que lo corres vuelve a consultar, así que nunca ves un dato viejo.
- Los números son **totales del equipo**, sin nombres de clientes.`,
      en: `Per-person reports: production, hours, leads, tasks, policies, activity, conversion and goals. It has two tabs.

### How
1. **Prebuilt:** pick the report and the period.
2. **My reports:** build your own — metric, columns, members, period and chart type. Name it, save it, share it and run it with ▶.
3. Download the result.

**Tips:**
- Only the **recipe** is saved, not the numbers: every run re-queries, so you never look at stale data.
- The numbers are **team totals**, with no client names.` } },
  { id: 'metrics', cat: 'reports', nav: ['metrics'],
    title: { es: 'Métricas (el panel del negocio)', en: 'Metrics (the business panel)' },
    body: {
      es: `Clientes, pólizas, prima, comisiones, gastos, conversión de leads, salud de las renovaciones, mezcla de productos y mejores aseguradoras.

### Cómo
1. Menú → **Métricas**.
2. Elige el período (YTD, Q1–Q4, todo) y la pestaña: Resumen / Crecimiento / Equipo / Financiero.
3. Lee las tarjetas y los gráficos.

**Consejos:**
- Un agente ve lo suyo; el dueño y los admins ven el equipo.
- Si ves "renovaciones expirando", ve a **Pólizas** y filtra por renovación.`,
      en: `Clients, policies, premium, commissions, expenses, lead conversion, renewal health, product mix and top carriers.

### How
1. Menu → **Metrics**.
2. Pick the period (YTD, Q1–Q4, All) and the tab: Overview / Growth / Team / Financial.
3. Read the cards and charts.

**Tips:**
- An agent sees their own; owners and admins see the team.
- If you see "renewals expiring", go to **Policies** and filter by renewal.` } },

  // ── Negocio y Herramientas ────────────────────────────────────────────────
  { id: 'workflows', cat: 'business', nav: ['workflows', 'automations'],
    title: { es: 'Automatizaciones ("cuando pase X, haz Y")', en: 'Automations ("when X happens, do Y")' },
    body: {
      es: `Reglas que trabajan por ti en el servidor, aunque tengas Deskte cerrado.

![Automatizaciones](ayuda/workflows-es.webp)

### Cómo
1. Menú → **Automatizaciones** → **"New Workflow"**.
2. Ponle nombre y descripción.
3. Elige el **disparador**: lead creado, cliente creado, factura pagada, firma completada, póliza creada.
4. Agrega **acciones**: crear tarea, enviar email, inscribir en una secuencia.
5. **Guardar**. Usa **⚡ Test** para probarla; puedes pausarla y reactivarla cuando quieras.

### AI Automations
Es **el mismo motor**, presentado junto a un chat con IA que te ayuda a pensar la regla. Usa el que te resulte más cómodo: lo que crees en uno funciona igual.

**Consejo:** un buen primer flujo es "cuando llega un lead, crear una tarea de seguimiento para mañana".`,
      en: `Rules that work for you on the server, even with Deskte closed.

![Automations](ayuda/workflows-en.webp)

### How
1. Menu → **Automations** → **"New Workflow"**.
2. Give it a name and description.
3. Pick the **trigger**: lead created, client created, invoice paid, signature completed, policy created.
4. Add **actions**: create task, send email, enroll in a sequence.
5. **Save**. Use **⚡ Test** to try it; pause and resume whenever you like.

### AI Automations
It's **the same engine**, shown next to an AI chat that helps you think the rule through. Use whichever you prefer: what you build in one works the same.

**Tip:** a good first flow is "when a lead arrives, create a follow-up task for tomorrow".` } },
  { id: 'ai-assistant', cat: 'business', nav: ['ai_assistant'],
    title: { es: 'Asistente AI (redactar, resumir, sugerir)', en: 'AI Assistant (draft, summarize, suggest)' },
    body: {
      es: `Tres herramientas con IA. Opcionalmente eliges un cliente y la IA usa su contexto.

### Cómo
1. Menú → **Asistente AI**.
2. **📧 Redactar email:** describe el propósito y el tono (amistoso, formal, urgente) → **Generar** → copia el asunto y el cuerpo.
3. **📄 Resumir texto:** pega un texto largo → **Resumir** → recibes un resumen y los puntos clave.
4. **💡 Sugerir próximo paso:** con un cliente elegido, propone qué hacer con él.

**Consejo:** es una ayuda para escribir más rápido, no una decisión. Lee siempre antes de mandar.`,
      en: `Three AI tools. You can optionally pick a client and the AI uses their context.

### How
1. Menu → **AI Assistant**.
2. **📧 Draft email:** describe the purpose and tone (friendly, formal, urgent) → **Generate** → copy the subject and body.
3. **📄 Summarize text:** paste a long text → **Summarize** → you get a summary plus key points.
4. **💡 Suggest next action:** with a client selected, it proposes what to do next.

**Tip:** it's help to write faster, not a decision. Always read before you send.` } },
  { id: 'deskte-ai', cat: 'business', nav: ['deskte_ai'],
    title: { es: 'Deskte AI (el chat que entiende tu CRM)', en: 'Deskte AI (the chat that knows your CRM)' },
    body: {
      es: `Un asistente de conversación al que le escribes como a una persona. Está en el menú y también en el icono flotante de la barra de arriba, para tenerlo abierto mientras trabajas.

### Para qué sirve
- Calificar leads, redactar seguimientos, contestar dudas y mirar tu pipeline.
- **Proponer cambios en el CRM:** crear o modificar un cliente o una póliza.

### Nunca escribe sola
Cuando propone un cambio, **no lo aplica**: te enseña una tarjeta con lo que haría y solo al pulsar **Confirmar** se guarda — por las mismas reglas y permisos que si lo escribieras tú en el formulario.

Queda un **historial** de lo que se aplicó, y lo que se puede revertir trae su botón de **deshacer**.

### Ponerle nombre
En la pantalla de Deskte AI puedes **renombrarlo** para tu agencia; el nombre nuevo lo ven todos.

**Consejo:** si tienes una ficha abierta, el asistente entiende que hablas de ese cliente. Es la forma más rápida de "agrégale el teléfono a este cliente".`,
      en: `A conversational assistant you write to like a person. It's in the menu and also behind the floating icon in the top bar, so you can keep it open while you work.

### What it's for
- Qualifying leads, drafting follow-ups, answering questions and reviewing your pipeline.
- **Proposing CRM changes:** creating or updating a client or a policy.

### It never writes on its own
When it proposes a change it **doesn't apply it**: it shows a card with what it would do, and only when you press **Confirm** is it saved — through the same rules and permissions as if you had typed it into the form.

A **history** of what was applied is kept, and anything reversible carries an **undo** button.

### Give it a name
On the Deskte AI screen you can **rename** it for your agency; everyone sees the new name.

**Tip:** with a client profile open, the assistant understands you're talking about that client. It's the fastest way to say "add the phone number to this client".` } },
  { id: 'goals-time', cat: 'business', nav: ['goals'],
    title: { es: 'Metas', en: 'Goals' },
    body: {
      es: `Objetivos mensuales, trimestrales o anuales: comisiones en $, pólizas nuevas, clientes nuevos, leads convertidos o prima total. El progreso se calcula solo.

### Cómo
1. Menú → **Metas** → **"Nueva meta"**.
2. Elige el **tipo**, el **número objetivo** y el **período**. Opcionalmente filtra por tipo de póliza y etiqueta.
3. **Crear**. Verás el % completado, el actual, la meta, lo que falta, y un 🏆 al llegar al 100%.

**Consejos:**
- El avance se actualiza en vivo desde comisiones, pólizas y leads.
- Puedes compartir una meta con toda la agencia.`,
      en: `Monthly, quarterly or yearly targets: commissions in $, new policies, new clients, converted leads or total premium. Progress calculates itself.

### How
1. Menu → **Goals** → **"New goal"**.
2. Pick the **type**, the **target number** and the **period**. Optionally filter by policy type and tag.
3. **Create**. You'll see % complete, current, target, what's left, and a 🏆 at 100%.

**Tips:**
- Progress updates live from commissions, policies and leads.
- You can share a goal with the whole agency.` } },
  { id: 'import', cat: 'business', nav: ['import'],
    title: { es: 'Importar datos (Excel, CSV o un PDF de solicitud)', en: 'Import data (Excel, CSV or an application PDF)' },
    body: {
      es: `Importador con IA: mapea las columnas, detecta errores y te avisa **antes** de guardar.

### Clientes y pólizas (Excel/CSV)
1. Menú → **Importar** y arrastra el archivo.
2. Deskte detecta los encabezados y sugiere el **mapeo** de columnas; ajústalo si hace falta.
3. Elige el tipo: **Combinado** (clientes + pólizas), **Solo clientes** o **Solo pólizas**.
4. **Vista previa** → **IMPORTAR**. Las filas con error se marcan en rojo y **no** se importan.

### Completar clientes que ya existen (👤➕)
Para cuando la cartera **ya está migrada** y lo que quedó fuera fueron las notas, o los métodos de pago, o los médicos. **No trae fichas nuevas:** le cuelga UNA COSA a clientes que ya tienes.
1. Menú → **Importar** → tarjeta **👤➕ Completar clientes**.
2. Elige **qué** vas a completar: **Notas**, **Métodos de pago**, **Médicos** o **Citas médicas**. Va primero porque decide qué columnas te pide.
   - **Médicos** y **Citas médicas** se le cuelgan al **titular** de la ficha — una hoja no dice de quién de la casa es cada médico. Desde la pestaña 🩺 se mueven a otra persona en un clic.
   - La **cita** necesita una fecha que se entienda; sin hora se pone a las **9:00 am**. Y queda **sin recordatorio**: los recordatorios se marcan después, cita por cita, para que una importación no le mande correos a media cartera.
3. Sube la hoja (la cabecera tiene que traer el **nombre**, o el correo, o el teléfono, para reconocer al cliente) y revisa el mapeo — lo que se pudo, ya está puesto.
4. **Antes de escribir nada** sale la revisión: *"3 reconocidos · 1 sin reconocer · 1 sin nada que añadir"*, fila por fila y diciendo a qué cliente va cada una. Si dos filas del mismo archivo caen sobre la misma persona, te lo avisa ahí mismo: como todo se **añade**, quedarían duplicadas.
5. De los que **no reconoce**, decides tú: **saltarlos** (es lo de fábrica) o **crearlos como nuevos**. Si los creas, queda una ficha con lo poco que trae la hoja.
6. Al terminar dice cuántas se escribieron y, si alguna falló, **a qué cliente** y por qué.

⚠️ Lo que se importa **se añade, no reemplaza**: subir el mismo archivo dos veces lo duplica. Por eso la revisión no se puede saltar.

### Solicitudes en PDF
Pestaña **APPLICATION PDF**: sube el PDF y el sistema extrae nombre, fecha de nacimiento, dirección, pólizas y familia. Revisa la previsualización e importa (crea o actualiza al cliente).

**Consejos:**
- Cada archivo necesita una **fila de encabezados**. Nombre y apellido son obligatorios.
- Fechas en **AAAA-MM-DD** o **MM/DD/AAAA**; el SSN, 9 dígitos.
- Deskte **no** elimina duplicados solo: revisa tu archivo antes.
- La bóveda de claves, las pólizas y la info de compañías tienen además su propio importador.`,
      en: `An AI importer: it maps the columns, spots errors and warns you **before** saving.

### Clients and policies (Excel/CSV)
1. Menu → **Import** and drag the file in.
2. Deskte detects the headers and suggests the column **mapping**; adjust it if needed.
3. Pick the type: **Combined** (clients + policies), **Clients only** or **Policies only**.
4. **Preview** → **IMPORT**. Rows with errors are flagged red and are **not** imported.

### Complete clients you already have (👤➕)
For when the book is **already migrated** and what got left out were the notes, or the payment methods. **It does not bring new records:** it attaches ONE THING to clients you already have.
1. Menu → **Import** → the **👤➕ Complete clients** card.
2. Pick **what** you are completing: **Notes** or **Payment methods**. It comes first because it decides which columns you are asked for.
3. Upload the sheet (the header needs the **name**, or the email, or the phone, so the client can be recognised) and check the mapping — whatever could be matched is already set.
4. **Before anything is written** you get the review: *"3 recognised · 1 not recognised · 1 with nothing to add"*, row by row, saying which client each one goes to.
5. For the ones it does **not** recognise, you decide: **skip them** (the default) or **create them as new**. If you create them, you get a record with only what the sheet carries.
6. At the end it says how many were written and, if any failed, **which client** and why.

⚠️ What gets imported is **added, not replaced**: uploading the same file twice duplicates it. That is why the review cannot be skipped.

### Application PDFs
**APPLICATION PDF** tab: upload the PDF and the system extracts name, date of birth, address, policies and household. Check the preview and import (it creates or updates the client).

**Tips:**
- Every file needs a **header row**. First and last name are required.
- Dates as **YYYY-MM-DD** or **MM/DD/YYYY**; SSN, 9 digits.
- Deskte does **not** de-duplicate for you: check your file first.
- The password vault, policies and carrier info each have their own importer too.` } },
  { id: 'vault', cat: 'business', nav: ['vault'],
    title: { es: 'Bóveda de Claves', en: 'Password Vault' },
    body: {
      es: `Tu cofre cifrado con las credenciales de los portales de aseguradoras: usuario, contraseña y preguntas de seguridad. Se revelan solo con tu **PIN de 6 dígitos** y se vuelven a ocultar a los 30 segundos.

### Cómo
1. Menú → **Bóveda de Claves** → **"+ Nueva Credencial"**.
2. Llena compañía, URL del portal, email o usuario, contraseña, hasta 3 preguntas de seguridad y notas → **Guardar**.
3. Para revelar: **🔒 Mostrar** → escribe tu **PIN de 6 dígitos** → se muestra 30 segundos → cópialo con 📋.
4. **📥 Importar** desde Excel o CSV; editar y eliminar por fila.

**Consejos:**
- El PIN de 6 dígitos se crea en **Mi Perfil → Seguridad**, y **no** es el PIN de 4 del inicio de sesión.
- Cada revelación queda auditada. Con 3 PINs equivocados se bloquea 15 minutos.
- La Bóveda de Claves es de **toda la agencia**. Para claves solo tuyas usa **Mi Bóveda Personal**.`,
      en: `Your encrypted safe for carrier-portal credentials: username, password and security questions. They're revealed only with your **6-digit PIN** and hide themselves again after 30 seconds.

### How
1. Menu → **Password Vault** → **"+ New Credential"**.
2. Fill in carrier, portal URL, email or username, password, up to 3 security questions and notes → **Save**.
3. To reveal: **🔒 Show** → type your **6-digit PIN** → it shows for 30 seconds → copy with 📋.
4. **📥 Import** from Excel or CSV; edit and delete per row.

**Tips:**
- The 6-digit PIN is created in **My Profile → Security**, and is **not** the 4-digit login PIN.
- Every reveal is audited. Three wrong PINs locks it for 15 minutes.
- The Password Vault is shared by **the whole agency**. For logins only you can see, use **My Personal Vault**.` } },
  { id: 'my_vault', cat: 'business', nav: ['my_vault'],
    title: { es: 'Mi Bóveda Personal', en: 'My Personal Vault' },
    body: {
      es: `Tu cofre de claves **privado**: igual que la Bóveda de Claves —cifrado, **PIN de 6 dígitos**, se oculta a los 30 segundos— pero **solo tú** ves lo que guardas aquí. Ni la dueña de la agencia, ni tus asistentes, ni nadie más.

### Cómo
1. Menú → **Mi Bóveda Personal** → **"+ Nueva entrada"**.
2. En **Tipo** escoge **"✏️ Nuevo personalizado…"** y escribe tu propia categoría (Banco, Correo, Redes…). Aquí no salen los tipos de seguro: las categorías las vas creando tú y se reutilizan.
3. Llena compañía, usuario, contraseña y preguntas de seguridad → **Guardar**.
4. Para revelar: **🔒 Mostrar** → tu **PIN de 6 dígitos**.
5. También puedes **importar** desde Excel o CSV y **exportar** con o sin contraseñas.

**Consejos:**
- La tienen los **agentes y dueños de agencia**. Los asistentes no, porque revelan con el PIN de su agente.
- No se abre en modo "ver como": ni el soporte de Deskte la ve.
- Lo que guardas aquí **no** aparece en la Bóveda de Claves de la agencia.`,
      en: `Your **private** password safe: just like the Password Vault —encrypted, **6-digit PIN**, hides after 30 seconds— but **only you** can see what you keep here. Not the agency owner, not your assistants, nobody else.

### How
1. Menu → **My Personal Vault** → **"+ New entry"**.
2. Under **Type** choose **"✏️ New custom…"** and write your own category (Bank, Email, Social…). Insurance types don't show here: you build your own categories and reuse them.
3. Fill in company, username, password and security questions → **Save**.
4. To reveal: **🔒 Show** → your **6-digit PIN**.
5. You can also **import** from Excel or CSV and **export** with or without passwords.

**Tips:**
- Agents and agency owners have it. Assistants don't, because they reveal with their agent's PIN.
- It never opens in "view as" mode: not even Deskte support sees it.
- What you keep here does **not** appear in the agency Password Vault.` } },
  { id: 'licenses', cat: 'business', nav: ['licenses'],
    title: { es: 'Licencias y Certificaciones', en: 'Licenses & Certifications' },
    body: {
      es: `Tus licencias estatales y certificaciones (AHIP, NABIP…), con aviso antes de que venzan.

### Cómo
1. Menú → **Licencias y Certificaciones** → **+ Nuevo**.
2. Sube el **PDF**: el OCR extrae número, estado, NPN, fechas y tipo. Completa lo que falte.
3. Elige la **categoría** (Vida/Salud, P&C, Excedentes, Ajustador, Título, Certificación), agrega notas y **Guarda**.
4. Si vence en 90 días verás una alerta naranja o roja; usa **Renovar / Subir** para cargar el PDF nuevo.

**Consejos:**
- Las alertas vuelven a aparecer si no renuevas; al subir la fecha nueva se reinician.
- Filtra por Vigente / Vencidas / Por vencer.`,
      en: `Your state licenses and certifications (AHIP, NABIP…), with a warning before they lapse.

### How
1. Menu → **Licenses & Certifications** → **+ New**.
2. Upload the **PDF**: OCR pulls out number, state, NPN, dates and type. Fill in whatever is missing.
3. Pick the **category** (Life/Health, P&C, Surplus, Adjuster, Title, Certification), add notes and **Save**.
4. If it expires within 90 days you'll see an orange or red alert; use **Renew / Upload** to load the new PDF.

**Tips:**
- Alerts come back if you don't renew; uploading the new date resets them.
- Filter by Current / Expired / Expiring.` } },
  { id: 'company-appointments', cat: 'business', nav: ['appointments'],
    title: { es: 'Citas con Compañías (matriz de carriers)', en: 'Company Appointments (carrier matrix)' },
    body: {
      es: `Una matriz **estados × aseguradoras** donde marcas dónde tienes cita abierta, es decir, dónde puedes hacer cambios en línea.

### Cómo
1. Menú → **Citas con Compañías**.
2. Elige una **categoría** (Salud, Medicare Advantage, Dental, Vida, Auto, Hogar…).
3. **Haz clic** en las celdas estado + aseguradora donde tienes cita.
4. **+ Nueva Compañía** o **+ Nueva Categoría** para personalizarla.
5. **Exporta / Importa** tu configuración en JSON, para pasársela a un agente nuevo.

**Consejo:** se guarda por usuario; los asistentes la ven en solo lectura si tienen permiso.`,
      en: `A **states × carriers** matrix where you mark where you're appointed — that is, where you can make changes online.

### How
1. Menu → **Company Appointments**.
2. Pick a **category** (Health, Medicare Advantage, Dental, Life, Auto, Home…).
3. **Click** the state + carrier cells where you're appointed.
4. **+ New Company** or **+ New Category** to customize it.
5. **Export / Import** your setup as JSON, to hand it to a new agent.

**Tip:** it's saved per user; assistants see it read-only if they have permission.` } },
  { id: 'company-info', cat: 'business', nav: ['company_info'],
    title: { es: 'Info de Compañías (el directorio)', en: 'Company Information (the directory)' },
    body: {
      es: `Directorio central de teléfonos, correos, enlaces de portales y videos de entrenamiento de las aseguradoras que usas.

### Cómo
1. Menú → **Info de Compañías** → **+ Nueva Compañía**.
2. Llena Compañía*, categoría (Teléfonos y Correos / Enlaces / Videos), teléfonos, correos, enlaces, URL de entrenamiento y notas → **Guardar**.
3. Edita con ✏️, elimina con 🗑️ o mira el **historial** 🕓 de cambios.

**Consejos:**
- Los teléfonos múltiples se separan con "/" o con saltos de línea.
- Los enlaces y correos se vuelven clicables solos, y los videos de YouTube o Vimeo se ven dentro de la pantalla.
- Puedes importar tu Excel de tres secciones de una vez.`,
      en: `A central directory of phone numbers, emails, portal links and training videos for the carriers you work with.

### How
1. Menu → **Company Information** → **+ New Company**.
2. Fill in Company*, category (Phones & Emails / Links / Videos), phones, emails, links, training URL and notes → **Save**.
3. Edit with ✏️, delete with 🗑️, or view the change **history** 🕓.

**Tips:**
- Multiple phone numbers are separated with "/" or line breaks.
- Links and emails become clickable on their own, and YouTube or Vimeo videos play inside the screen.
- You can import your three-section Excel in one go.` } },
  { id: 'expenses', cat: 'business', nav: ['expenses'],
    title: { es: 'Gastos', en: 'Expenses' },
    body: {
      es: `Los gastos del negocio (licencias, software, marketing, E&O, renta…) para tu deducción fiscal y tu análisis.

### Cómo
1. Menú → **Gastos** → **+ Nuevo Gasto**.
2. Llena fecha, categoría, descripción, proveedor, monto, método de pago, **deducible** (sí/no), **recurrente** (sí/no) y un comprobante opcional → **Guardar**.
3. Filtra por mes y revisa el resumen 📊. Edita o elimina por fila.

**Consejos:**
- Las tarjetas de arriba muestran el total del mes, el del año, los deducibles y los recurrentes.
- Exporta el período para tu contable.
- Es **por usuario**.
- Si registras aquí lo que le pagas a un agente con la categoría "Pago a Agente/Contratista", luego puedes **jalar el total del año** en el 1099.`,
      en: `Your business expenses (licenses, software, marketing, E&O, rent…) for tax deductions and analysis.

### How
1. Menu → **Expenses** → **+ New Expense**.
2. Fill in date, category, description, vendor, amount, payment method, **deductible** (yes/no), **recurring** (yes/no) and an optional receipt → **Save**.
3. Filter by month and check the 📊 summary. Edit or delete per row.

**Tips:**
- The cards on top show the month's total, the year's, deductibles and recurring ones.
- Export the period for your accountant.
- It's **per user**.
- If you record agent payments here under the "Agent/Contractor Payment" category, you can later **pull the yearly total** into the 1099.` } },
  { id: 'payroll', cat: 'business', nav: ['payroll'],
    title: { es: 'Nómina y Formularios (Paystub + 1099)', en: 'Payroll & Forms (Paystub + 1099)' },
    body: {
      es: `Genera dos documentos imprimibles: el **recibo de pago (paystub)** y el **1099-NEC** (copia del destinatario).

### Cómo
1. Menú (grupo Gastos y Horas) → **Nómina y Formularios**.
2. Llena una vez los **datos del pagador**, incluido el EIN. Se quedan guardados en tu navegador.
3. Elige la pestaña **Paystub** o **1099-NEC**.
4. Escribe los montos: bruto, deducciones (con "Agregar deducción"), neto y acumulado del año; o la Casilla 1 y la Casilla 4 del 1099.
5. **"Guardar como PDF"** o imprime. Queda un historial de lo generado.

**⚠️ Importante:**
- Es **manual y solo genera el PDF**: no calcula impuestos y **no presenta nada ante el IRS**. La Copia A se presenta aparte, y con 10 formularios o más el envío electrónico es obligatorio.
- **No guarda el SSN ni el TIN** del destinatario: solo se usan para pintar el PDF.
- No aparece para los asistentes. Verifica siempre con tu contadora.`,
      en: `Generates two printable documents: the **pay stub** and the **1099-NEC** (recipient copy).

### How
1. Menu (Expenses & Time group) → **Payroll & Forms**.
2. Fill in the **payer details** once, EIN included. They're saved in your browser.
3. Pick the **Paystub** or **1099-NEC** tab.
4. Type the amounts: gross, deductions (with "Add deduction"), net and year-to-date; or Box 1 and Box 4 of the 1099.
5. **"Save as PDF"** or print. A history of what you generated is kept.

**⚠️ Important:**
- It is **manual and only produces the PDF**: it does not calculate taxes and **files nothing with the IRS**. Copy A is filed separately, and with 10 or more forms electronic filing is mandatory.
- It **does not store the recipient's SSN or TIN**: they're only used to render the PDF.
- Assistants don't see it. Always check with your accountant.` } },
  { id: 'team-training', cat: 'business', nav: ['team_training'],
    title: { es: 'Team Training (material para tu equipo)', en: 'Team Training (material for your team)' },
    body: {
      es: `Sube y comparte material de capacitación con todo el equipo.

### Cómo
1. **"Agregar material"**: Título, Descripción y Tipo (video, PDF, imagen).
2. Sube el archivo o pega un enlace (YouTube, Vimeo, Drive, Loom).
3. **Guardar**. Busca por título; descarga o elimina material.

**Consejo:** lo ve **todo el equipo**, y los videos que subes se reproducen dentro de Deskte.`,
      en: `Upload and share training material with the whole team.

### How
1. **"Add material"**: Title, Description and Type (video, PDF, image).
2. Upload the file or paste a link (YouTube, Vimeo, Drive, Loom).
3. **Save**. Search by title; download or delete material.

**Tip:** **the whole team** sees it, and videos you upload play inside Deskte.` } },

  // ── Mi Agencia y Equipo ───────────────────────────────────────────────────
  { id: 'team', cat: 'agency', nav: ['team'],
    title: { es: 'Equipo: agentes, asientos y permisos', en: 'Team: agents, seats and permissions' },
    body: {
      es: `Aquí agregas gente, fijas quién supervisa a quién y decides qué puede ver cada uno.

### Cómo
1. Menú → **Equipo** → **+ Nuevo Agente** o **+ Nuevo Asiento**.
2. **Agente:** nombre, email, rol, color, supervisor (opcional) y PIN de 4 dígitos.
3. **Asiento:** nombre, email, **agente padre** (su supervisor), etiqueta, PIN y color.
4. **Guardar** o **✉️ Enviar Invitación** para que la persona complete sus datos.
5. Con ✏️ editas, y con 🛡️ revisas los **permisos del asiento**: qué módulos ve y qué puede tocar.
   - **Métodos de pago:** en *Clientes y Leads* está **"Ver métodos de pago del cliente"**. Sin esa casilla, el asiento no ve la pestaña **Pagos** de la ficha (ni tarjetas ni cuentas). Con ella, el número completo sigue pidiendo el PIN. Los asientos nuevos empiezan sin ella; a los que ya existían se les dejó puesta para que nadie la perdiera de golpe.

**Consejos:**
- Un asiento solo ve los clientes y datos que su agente le permite: es el principio HIPAA de "mínimo necesario".
- El PIN de 4 dígitos (inicio de sesión) no es el PIN de 6 (bóveda).
- Eliminar a un usuario **no borra** su historial: la auditoría se mantiene.`,
      en: `This is where you add people, set who supervises whom, and decide what each person can see.

### How
1. Menu → **Team** → **+ New Agent** or **+ New Seat**.
2. **Agent:** name, email, role, color, supervisor (optional) and 4-digit PIN.
3. **Seat:** name, email, **parent agent** (their supervisor), label, PIN and color.
4. **Save** or **✉️ Send Invitation** so the person completes their own details.
5. ✏️ edits, and 🛡️ opens the **seat permissions**: which modules they see and what they can touch.
   - **Payment methods:** under *Clients & Leads* there is **"View client payment methods"**. Without it, the seat doesn't see the record's **Payment** tab (no cards, no accounts). With it, the full number still asks for the PIN. New seats start without it; existing seats kept it so nobody lost it overnight.

**Tips:**
- A seat only sees the clients and data its agent allows: that's the HIPAA "minimum necessary" principle.
- The 4-digit PIN (login) is not the 6-digit one (vault).
- Deleting a user **does not erase** their history: the audit trail stays.` } },
  { id: 'time-tracking', cat: 'agency', nav: ['timetrack'],
    title: { es: 'Horas del Equipo (reloj de entrada y salida)', en: 'Team Hours (clock in and out)' },
    body: {
      es: `El registro de horas trabajadas, el calendario del mes y el resumen para la nómina.

### Cómo
1. Menú → **Horas del Equipo**.
2. **🟢 Entrada** al empezar (verás el contador en vivo) → **🔴 Salida** al terminar.
3. O usa **+ Registrar Manual** con la hora de entrada, la de salida y una nota.
4. Revisa el **calendario** de horas por día.
5. (Dueño) Pestaña **👥 Equipo**: la nómina del mes por miembro — días y horas.

**Consejos:**
- Solo cuentan para la nómina las sesiones **cerradas**, es decir, con salida marcada.
- El dueño puede cerrar la sesión que un asiento dejó abierta.`,
      en: `The record of hours worked, the month's calendar and the payroll summary.

### How
1. Menu → **Team Hours**.
2. **🟢 Clock In** when you start (you'll see a live counter) → **🔴 Clock Out** when you finish.
3. Or use **+ Manual entry** with the start time, end time and a note.
4. Review the **calendar** of hours per day.
5. (Owner) **👥 Team** tab: the month's payroll per member — days and hours.

**Tips:**
- Only **closed** sessions count toward payroll — meaning ones with a clock-out.
- The owner can close a session a seat left open.` } },
  { id: 'hipaa-seats', cat: 'agency', nav: ['hipaa_seats'],
    title: { es: 'HIPAA Seats (entrenamiento y acuerdo)', en: 'HIPAA Seats (training & agreement)' },
    body: {
      es: `Capacitación HIPAA obligatoria para los asientos que ven información médica: video, acuerdo de confidencialidad, firma y auditoría de quién completó qué.

### Lo que ve el asiento
1. Si está activado, al iniciar sesión ve una **pantalla de bloqueo** 🛡️.
2. Mira el **video**, **lee el acuerdo** completo (hay que bajar hasta el final), marca "lo entiendo", **firma** con su nombre y pulsa **🔒 Aceptar y continuar**.
3. Queda desbloqueado hasta que subas una versión nueva o expire el plazo.

### Lo que configura el dueño
1. Menú → **HIPAA Seats** (solo dueños y admins).
2. **Habilita** el entrenamiento, carga la **URL del video**, edita el **acuerdo**, sube la **versión** y define cada cuánto se renueva (por ejemplo, 12 meses).
3. **Guarda** y revisa la **auditoría**: qué asiento firmó, cuándo y qué versión.

**⚠️ Importante:**
- El texto que viene es un **BORRADOR**: debe reemplazarlo tu abogada de HIPAA.
- Subir una versión nueva obliga a todos los asientos a re-entrenar. El historial es inmutable.
- Viene **apagado** por defecto, para que nadie quede bloqueado por sorpresa.`,
      en: `Mandatory HIPAA training for seats that see medical information: video, confidentiality agreement, signature, and an audit of who completed what.

### What the seat sees
1. If it's enabled, they hit a 🛡️ **lock screen** at sign-in.
2. They watch the **video**, **read the agreement** in full (they must scroll to the end), tick "I understand", **sign** their name and press **🔒 Accept and continue**.
3. They're unlocked until you upload a new version or the term expires.

### What the owner sets up
1. Menu → **HIPAA Seats** (owners and admins only).
2. **Enable** the training, load the **video URL**, edit the **agreement**, bump the **version** and set the renewal period (12 months, for example).
3. **Save** and review the **audit**: which seat signed, when, and which version.

**⚠️ Important:**
- The text that ships is a **DRAFT**: your HIPAA attorney must replace it.
- Uploading a new version forces every seat to retrain. The history is immutable.
- It ships **off** by default, so nobody gets locked out by surprise.` } },
  { id: 'my-agency', cat: 'agency', nav: ['agency'],
    title: { es: 'Mi Agencia (la pantalla del dueño)', en: 'My Agency (the owner screen)' },
    body: {
      es: `Cuatro pestañas para ver cómo va cada agente y manejar la identidad de la agencia.

1. **Leaderboard:** el ranking de agentes.
2. **Agentes CRM:** clic en un agente para ver su ficha — clientes, pólizas activas, comisiones, licencias y citas con compañías.
3. **Equipo:** los asientos y agentes con su precio (agente +$19/mes, asiento +$9/mes).
4. **Marca:** subes el **logo de la agencia** (el que sale en los PDF y en los correos al cliente), eliges la tipografía y la **zona horaria**.

**Consejos:**
- Los compañeros reales se agregan desde el módulo **Equipo**, con "Invitar".
- Cambiar el nombre y la zona horaria de la agencia solo lo puede hacer el dueño.
- Es un módulo **solo para dueños**: un asistente nunca lo ve.`,
      en: `Four tabs for seeing how each agent is doing and managing the agency's identity.

1. **Leaderboard:** the agent ranking.
2. **CRM Agents:** click an agent to see their sheet — clients, active policies, commissions, licenses and carrier appointments.
3. **Team:** the seats and agents with their price (agent +$19/mo, seat +$9/mo).
4. **Brand:** upload the **agency logo** (the one on PDFs and client emails), choose the typeface and the **time zone**.

**Tips:**
- Real teammates are added from the **Team** module, with "Invite".
- Only the owner can change the agency name and time zone.
- It's an **owners-only** module: an assistant never sees it.` } },
  { id: 'agent-production', cat: 'agency', nav: ['agent_production'],
    title: { es: 'Producción por Agente', en: 'Production by Agent' },
    body: {
      es: `Un reporte de puros números por agente, en siete categorías: cartera, producción, comisiones, conversión, renovaciones, retención y actividad.

### Cómo
1. Menú → **📊 Producción por Agente**.
2. Lee la tabla: clientes activos, clientes nuevos del mes, pólizas activas y nuevas, prima anual, comisión del mes y del año, leads del mes, convertidos, renovaciones, bajas y última actividad.
3. **Actualizar** para recargar.

**Consejos:**
- **Nunca muestra clientes ni datos de salud**, solo totales. Es justo eso lo que permite que una agencia trabaje con libros privados: el dueño supervisa sin ver la cartera ajena.
- Solo lo ve el dueño de la agencia.`,
      en: `A numbers-only report per agent, across seven categories: book, production, commissions, conversion, renewals, retention and activity.

### How
1. Menu → **📊 Production by Agent**.
2. Read the table: active clients, new clients this month, active and new policies, annual premium, commission this month and this year, leads this month, conversions, renewals, cancellations and last activity.
3. **Refresh** to reload.

**Tips:**
- It **never shows clients or health data**, only totals. That's exactly what lets an agency run private books: the owner supervises without seeing someone else's clients.
- Only the agency owner sees it.` } },
  { id: 'agency-analytics', cat: 'agency', nav: ['agency_analytics'],
    title: { es: 'Métricas y Análisis de la agencia', en: 'Agency Metrics & Analytics' },
    body: {
      es: `El panel analítico de la agencia entera, con gráficas de torta, barras y líneas.

### Cómo
1. Menú → **📈 Métricas y Análisis**.
2. Usa la barra de filtros: período, agente, tipo de póliza y compañía.
3. Lee las tarjetas: clientes activos, pólizas activas, cartera anual, comisión del mes y del año, bajas, crecimiento acumulado del libro y prima promedio por póliza.
4. **Actualizar**.

**Consejos:**
- Requiere plan **Agency o Enterprise**. Si tu plan no lo incluye, la propia pantalla te lo dice.
- Como Producción por Agente, son totales: no salen nombres de clientes.
- Solo lo ven dueños y administradores.`,
      en: `The analytics panel for the whole agency, with pie, bar and line charts.

### How
1. Menu → **📈 Metrics & Analytics**.
2. Use the filter bar: period, agent, policy type and carrier.
3. Read the cards: active clients, active policies, annual book, commission this month and this year, cancellations, cumulative book growth and average premium per policy.
4. **Refresh**.

**Tips:**
- Requires the **Agency or Enterprise** plan. If yours doesn't include it, the screen says so.
- Like Production by Agent, these are totals: no client names appear.
- Only owners and admins see it.` } },

  // ── Funciones nuevas ──────────────────────────────────────────────────────
  { id: 'status-toggle', cat: 'new', nav: [],
    title: { es: 'Cambiar el estado desde la ficha', en: 'Change status from the profile' },
    body: {
      es: `Cambia **Activo / Inactivo / Prospecto** directo desde la ficha del cliente, sin entrar a Editar.

### Cómo
- Haz clic en el **badge de estado** (junto al nombre, con flechita ▾) y elige el nuevo estado. Se guarda al instante.`,
      en: `Switch **Active / Inactive / Prospect** right from the client profile, without opening Edit.

### How
- Click the **status badge** (next to the name, with a ▾ caret) and pick the new status. Saved instantly.` } },
  { id: 'announcements', cat: 'new', nav: [],
    title: { es: 'Novedades (campana 📣 arriba)', en: 'News (📣 bell at the top)' },
    body: {
      es: `El icono **📣** arriba muestra las novedades y anuncios del sistema (funciones nuevas, avisos de mantenimiento, etc.).

- Tiene un contador de no leídos.
- La fundadora publica desde **Platform Admin → Novedades**.`,
      en: `The **📣** icon at the top shows system news and announcements (new features, maintenance notices, etc.).

- It has an unread counter.
- The founder posts from **Platform Admin → News**.` } },
];
